import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ReactFlow,
  Background,
  Controls,
  MarkerType,
  ConnectionMode,
  Handle,
  Position,
  BaseEdge,
  EdgeLabelRenderer,
  getSmoothStepPath,
  type Node,
  type NodeProps,
  type NodeTypes,
  type Edge,
  type EdgeProps,
  type EdgeTypes,
  type NodeChange,
  type EdgeChange,
  applyNodeChanges,
  applyEdgeChanges,
  type Connection,
  type ReactFlowInstance,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import type {
  Action,
  AgentFlowDTO,
  AgentFlowSlotDTO,
  ApprovedWhatsAppTemplateDTO,
  CreateFlowChannelBindingResponse,
  FlowChannelBindingDTO,
  GetWhatsAppTemplateResponse,
  FlowDefinition,
  Slot,
  SlotEntry,
  SlotCondition,
  SlotConditionOp,
  Stage,
  StageTransition,
  StageTransitionTrigger,
  FlowVariable,
  SandboxSimulatedChannel,
  SandboxToolCallSummary,
  ToolDefinitionDTO,
  TwilioChannelSenderDTO,
  TwilioConnection,
  TwilioNumberDTO,
} from "@tw-pulse/shared";
import {
  FLOW_VARIABLES,
  interpolateFlowVariables,
  FlowDefinitionSchema,
  STAGE_TRANSITION_TRIGGERS,
  STAGE_TYPE_EXAMPLE_DEFAULTS,
  isSlotRef,
} from "@tw-pulse/shared";
import { api, ApiClientError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/Button";
import { ChipsInput } from "@/components/ui/ChipsInput";

/** Materialize an account-catalog slot DTO as a concrete `Slot` (ADR 0030). */
function catalogSlotToSlot(dto: AgentFlowSlotDTO): Slot {
  return {
    id: dto.key,
    prompt: dto.prompt,
    required: true,
    source: dto.source,
    type: dto.type,
    ...(dto.options ? { options: dto.options } : {}),
    ...(dto.format ? { format: dto.format } : {}),
    ...(dto.min !== undefined ? { min: dto.min } : {}),
    ...(dto.max !== undefined ? { max: dto.max } : {}),
    ...(dto.askExamples ? { askExamples: dto.askExamples } : {}),
  } as Slot;
}

/**
 * Slots the editor's pickers offer: the WHOLE account catalog (ADR 0030 — the
 * catalog is now the source of truth) plus any legacy inline slots a pre-catalog
 * flow still carries. Every stage can require or branch on any catalog slot
 * without first "referencing" it into the flow.
 */
function computeEffectiveSlots(localSlots: SlotEntry[], catalog: AgentFlowSlotDTO[]): Slot[] {
  const out: Slot[] = catalog.map(catalogSlotToSlot);
  const seen = new Set(out.map((s) => s.id));
  for (const entry of localSlots) {
    if (!isSlotRef(entry) && !seen.has(entry.id)) {
      seen.add(entry.id);
      out.push(entry); // legacy inline slot — still usable, but deprecated
    }
  }
  return out;
}

/** Every slot id a flow's stages actually reference (requiredSlotIds + branches). */
function usedSlotIds(def: FlowDefinition): Set<string> {
  const ids = new Set<string>();
  for (const stage of def.stages) {
    for (const sid of stage.requiredSlotIds) ids.add(sid);
    for (const t of stage.transitions) {
      if (t.on === "slot_condition" && t.condition) ids.add(t.condition.slotId);
    }
  }
  return ids;
}

/**
 * Derive a flow's persisted slot entries from what its stages actually use
 * (ADR 0030). Used catalog slots become `{ ref }`; a used id that isn't in the
 * catalog but has a legacy inline slot is preserved inline. Ids referenced but
 * unknown are dropped — which surfaces as a validation error on the stage that
 * references them, rather than a silent dangling slot. The author no longer
 * maintains this list by hand; it follows stage usage against the catalog.
 */
function deriveSlotEntries(
  def: FlowDefinition,
  catalogByKey: Map<string, AgentFlowSlotDTO>,
): SlotEntry[] {
  const used = usedSlotIds(def);
  const legacyById = new Map<string, Slot>();
  for (const e of def.slots) if (!isSlotRef(e)) legacyById.set(e.id, e);
  const out: SlotEntry[] = [];
  for (const id of used) {
    if (catalogByKey.has(id)) out.push({ ref: id });
    else {
      const local = legacyById.get(id);
      if (local) out.push(local);
    }
  }
  return out;
}

/**
 * FlowEditor — redesigned layout.
 *
 * Two-pane workspace: canvas + persona/greeting on the left (where the
 * author actually builds), sandbox conversation on the right (the
 * feedback loop). The properties card swaps between persona/greeting
 * (default) and per-node editors (when a canvas node is selected),
 * reusing the same physical space.
 *
 * Sandbox can be collapsed to give the canvas full width.
 */
export function FlowEditor() {
  const { id } = useParams<{ id: string }>();
  const flowId = id ?? "";

  const [state, setState] = useState<
    | { status: "loading" }
    | { status: "ready"; flow: AgentFlowDTO }
    | { status: "error"; message: string }
  >({ status: "loading" });
  const [tools, setTools] = useState<ToolDefinitionDTO[] | null>(null);

  // Initial fetch — flow + tool registry.
  useEffect(() => {
    if (!flowId) return;
    let cancelled = false;
    setState({ status: "loading" });
    Promise.all([api.agentflow.getFlow(flowId), api.agentflow.listTools()])
      .then(([flow, toolsRes]) => {
        if (cancelled) return;
        setState({ status: "ready", flow });
        setTools(toolsRes.tools);
      })
      .catch((e) => {
        if (cancelled) return;
        setState({
          status: "error",
          message: e instanceof ApiClientError ? e.message : "Failed to load flow",
        });
      });
    return () => {
      cancelled = true;
    };
  }, [flowId]);

  if (!flowId) return <p className="p-6 text-sm">Bad route.</p>;
  if (state.status === "loading") {
    return (
      <div className="flex h-screen items-center justify-center">
        <p className="text-sm text-zinc-500">Loading flow…</p>
      </div>
    );
  }
  if (state.status === "error") {
    return (
      <p
        role="alert"
        className="m-6 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300"
      >
        {state.message}
      </p>
    );
  }

  // Every flow is a stage machine (v2). The editor owns its own draft state,
  // autosave, and validation; we just feed it the loaded flow + tool registry.
  return (
    <StagesFlowEditor
      flow={state.flow}
      tools={tools}
      onFlowUpdated={(updated) =>
        setState((prev) => (prev.status === "ready" ? { ...prev, flow: updated } : prev))
      }
    />
  );
}

// ===========================================================================
// State types
// ===========================================================================

function CopyableId({ id, label = "ID" }: { id: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={() => {
        void navigator.clipboard?.writeText(id);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
      title={`Copy ${label}`}
      className="inline-flex items-center gap-1.5 font-mono text-[11px] text-zinc-500 transition-colors hover:text-zinc-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40 dark:hover:text-zinc-200"
    >
      <span className="uppercase tracking-wider not-italic">{label}</span>
      <span className="select-all">{id}</span>
      <span className="text-[10px] text-zinc-400">{copied ? "copied" : "copy"}</span>
    </button>
  );
}

function PersonaEditor({
  persona,
  onChange,
  fullWidth = false,
}: {
  persona: string;
  onChange: (s: string) => void;
  fullWidth?: boolean;
}) {
  return (
    <EditorPage
      eyebrow="Persona"
      title="How the agent should talk"
      subtitle="Tone, identity, and behavior. This system prompt is prepended to every conversation the agent runs."
      fullWidth={fullWidth}
    >
      <ExplainerCard
        kind="intent"
        text="Keep it short and concrete. Use variables to personalize — the runtime fills them in per conversation. Reference tenant settings (your business name, hours) or conversation context (customer name, channel)."
      />
      <RichField
        label="Persona"
        hint="What identity should the agent adopt? What tone? What should it refuse to do? Aim for 4–10 sentences."
        value={persona}
        onChange={onChange}
        rows={16}
      />
    </EditorPage>
  );
}

function GreetingEditor({
  greeting,
  onChange,
  fullWidth = false,
}: {
  greeting: string;
  onChange: (s: string) => void;
  fullWidth?: boolean;
}) {
  return (
    <EditorPage
      eyebrow="Greeting"
      title="First outbound message"
      subtitle="What the agent says to kick off the conversation when it initiates contact. Inbound flows (customer-first) skip this."
      fullWidth={fullWidth}
    >
      <ExplainerCard
        kind="action"
        text="Short, warm, and specific. Identify who the agent is, what it can help with, and invite a response. Use variables to personalize — e.g. {customer_first_name}."
      />
      <RichField
        label="Greeting"
        hint="One or two lines. Don't bury the lede."
        value={greeting}
        onChange={onChange}
        rows={6}
      />
    </EditorPage>
  );
}

/**
 * Shared chrome for page-level editors: scrollable column with a
 * comfortable max-width, eyebrow + title + subtitle header, then
 * children. Keeps Persona / Greeting / Channels visually consistent.
 */
function EditorPage({
  eyebrow,
  title,
  subtitle,
  children,
  fullWidth = false,
}: {
  eyebrow: string;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  /** v2 editor uses the full workspace width; v1 stays centered. */
  fullWidth?: boolean;
}) {
  return (
    <div className="min-h-0 flex-1 overflow-y-auto bg-white dark:bg-zinc-900/30">
      <div className={"w-full space-y-6 px-8 py-8 " + (fullWidth ? "" : "mx-auto max-w-3xl")}>
        <header>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-brand-600 dark:text-brand-400">
            {eyebrow}
          </p>
          <h2 className="mt-1.5 text-[20px] font-semibold text-zinc-900 dark:text-zinc-50">
            {title}
          </h2>
          {subtitle && (
            <p className="mt-1.5 text-[13px] leading-relaxed text-zinc-500">{subtitle}</p>
          )}
        </header>
        {children}
      </div>
    </div>
  );
}

// ===========================================================================
// Sample values used by RichField's live preview pane (renders variables
// with realistic stand-in text so authors can see what the runtime will
// produce per conversation). `{businessName}` is overridden at render time
// with the tenant's actual configured name (see RichField) — only the
// conversation/runtime variables fall back to these stand-ins.
// ===========================================================================

const SAMPLE_VALUES: Record<string, string> = Object.fromEntries(
  FLOW_VARIABLES.map((v) => [v.key, v.sampleValue]),
);

/**
 * Textarea + insert-variable picker + live chip preview. Cursor-aware
 * insertion: clicking a variable in the picker inserts `{key}` at the
 * current selection, replacing any selected text.
 */
function RichField({
  label,
  hint,
  value,
  onChange,
  rows,
}: {
  label: string;
  hint?: string;
  value: string;
  onChange: (s: string) => void;
  rows: number;
}) {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const { me } = useAuth();

  // Preview with the tenant's REAL business name (set in Settings → Branding);
  // fall back to the sample only when it hasn't been configured yet. Other
  // tokens (customer name, dates, …) keep their per-conversation stand-ins.
  const previewValues = useMemo(() => {
    const realBusinessName = me?.tenant.businessName?.trim();
    return realBusinessName ? { ...SAMPLE_VALUES, businessName: realBusinessName } : SAMPLE_VALUES;
  }, [me]);

  function insertVariable(key: string) {
    const ta = textareaRef.current;
    const token = `{${key}}`;
    if (!ta) {
      onChange(value + token);
      return;
    }
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const next = value.slice(0, start) + token + value.slice(end);
    onChange(next);
    // Restore focus + place the cursor after the inserted token.
    setTimeout(() => {
      ta.focus();
      const pos = start + token.length;
      ta.selectionStart = ta.selectionEnd = pos;
    }, 0);
  }

  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <label className="block">
          <span className="text-[11px] font-medium uppercase tracking-wider text-zinc-500">
            {label}
          </span>
        </label>
        <VariablePicker onInsert={insertVariable} />
      </div>
      <textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        className="block w-full resize-none rounded-md border border-zinc-200 bg-white px-3 py-2 text-[13.5px] leading-relaxed text-zinc-900 placeholder:text-zinc-400 focus-visible:border-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30 dark:border-zinc-800 dark:bg-zinc-950/40 dark:text-zinc-100 dark:placeholder:text-zinc-600"
      />
      {hint && <p className="mt-1 text-[11px] text-zinc-500">{hint}</p>}

      <div className="mt-2 rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2 dark:border-zinc-800 dark:bg-zinc-900/40">
        <p className="mb-1 text-[10.5px] font-medium uppercase tracking-wider text-zinc-500">
          Preview
        </p>
        <p className="text-[13px] leading-relaxed text-zinc-800 dark:text-zinc-200">
          {value.trim() === "" ? (
            <span className="text-zinc-400">Empty</span>
          ) : (
            interpolateFlowVariables(value, previewValues)
          )}
        </p>
      </div>
    </div>
  );
}

/**
 * Dropdown of variables grouped by source. Available ones are insertable;
 * coming-soon ones are visible but disabled so authors can see the
 * roadmap without picking unwired tokens.
 */
function VariablePicker({ onInsert }: { onInsert: (key: string) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as globalThis.Node)) setOpen(false);
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const grouped: Record<FlowVariable["source"], FlowVariable[]> = {
    tenant: [],
    conversation: [],
    runtime: [],
  };
  for (const v of FLOW_VARIABLES) grouped[v.source].push(v);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="menu"
        className="inline-flex h-7 items-center gap-1 rounded-md border border-zinc-200 bg-white px-2 text-[11.5px] font-medium text-zinc-700 transition-colors hover:bg-zinc-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 dark:border-zinc-800 dark:bg-zinc-900/40 dark:text-zinc-300 dark:hover:bg-zinc-800/60"
      >
        <BraceIcon />
        Insert variable
        <ChevronDownIcon />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 z-30 mt-1 w-80 overflow-hidden rounded-md border border-zinc-200 bg-white shadow-lg dark:border-zinc-800 dark:bg-zinc-900"
        >
          <VariableGroup
            heading="Tenant settings"
            items={grouped.tenant}
            onInsert={(k) => {
              onInsert(k);
              setOpen(false);
            }}
          />
          <VariableGroup
            heading="Conversation context"
            items={grouped.conversation}
            onInsert={(k) => {
              onInsert(k);
              setOpen(false);
            }}
          />
          <VariableGroup
            heading="Runtime"
            items={grouped.runtime}
            onInsert={(k) => {
              onInsert(k);
              setOpen(false);
            }}
          />
        </div>
      )}
    </div>
  );
}

function VariableGroup({
  heading,
  items,
  onInsert,
}: {
  heading: string;
  items: FlowVariable[];
  onInsert: (key: string) => void;
}) {
  if (items.length === 0) return null;
  const anyAvailable = items.some((v) => v.available);
  return (
    <section className="border-b border-zinc-100 last:border-b-0 dark:border-zinc-800/60">
      <header className="flex items-center justify-between bg-zinc-50/60 px-3 py-1.5 dark:bg-zinc-900/40">
        <p className="text-[10.5px] font-medium uppercase tracking-wider text-zinc-500">
          {heading}
        </p>
        {!anyAvailable && (
          <span className="rounded-full bg-zinc-100 px-1.5 py-0.5 text-[9.5px] font-medium uppercase tracking-wider text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400">
            coming soon
          </span>
        )}
      </header>
      <ul>
        {items.map((v) => (
          <li key={v.key}>
            <button
              type="button"
              role="menuitem"
              onClick={() => v.available && onInsert(v.key)}
              disabled={!v.available}
              title={v.description}
              className={
                "block w-full px-3 py-2 text-left transition-colors disabled:cursor-not-allowed " +
                (v.available
                  ? "hover:bg-zinc-50 focus-visible:bg-zinc-50 focus-visible:outline-none dark:hover:bg-zinc-800/60 dark:focus-visible:bg-zinc-800/60"
                  : "opacity-60")
              }
            >
              <div className="flex items-center gap-2">
                <span className="font-mono text-[11.5px] text-brand-600 dark:text-brand-300">
                  {"{" + v.key + "}"}
                </span>
                <span className="text-[12px] text-zinc-700 dark:text-zinc-300">{v.label}</span>
                {!v.available && (
                  <span className="ml-auto text-[10px] font-medium uppercase tracking-wider text-zinc-400">
                    soon
                  </span>
                )}
              </div>
              <p className="mt-0.5 text-[11px] text-zinc-500">{v.description}</p>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function FieldGroup({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-medium uppercase tracking-wider text-zinc-500">
        {label}
      </span>
      {children}
      {hint && <span className="mt-1 block text-[11px] text-zinc-500">{hint}</span>}
    </label>
  );
}

// ===========================================================================
// ChipList — reusable add-input + numbered chip pattern.
// Shared across IntentDialog (examples), GuardrailDialog (never_say
// phrases, stay_on_topic topics), and ActionDialog (book_appointment
// service types). Idempotent (no duplicate adds), capped via `max`,
// live counter.
// ===========================================================================

function ChipList({
  label,
  hint,
  placeholder,
  emptyLabel,
  removeAriaPrefix,
  items,
  onAdd,
  onRemove,
  max,
}: {
  label: string;
  hint?: string;
  placeholder: string;
  emptyLabel: string;
  removeAriaPrefix: string;
  items: string[];
  onAdd: (value: string) => void;
  onRemove: (value: string) => void;
  max?: number;
}) {
  const [draft, setDraft] = useState("");
  const addRef = useRef<HTMLInputElement | null>(null);
  const atMax = max != null && items.length >= max;
  const remaining = max != null ? max - items.length : null;
  const headingId = `chiplist-${label.toLowerCase().replace(/\W+/g, "-")}`;

  function commit() {
    const value = draft.trim();
    if (!value) return;
    if (items.includes(value)) {
      setDraft("");
      addRef.current?.focus();
      return;
    }
    if (atMax) return;
    onAdd(value);
    setDraft("");
    addRef.current?.focus();
  }

  return (
    <section aria-labelledby={headingId}>
      <div className="mb-2 flex items-baseline justify-between">
        <div>
          <h3
            id={headingId}
            className="text-[11px] font-medium uppercase tracking-wider text-zinc-500"
          >
            {label}
          </h3>
          {hint && <p className="mt-0.5 text-[11.5px] text-zinc-500">{hint}</p>}
        </div>
        {max != null && (
          <span
            className={
              "text-[11px] tabular-nums " +
              (atMax ? "font-medium text-amber-700 dark:text-amber-300" : "text-zinc-500")
            }
          >
            {atMax ? `${max} / ${max} — max` : `${items.length} / ${max}`}
          </span>
        )}
      </div>

      <div className="flex gap-2">
        <input
          ref={addRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commit();
            }
          }}
          placeholder={
            atMax
              ? `Remove a ${label.toLowerCase().slice(0, -1)} below to add another`
              : remaining != null && remaining < 5
                ? `${placeholder} (${remaining} left)`
                : placeholder
          }
          disabled={atMax}
          aria-label={placeholder}
          className="h-10 flex-1 rounded-md border border-zinc-200 bg-white px-3 text-[13.5px] text-zinc-900 placeholder:text-zinc-400 focus-visible:border-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30 disabled:bg-zinc-50 disabled:text-zinc-400 dark:border-zinc-800 dark:bg-zinc-950/40 dark:text-zinc-100 dark:placeholder:text-zinc-600 dark:disabled:bg-zinc-900/30"
        />
        <Button onClick={commit} disabled={atMax || draft.trim().length === 0} size="sm">
          Add
        </Button>
      </div>
      <p className="mt-1 text-[10.5px] text-zinc-500">↵ to add</p>

      <ul className="mt-3 space-y-1.5">
        {items.length === 0 && (
          <li className="rounded-md border border-dashed border-zinc-200 bg-zinc-50 px-3 py-3 text-center text-[12.5px] text-zinc-500 dark:border-zinc-800 dark:bg-zinc-900/30">
            {emptyLabel}
          </li>
        )}
        {items.map((it, idx) => (
          <li
            key={`${idx}-${it}`}
            className="group flex items-center gap-2 rounded-md border border-zinc-200 bg-white px-3 py-2 transition-colors hover:border-zinc-300 dark:border-zinc-800 dark:bg-zinc-900/40 dark:hover:border-zinc-700"
          >
            <span
              className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-zinc-100 text-[10.5px] font-mono text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400"
              aria-hidden="true"
            >
              {idx + 1}
            </span>
            <span className="flex-1 text-[13px] text-zinc-800 dark:text-zinc-200">{it}</span>
            <button
              type="button"
              onClick={() => onRemove(it)}
              aria-label={`${removeAriaPrefix}: ${it}`}
              className="inline-flex h-7 w-7 items-center justify-center rounded text-zinc-400 opacity-0 transition-opacity hover:bg-zinc-100 hover:text-zinc-700 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 group-hover:opacity-100 dark:hover:bg-zinc-800 dark:hover:text-zinc-200"
            >
              <CloseIcon />
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

// ===========================================================================
// Dialog primitive — used by every node-editing dialog. Renders a centered
// modal with header (kind label + title + close), scrollable body, and a
// footer with destructive action (left) + Done button (right). Escape +
// backdrop close are handled here so each consumer doesn't repeat them.
// ===========================================================================

function ExplainerCard({ kind, text }: { kind: "intent" | "action" | "guardrail"; text: string }) {
  const tone = {
    intent:
      "border-indigo-200/70 bg-indigo-50/60 text-indigo-900 dark:border-indigo-500/30 dark:bg-indigo-500/10 dark:text-indigo-200",
    action:
      "border-emerald-200/70 bg-emerald-50/60 text-emerald-900 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-200",
    guardrail:
      "border-rose-200/70 bg-rose-50/60 text-rose-900 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-200",
  }[kind];
  return (
    <div className={"rounded-md border px-3 py-2 text-[12.5px] leading-relaxed " + tone}>
      {text}
    </div>
  );
}

// ===========================================================================
// ActionDialog
//
// Per-tool structured forms instead of a raw JSON editor. Each registered
// tool gets its own purpose-built config UI so the canvas author works in
// the language of the product (service types, question prompts, FAQ pairs)
// rather than the language of the schema.
//
// Includes a "Used by" panel listing which intents trigger this action,
// with click-to-jump.
// ===========================================================================

function ActionConfigEditor({
  toolId,
  config,
  onPatch,
}: {
  toolId: string;
  config: Record<string, unknown>;
  onPatch: (patch: Record<string, unknown>) => void;
}) {
  switch (toolId) {
    case "book_appointment":
      return <BookAppointmentConfig config={config} onPatch={onPatch} />;
    case "qualify_lead":
      return <QualifyLeadConfig config={config} onPatch={onPatch} />;
    case "faq_lookup":
      return <FaqLookupConfig config={config} onPatch={onPatch} />;
    case "escalate_to_human":
      return <EscalateToHumanConfig config={config} onPatch={onPatch} />;
    default:
      return <RawJsonConfig config={config} onPatch={onPatch} />;
  }
}

function BookAppointmentConfig({
  config,
  onPatch,
}: {
  config: Record<string, unknown>;
  onPatch: (patch: Record<string, unknown>) => void;
}) {
  const serviceTypes = (Array.isArray(config.serviceTypes) ? config.serviceTypes : []) as string[];
  const duration = typeof config.defaultDurationMin === "number" ? config.defaultDurationMin : 60;

  return (
    <div className="space-y-5">
      <ChipList
        label="Service types"
        hint="What kinds of work this action can book. The agent picks one based on the conversation."
        placeholder="Add a service type…"
        emptyLabel="Add at least one service type."
        removeAriaPrefix="Remove service type"
        max={20}
        items={serviceTypes}
        onAdd={(value) => onPatch({ serviceTypes: [...serviceTypes, value] })}
        onRemove={(value) => onPatch({ serviceTypes: serviceTypes.filter((s) => s !== value) })}
      />
      <FieldGroup
        label="Default duration"
        hint="How long the agent should expect each appointment to take."
      >
        <div className="flex items-center gap-2">
          <input
            type="number"
            min={5}
            max={480}
            step={5}
            value={duration}
            onChange={(e) => onPatch({ defaultDurationMin: Number(e.target.value) })}
            className="h-10 w-28 rounded-md border border-zinc-200 bg-white px-3 text-[13.5px] tabular-nums text-zinc-900 focus-visible:border-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30 dark:border-zinc-800 dark:bg-zinc-950/40 dark:text-zinc-100"
          />
          <span className="text-[12.5px] text-zinc-500">minutes</span>
        </div>
      </FieldGroup>
    </div>
  );
}

function QualifyLeadConfig({
  config,
  onPatch,
}: {
  config: Record<string, unknown>;
  onPatch: (patch: Record<string, unknown>) => void;
}) {
  const raw = Array.isArray(config.questions) ? config.questions : [];
  const questions = raw.filter(
    (q): q is { id: string; prompt: string } =>
      typeof q === "object" &&
      q !== null &&
      typeof (q as { id?: unknown }).id === "string" &&
      typeof (q as { prompt?: unknown }).prompt === "string",
  );

  function patchAt(i: number, p: Partial<{ id: string; prompt: string }>) {
    onPatch({
      questions: questions.map((q, idx) => (idx === i ? { ...q, ...p } : q)),
    });
  }
  function removeAt(i: number) {
    onPatch({ questions: questions.filter((_, idx) => idx !== i) });
  }
  function add() {
    const id = `q_${Math.random().toString(36).slice(2, 6)}`;
    onPatch({ questions: [...questions, { id, prompt: "" }] });
  }

  return (
    <section aria-labelledby="qualify-questions">
      <div className="mb-2 flex items-baseline justify-between">
        <div>
          <h3
            id="qualify-questions"
            className="text-[11px] font-medium uppercase tracking-wider text-zinc-500"
          >
            Questions to ask
          </h3>
          <p className="mt-0.5 text-[11.5px] text-zinc-500">
            The agent will ask these in conversation. Each needs a stable id (used in the score) and
            a prompt the customer sees.
          </p>
        </div>
        <span className="text-[11px] tabular-nums text-zinc-500">{questions.length}</span>
      </div>

      <ul className="space-y-2">
        {questions.length === 0 && (
          <li className="rounded-md border border-dashed border-zinc-200 bg-zinc-50 px-3 py-3 text-center text-[12.5px] text-zinc-500 dark:border-zinc-800 dark:bg-zinc-900/30">
            No questions yet.
          </li>
        )}
        {questions.map((q, i) => (
          <li
            key={i}
            className="group flex items-start gap-2 rounded-md border border-zinc-200 bg-white px-3 py-2 dark:border-zinc-800 dark:bg-zinc-900/40"
          >
            <span
              className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-zinc-100 text-[10.5px] font-mono text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400"
              aria-hidden="true"
            >
              {i + 1}
            </span>
            <div className="flex-1 space-y-1.5">
              <input
                value={q.id}
                onChange={(e) => patchAt(i, { id: e.target.value })}
                aria-label={`Question ${i + 1} id`}
                placeholder="q_id"
                className="block h-7 w-32 rounded border border-zinc-200 bg-zinc-50 px-2 font-mono text-[11.5px] text-zinc-700 focus-visible:border-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30 dark:border-zinc-800 dark:bg-zinc-950/40 dark:text-zinc-300"
              />
              <input
                value={q.prompt}
                onChange={(e) => patchAt(i, { prompt: e.target.value })}
                aria-label={`Question ${i + 1} prompt`}
                placeholder="What should the agent ask?"
                className="block h-9 w-full rounded-md border border-zinc-200 bg-white px-3 text-[13px] text-zinc-900 focus-visible:border-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30 dark:border-zinc-800 dark:bg-zinc-950/40 dark:text-zinc-100"
              />
            </div>
            <button
              type="button"
              onClick={() => removeAt(i)}
              aria-label={`Remove question ${i + 1}`}
              className="inline-flex h-7 w-7 items-center justify-center rounded text-zinc-400 opacity-0 hover:bg-zinc-100 hover:text-zinc-700 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 group-hover:opacity-100 dark:hover:bg-zinc-800 dark:hover:text-zinc-200"
            >
              <CloseIcon />
            </button>
          </li>
        ))}
      </ul>

      <button
        type="button"
        onClick={add}
        className="mt-3 inline-flex h-8 items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-2.5 text-[12.5px] font-medium text-zinc-800 hover:bg-zinc-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 dark:border-zinc-800 dark:bg-zinc-900/40 dark:text-zinc-200 dark:hover:bg-zinc-800/60"
      >
        <PlusIcon />
        Add question
      </button>
    </section>
  );
}

/**
 * FAQ lookup action config (ADR 0021).
 *
 * The action no longer carries its own Q&A entries — those live in
 * the tenant's central Knowledge Base. This config carries only an
 * optional set of tags that scope which KB entries the action will
 * search. Empty tags = search the whole KB.
 *
 * A richer multi-select autocomplete driven by `/knowledge-base/tags`
 * lives in the polished UI pass; this minimal text input is the
 * compatibility shim that keeps the editor working immediately.
 */
function FaqLookupConfig({
  config,
  onPatch,
}: {
  config: Record<string, unknown>;
  onPatch: (patch: Record<string, unknown>) => void;
}) {
  const rawTags = Array.isArray(config.tags) ? config.tags : [];
  const tags = rawTags.filter((t): t is string => typeof t === "string");

  return (
    <section aria-labelledby="faq-tags">
      <div className="mb-2">
        <h3
          id="faq-tags"
          className="text-[11px] font-medium uppercase tracking-wider text-zinc-500"
        >
          Knowledge base filter
        </h3>
        <p className="mt-0.5 text-[11.5px] text-zinc-500">
          The agent will only consider Knowledge Base entries that carry every tag listed here.
          Leave blank to search the whole knowledge base.
        </p>
      </div>

      <ChipsInput
        value={tags}
        placeholder="e.g. pricing, after-hours"
        title="Knowledge base tag filter — type a tag and press Enter"
        // KB tag slugs allow hyphens/underscores; lowercase for consistency.
        clean={(s) => s.toLowerCase().replace(/[^a-z0-9 _-]/g, "")}
        onChange={(items) => onPatch({ tags: items })}
      />

      <p className="mt-3 text-[11.5px] text-zinc-500">
        Add or edit entries on the{" "}
        <a
          href="/agentflow/knowledge-base"
          className="font-medium text-brand-600 hover:underline dark:text-brand-400"
        >
          Knowledge Base
        </a>{" "}
        page.
      </p>
    </section>
  );
}

function EscalateToHumanConfig({
  config,
  onPatch,
}: {
  config: Record<string, unknown>;
  onPatch: (patch: Record<string, unknown>) => void;
}) {
  const message = typeof config.message === "string" ? config.message : "";
  return (
    <FieldGroup
      label="Message to customer"
      hint="What the agent says when it hands off to a human."
    >
      <textarea
        value={message}
        onChange={(e) => onPatch({ message: e.target.value })}
        rows={4}
        className="block w-full resize-none rounded-md border border-zinc-200 bg-white px-3 py-2 text-[13px] text-zinc-900 focus-visible:border-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30 dark:border-zinc-800 dark:bg-zinc-950/40 dark:text-zinc-100"
      />
    </FieldGroup>
  );
}

function RawJsonConfig({
  config,
  onPatch,
}: {
  config: Record<string, unknown>;
  onPatch: (patch: Record<string, unknown>) => void;
}) {
  return (
    <FieldGroup label="Config (JSON)" hint="No structured editor for this tool yet.">
      <textarea
        defaultValue={JSON.stringify(config, null, 2)}
        onBlur={(e) => {
          try {
            const parsed = JSON.parse(e.target.value) as Record<string, unknown>;
            onPatch(parsed);
          } catch {
            /* user fixes on next blur */
          }
        }}
        rows={8}
        className="block w-full resize-none rounded-md border border-zinc-200 bg-white px-3 py-2 font-mono text-[12px] text-zinc-900 focus-visible:border-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30 dark:border-zinc-800 dark:bg-zinc-950/40 dark:text-zinc-100"
      />
    </FieldGroup>
  );
}

// ===========================================================================
// GuardrailDialog
//
// Kind-specific editors:
//   - never_say        → ChipList of phrases
//   - must_confirm_before → checkbox grid (one per action, tool icon visible)
//   - stay_on_topic    → ChipList of topics
//
// Each kind also gets a tailored explainer so the author knows what they're
// signing up for.
// ===========================================================================

/** Substitute {{n}} placeholders for the live preview; unfilled stay as {{key}}. */
function renderTemplatePreview(body: string, vars: Record<string, string>): string {
  return body.replace(/\{\{\s*([\w]+)\s*\}\}/g, (_m, k: string) =>
    (vars[k] ?? "").trim() ? vars[k]!.trim() : `{{${k}}}`,
  );
}

function OutboundModal({
  flow,
  onClose,
  onGoToChannels,
}: {
  flow: AgentFlowDTO;
  onClose: () => void;
  /** Switch the editor to the Channels tab (to register a WhatsApp template). */
  onGoToChannels?: () => void;
}) {
  const [bindings, setBindings] = useState<FlowChannelBindingDTO[] | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [selectedBindingId, setSelectedBindingId] = useState<string | null>(null);
  const [recipient, setRecipient] = useState("");
  const [message, setMessage] = useState("");
  const [attestConsent, setAttestConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [sent, setSent] = useState<{ conversationId: string; message: string } | null>(null);

  // WhatsApp: approved templates from the connected account (loaded on demand).
  const [waLoading, setWaLoading] = useState(false);
  const [waLoadErr, setWaLoadErr] = useState<string | null>(null);
  const [waBindingExists, setWaBindingExists] = useState(true);
  const [waTemplates, setWaTemplates] = useState<ApprovedWhatsAppTemplateDTO[] | null>(null);
  const [selectedContentSid, setSelectedContentSid] = useState<string | null>(null);
  const [varValues, setVarValues] = useState<Record<string, string>>({});
  const [waReloadKey, setWaReloadKey] = useState(0);

  // Pre-fill the SMS/RCS first message with the flow's greeting verbatim —
  // the tenant sees what the customer will see (server interpolates at send).
  useEffect(() => {
    setMessage(flow.definition.greeting ?? "");
  }, [flow.definition.greeting]);

  // Load the flow's bindings — only telephony channels are eligible for
  // outbound; webhook bindings are filtered out.
  useEffect(() => {
    let cancelled = false;
    api.agentflow
      .listChannelBindings(flow.id)
      .then((all) => {
        if (cancelled) return;
        const telephony = all.filter(
          (b) => b.channel === "sms" || b.channel === "rcs" || b.channel === "whatsapp",
        );
        setBindings(telephony);
        if (telephony.length === 1) setSelectedBindingId(telephony[0]!.id);
      })
      .catch((e) => {
        if (cancelled) return;
        setLoadErr(e instanceof ApiClientError ? e.message : "Failed to load channels");
      });
    return () => {
      cancelled = true;
    };
  }, [flow.id]);

  const selected = bindings?.find((b) => b.id === selectedBindingId) ?? null;
  const isWhatsApp = selected?.channel === "whatsapp";

  // For a WhatsApp binding, load the connected account's approved templates.
  useEffect(() => {
    if (!isWhatsApp) {
      setWaTemplates(null);
      setSelectedContentSid(null);
      setVarValues({});
      setWaLoadErr(null);
      return;
    }
    let cancelled = false;
    setWaLoading(true);
    setWaLoadErr(null);
    api.agentflow
      .listApprovedWhatsAppTemplates(flow.id)
      .then((d) => {
        if (cancelled) return;
        setWaTemplates(d.templates);
        setWaBindingExists(d.whatsappBindingExists);
        setSelectedContentSid(d.templates.length === 1 ? d.templates[0]!.contentSid : null);
        setVarValues({});
      })
      .catch((e) => {
        if (cancelled) return;
        setWaLoadErr(e instanceof ApiClientError ? e.message : "Failed to load templates");
      })
      .finally(() => {
        if (!cancelled) setWaLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isWhatsApp, flow.id, waReloadKey]);

  const selectedTemplate = waTemplates?.find((t) => t.contentSid === selectedContentSid) ?? null;
  const missingVars = selectedTemplate
    ? selectedTemplate.variables.filter((v) => !(varValues[v] ?? "").trim())
    : [];
  const waReady = isWhatsApp ? !!selectedTemplate && missingVars.length === 0 : true;
  const canSend =
    !!selected &&
    recipient.trim().length > 0 &&
    !busy &&
    waReady &&
    (isWhatsApp || message.trim().length > 0);

  async function send() {
    if (!selected) return;
    setBusy(true);
    setErr(null);
    try {
      const channel = selected.channel as "sms" | "rcs" | "whatsapp";
      const res = await api.agentflow.initiateOutbound(flow.id, {
        channel,
        channelAddress: selected.address,
        endCustomerRef: recipient.trim(),
        attestConsent: attestConsent || undefined,
        ...(isWhatsApp && selectedTemplate
          ? {
              contentSid: selectedTemplate.contentSid,
              contentVariables: Object.fromEntries(
                selectedTemplate.variables.map((v) => [v, (varValues[v] ?? "").trim()]),
              ),
            }
          : {
              initialMessage:
                message.trim() === flow.definition.greeting ? undefined : message.trim(),
            }),
      });
      setSent({ conversationId: res.conversationId, message: res.assistantMessage });
    } catch (e) {
      if (e instanceof ApiClientError && e.code === "consent_attestation_required") {
        setErr(
          "We don't have consent on file for this number. Tick the consent box if you have it, then try again.",
        );
      } else {
        setErr(e instanceof ApiClientError ? e.message : "Send failed");
      }
    } finally {
      setBusy(false);
    }
  }

  function goChannels() {
    onGoToChannels?.();
    onClose();
  }

  return (
    <ChannelDialog
      title={sent ? "Message sent" : "Send a message"}
      onClose={onClose}
      primary={
        sent ? (
          <Button onClick={onClose}>Done</Button>
        ) : (
          <Button onClick={() => void send()} disabled={!canSend}>
            {busy ? "Sending…" : "Send"}
          </Button>
        )
      }
    >
      {sent ? (
        <div className="space-y-3">
          <p className="text-[12.5px] text-zinc-700 dark:text-zinc-200">
            Message delivered. Reply lands back here as a conversation.
          </p>
          <div className="rounded-md border border-zinc-200 bg-zinc-50 p-3 dark:border-zinc-800 dark:bg-zinc-900/40">
            <p className="text-[11px] uppercase tracking-wider text-zinc-500">Sent</p>
            <p className="mt-1 whitespace-pre-wrap text-[13px] text-zinc-800 dark:text-zinc-200">
              {sent.message}
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {loadErr && <p className="text-[12px] text-red-600">{loadErr}</p>}

          {/* Binding picker — auto-skips when there's exactly one. */}
          {bindings && bindings.length > 1 && (
            <label className="flex flex-col gap-1">
              <span className="text-[11px] font-medium uppercase tracking-wider text-zinc-500">
                Send from
              </span>
              <select
                value={selectedBindingId ?? ""}
                onChange={(e) => setSelectedBindingId(e.target.value)}
                className="h-9 rounded-md border border-zinc-200 bg-white px-2.5 text-[13px] dark:border-zinc-800 dark:bg-zinc-950/40 dark:text-zinc-100"
              >
                <option value="" disabled>
                  Pick a channel
                </option>
                {bindings.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.channel.toUpperCase()} · {b.address}
                  </option>
                ))}
              </select>
            </label>
          )}
          {bindings && bindings.length === 0 && (
            <p className="rounded-md border border-dashed border-zinc-200 px-3 py-3 text-[12.5px] text-zinc-500 dark:border-zinc-800">
              No Twilio channels connected on this flow yet. Connect one from the Channels card
              first.
            </p>
          )}

          {bindings && bindings.length > 0 && (
            <>
              <label className="flex flex-col gap-1">
                <span className="text-[11px] font-medium uppercase tracking-wider text-zinc-500">
                  To (E.164)
                </span>
                <input
                  value={recipient}
                  onChange={(e) => setRecipient(e.target.value)}
                  placeholder="+14155551234"
                  className="h-9 rounded-md border border-zinc-200 bg-white px-3 font-mono text-[12px] dark:border-zinc-800 dark:bg-zinc-950/40"
                />
              </label>

              {/* SMS / RCS: free-form first message. */}
              {!isWhatsApp && (
                <label className="flex flex-col gap-1">
                  <span className="text-[11px] font-medium uppercase tracking-wider text-zinc-500">
                    First message
                  </span>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={4}
                    placeholder="Pre-filled with the flow's greeting. Edit if you want something different."
                    className="rounded-md border border-zinc-200 bg-white p-2.5 text-[13px] dark:border-zinc-800 dark:bg-zinc-950/40 dark:text-zinc-100"
                  />
                  <span className="text-[11px] text-zinc-500">{message.length} / 1600</span>
                </label>
              )}

              {/* WhatsApp: pick an approved template + fill its variables. */}
              {isWhatsApp && (
                <WhatsAppTemplateChooser
                  loading={waLoading}
                  loadErr={waLoadErr}
                  bindingExists={waBindingExists}
                  templates={waTemplates}
                  selectedContentSid={selectedContentSid}
                  onSelect={(sid) => {
                    setSelectedContentSid(sid);
                    setVarValues({});
                  }}
                  varValues={varValues}
                  onVarChange={(k, v) => setVarValues((prev) => ({ ...prev, [k]: v }))}
                  onRetry={() => setWaReloadKey((k) => k + 1)}
                  onGoToChannels={onGoToChannels ? goChannels : undefined}
                />
              )}

              <label className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-[12px] text-amber-900 dark:border-amber-700/50 dark:bg-amber-900/20 dark:text-amber-200">
                <input
                  type="checkbox"
                  checked={attestConsent}
                  onChange={(e) => setAttestConsent(e.target.checked)}
                  className="mt-0.5"
                />
                <span>
                  I have consent to message this recipient. (Required if we don&rsquo;t have a
                  consent record yet — see your Compliance page.)
                </span>
              </label>

              {err && <p className="text-[12px] text-red-600">{err}</p>}
            </>
          )}
        </div>
      )}
    </ChannelDialog>
  );
}

/** WhatsApp approved-template picker + variables form for the outbound modal. */
function WhatsAppTemplateChooser({
  loading,
  loadErr,
  bindingExists,
  templates,
  selectedContentSid,
  onSelect,
  varValues,
  onVarChange,
  onRetry,
  onGoToChannels,
}: {
  loading: boolean;
  loadErr: string | null;
  bindingExists: boolean;
  templates: ApprovedWhatsAppTemplateDTO[] | null;
  selectedContentSid: string | null;
  onSelect: (contentSid: string) => void;
  varValues: Record<string, string>;
  onVarChange: (key: string, value: string) => void;
  onRetry: () => void;
  onGoToChannels?: () => void;
}) {
  const selected = templates?.find((t) => t.contentSid === selectedContentSid) ?? null;

  if (loading) {
    return (
      <p className="rounded-md border border-zinc-200 px-3 py-2.5 text-[12.5px] text-zinc-500 dark:border-zinc-800">
        Loading approved templates…
      </p>
    );
  }

  if (loadErr) {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2.5 text-[12px] text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
        <p>{loadErr}</p>
        <button
          type="button"
          onClick={onRetry}
          className="mt-1.5 font-medium underline underline-offset-2"
        >
          Retry
        </button>
      </div>
    );
  }

  // No WhatsApp sender attached, or none approved → guide to the Channels tab.
  if (!bindingExists || (templates && templates.length === 0)) {
    const channelsLink = onGoToChannels ? (
      <button
        type="button"
        onClick={onGoToChannels}
        className="font-medium underline underline-offset-2"
      >
        the Channels tab
      </button>
    ) : (
      <span className="font-medium">the Channels tab</span>
    );
    return (
      <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2.5 text-[12px] text-amber-900 dark:border-amber-700/50 dark:bg-amber-900/20 dark:text-amber-200">
        {!bindingExists ? (
          <p>Attach a WhatsApp sender on {channelsLink} before sending on WhatsApp.</p>
        ) : (
          <p>
            No approved WhatsApp templates on this account yet. Register one on {channelsLink} and
            submit it for WhatsApp approval. (Approval is handled by WhatsApp and can take a while.)
          </p>
        )}
      </div>
    );
  }

  if (!templates) return null;

  return (
    <div className="space-y-3">
      <label className="flex flex-col gap-1">
        <span className="text-[11px] font-medium uppercase tracking-wider text-zinc-500">
          WhatsApp template
        </span>
        <select
          value={selectedContentSid ?? ""}
          onChange={(e) => onSelect(e.target.value)}
          className="h-9 rounded-md border border-zinc-200 bg-white px-2.5 text-[13px] dark:border-zinc-800 dark:bg-zinc-950/40 dark:text-zinc-100"
        >
          <option value="" disabled>
            Pick an approved template
          </option>
          {templates.map((t) => (
            <option key={t.contentSid} value={t.contentSid}>
              {t.friendlyName} ({t.language})
            </option>
          ))}
        </select>
      </label>

      {selected && selected.variables.length > 0 && (
        <div className="flex flex-col gap-2">
          <span className="text-[11px] font-medium uppercase tracking-wider text-zinc-500">
            Template values
          </span>
          {selected.variables.map((key) => (
            <label key={key} className="flex items-center gap-2">
              <span className="w-12 shrink-0 font-mono text-[12px] text-zinc-500">
                {`{{${key}}}`}
              </span>
              <input
                value={varValues[key] ?? ""}
                onChange={(e) => onVarChange(key, e.target.value)}
                placeholder={`Value for {{${key}}}`}
                className="h-9 flex-1 rounded-md border border-zinc-200 bg-white px-3 text-[13px] dark:border-zinc-800 dark:bg-zinc-950/40 dark:text-zinc-100"
              />
            </label>
          ))}
        </div>
      )}

      {selected && (
        <div className="rounded-md border border-zinc-200 bg-zinc-50 p-3 dark:border-zinc-800 dark:bg-zinc-900/40">
          <p className="text-[11px] uppercase tracking-wider text-zinc-500">Preview</p>
          <p className="mt-1 whitespace-pre-wrap text-[13px] text-zinc-800 dark:text-zinc-200">
            {renderTemplatePreview(selected.body, varValues)}
          </p>
        </div>
      )}
    </div>
  );
}

function ChannelDialog({
  title,
  onClose,
  primary,
  children,
}: {
  title: string;
  onClose: () => void;
  /** Right-aligned button. Pass `null` when the body owns its own actions. */
  primary: React.ReactNode | null;
  children: React.ReactNode;
}) {
  useEffect(() => {
    function onEsc(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [onClose]);
  function onBackdropMouseDown(e: React.MouseEvent) {
    if (e.target === e.currentTarget) onClose();
  }
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onMouseDown={onBackdropMouseDown}
      className="fixed inset-0 z-30 flex items-center justify-center bg-zinc-950/40 p-4"
    >
      <div className="flex w-full max-w-lg flex-col overflow-hidden rounded-lg border border-zinc-200 bg-white shadow-xl dark:border-zinc-800 dark:bg-zinc-900">
        <header className="border-b border-zinc-200 px-5 py-3.5 dark:border-zinc-800">
          <h2 className="text-[15px] font-semibold text-zinc-900 dark:text-zinc-100">{title}</h2>
        </header>
        <div className="space-y-3 px-5 py-4">{children}</div>
        <footer className="flex items-center justify-end gap-2 border-t border-zinc-200 bg-zinc-50 px-5 py-3 dark:border-zinc-800 dark:bg-zinc-900/60">
          <Button variant="secondary" onClick={onClose}>
            {primary ? "Cancel" : "Close"}
          </Button>
          {primary}
        </footer>
      </div>
    </div>
  );
}

type WizardStep = "type" | "pick-account" | "pick-number" | "pick-sender" | "webhook-confirm";
/** Which channel the account-picker is feeding: SMS-via-number, or a channel sender. */
type ChannelMode = "number" | "voice" | "whatsapp" | "rcs";

/**
 * WhatsApp template panel — shown on the Channels tab once a WhatsApp
 * sender is bound. WhatsApp requires an approved template to start
 * (business-initiated) conversations, so this is where the user submits
 * the greeting for approval and watches its status. The flow can't
 * initiate WhatsApp messages until status is `approved`.
 */
function WhatsAppTemplatePanel({ flowId }: { flowId: string }) {
  const [data, setData] = useState<GetWhatsAppTemplateResponse | null>(null);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function hydrate(d: GetWhatsAppTemplateResponse) {
    setData(d);
    setBody(d.template?.bodyText ?? d.suggestedBody ?? "");
  }

  useEffect(() => {
    void api.agentflow
      .getWhatsAppTemplate(flowId)
      .then(hydrate)
      .catch(() => setData(null));
  }, [flowId]);

  if (!data || !data.whatsappBindingExists) return null;
  const t = data.template;

  async function register() {
    setBusy(true);
    setErr(null);
    try {
      const tpl = await api.agentflow.registerWhatsAppTemplate(flowId, { body });
      setData((prev) => (prev ? { ...prev, template: tpl } : prev));
    } catch (e) {
      setErr(e instanceof ApiClientError ? e.message : "Failed to submit template");
    } finally {
      setBusy(false);
    }
  }

  async function refresh() {
    setRefreshing(true);
    setErr(null);
    try {
      const tpl = await api.agentflow.refreshWhatsAppTemplate(flowId);
      setData((prev) => (prev ? { ...prev, template: tpl } : prev));
    } catch (e) {
      setErr(e instanceof ApiClientError ? e.message : "Failed to refresh status");
    } finally {
      setRefreshing(false);
    }
  }

  const statusColor =
    t?.status === "approved"
      ? "text-emerald-600"
      : t?.status === "rejected"
        ? "text-red-600"
        : "text-amber-600";

  // Only allow (re)submission when there's no template yet, or the prior
  // one was rejected. Approved / in-review templates are locked.
  const canSubmit = !t || t.status === "rejected";

  return (
    <div className="mt-5 rounded-md border border-zinc-200 p-4 dark:border-zinc-800/80">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-[13.5px] font-medium text-zinc-900 dark:text-zinc-100">
          WhatsApp template
        </h3>
        {t && (
          <span className={`text-[11.5px] font-medium uppercase ${statusColor}`}>{t.status}</span>
        )}
      </div>
      <p className="mb-3 text-[11.5px] text-zinc-500">
        WhatsApp requires an approved template to start conversations. Edit the opening message and
        submit it for approval (usually 5 minutes to 24 hours). The flow can&rsquo;t initiate
        WhatsApp messages until it&rsquo;s approved.
      </p>
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={4}
        disabled={!canSubmit}
        className="w-full rounded-md border border-zinc-200 bg-white px-3 py-2 text-[13px] disabled:bg-zinc-50 dark:border-zinc-800 dark:bg-zinc-950/40 dark:text-zinc-100 dark:disabled:bg-zinc-900/40"
        placeholder="Hi, thanks for reaching out…"
      />
      {t?.status === "rejected" && t.rejectionReason && (
        <p className="mt-2 text-[11.5px] text-red-600">Rejected: {t.rejectionReason}</p>
      )}
      {t?.status === "approved" && (
        <p className="mt-2 text-[11.5px] text-emerald-600">
          Approved — this flow can initiate WhatsApp messages.
        </p>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-3">
        {canSubmit && (
          <Button onClick={() => void register()} disabled={busy || !body.trim()}>
            {busy ? "Submitting…" : t ? "Resubmit for approval" : "Submit for approval"}
          </Button>
        )}
        {t && (
          <Button variant="secondary" onClick={() => void refresh()} disabled={refreshing}>
            {refreshing ? "Checking…" : "Refresh status"}
          </Button>
        )}
        {t && !canSubmit && t.status !== "approved" && (
          <span className="text-[11.5px] text-zinc-500">
            Submitted — locked while WhatsApp reviews it.
          </span>
        )}
        {err && <span className="text-[12px] text-red-600">{err}</span>}
      </div>
    </div>
  );
}

function ChannelsEditor({ flowId }: { flowId: string }) {
  const [bindings, setBindings] = useState<FlowChannelBindingDTO[] | null>(null);
  /** Global busy — used only for the webhook-create button. */
  const [busy, setBusy] = useState(false);
  /**
   * Per-row spinners. Keyed by `twilioPhoneSid` for the number-picker
   * (so only the row the user actually clicked shows "Connecting…") and
   * by `bindingId` for the bindings list (so only the row being
   * removed shows "Removing…"). Prevents the "every button says
   * Connecting" confusion.
   */
  const [connectingSid, setConnectingSid] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // Wizard state — `step` is null when the wizard is closed. The wizard
  // remains modal (it's a guided multi-step flow), but the channels list
  // itself is now a page, not a dialog.
  const [step, setStep] = useState<WizardStep | null>(null);
  const [twilioAccounts, setTwilioAccounts] = useState<TwilioConnection[] | null>(null);
  const [accountLoadErr, setAccountLoadErr] = useState<string | null>(null);
  const [selectedConnection, setSelectedConnection] = useState<TwilioConnection | null>(null);
  const [twilioNumbers, setTwilioNumbers] = useState<TwilioNumberDTO[] | null>(null);
  const [twilioLoadErr, setTwilioLoadErr] = useState<string | null>(null);
  const [twilioQuery, setTwilioQuery] = useState("");
  const [twilioSearching, setTwilioSearching] = useState(false);
  const [createdSecret, setCreatedSecret] = useState<CreateFlowChannelBindingResponse | null>(null);
  // Channel-sender path (WhatsApp / RCS).
  const [channelMode, setChannelMode] = useState<ChannelMode>("number");
  const [channelSenders, setChannelSenders] = useState<TwilioChannelSenderDTO[] | null>(null);
  const [channelSendersErr, setChannelSendersErr] = useState<string | null>(null);
  const [attachNote, setAttachNote] = useState<string | null>(null);

  async function refresh() {
    try {
      const list = await api.agentflow.listChannelBindings(flowId);
      setBindings(list);
    } catch (e) {
      setErr(e instanceof ApiClientError ? e.message : "Failed to load channels");
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowId]);

  function openWizard() {
    setStep("type");
    setTwilioNumbers(null);
    setTwilioLoadErr(null);
    setTwilioQuery("");
    setTwilioAccounts(null);
    setSelectedConnection(null);
    setAccountLoadErr(null);
    setChannelMode("number");
    setChannelSenders(null);
    setChannelSendersErr(null);
    setAttachNote(null);
    setErr(null);
  }

  /** The account-picker step after the account is known, per mode. */
  function afterAccountStep(mode: ChannelMode): WizardStep {
    // Voice is number-based (like SMS): both use the number picker.
    return mode === "number" || mode === "voice" ? "pick-number" : "pick-sender";
  }

  async function startChannelPath(mode: ChannelMode) {
    // Fetch the tenant's Twilio connections. If there's exactly one
    // active account, auto-select + skip straight to the picker for `mode`.
    setChannelMode(mode);
    setStep("pick-account");
    setAccountLoadErr(null);
    setTwilioAccounts(null);
    setChannelSenders(null);
    setChannelSendersErr(null);
    try {
      const list = await api.twilio.list();
      const active = list.connections.filter((c) => c.status === "active");
      setTwilioAccounts(active);
      if (active.length === 1) {
        const sole = active[0]!;
        setSelectedConnection(sole);
        setTwilioNumbers(null);
        setTwilioQuery("");
        setTwilioLoadErr(null);
        setStep(afterAccountStep(mode));
      }
    } catch (e) {
      setAccountLoadErr(e instanceof ApiClientError ? e.message : "Failed to load Twilio accounts");
    }
  }

  function chooseAccount(conn: TwilioConnection) {
    setSelectedConnection(conn);
    setTwilioNumbers(null);
    setTwilioQuery("");
    setTwilioLoadErr(null);
    setChannelSenders(null);
    setChannelSendersErr(null);
    setStep(afterAccountStep(channelMode));
  }

  // Debounced search — fires 300ms after the user stops typing while
  // the pick-number step is open AND an account has been selected.
  // Server-side filter via Twilio's PhoneNumber / FriendlyName params.
  useEffect(() => {
    if (step !== "pick-number" || !selectedConnection) return;
    const handle = setTimeout(() => {
      setTwilioSearching(true);
      setTwilioLoadErr(null);
      api.agentflow
        .listTwilioNumbers({
          connectionId: selectedConnection.id,
          query: twilioQuery.trim() || undefined,
          limit: 10,
          // "Already connected" is per-channel: an SMS-bound number is still
          // offerable for voice, and vice versa (ADR 0029).
          channel: channelMode === "voice" ? "voice" : "sms",
        })
        .then(setTwilioNumbers)
        .catch((e) => {
          setTwilioLoadErr(e instanceof ApiClientError ? e.message : "Search failed");
        })
        .finally(() => setTwilioSearching(false));
    }, 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [twilioQuery, step, selectedConnection?.id, channelMode]);

  async function connectTwilio(sid: string) {
    if (!selectedConnection || connectingSid) return;
    setConnectingSid(sid);
    setErr(null);
    try {
      await api.agentflow.connectTwilioNumber(flowId, {
        twilioConnectionId: selectedConnection.id,
        twilioPhoneSid: sid,
      });
      setStep(null);
      await refresh();
    } catch (e) {
      setErr(e instanceof ApiClientError ? e.message : "Failed to connect number");
    } finally {
      setConnectingSid(null);
    }
  }

  // Voice connect (ADR 0029): points the number's Voice webhook at this flow.
  // Mirrors connectTwilio but hits the voice endpoint and creates a voice binding.
  async function connectVoiceNumber(sid: string) {
    if (!selectedConnection || connectingSid) return;
    setConnectingSid(sid);
    setErr(null);
    try {
      await api.agentflow.connectTwilioVoiceNumber(flowId, {
        twilioConnectionId: selectedConnection.id,
        twilioPhoneSid: sid,
      });
      setStep(null);
      await refresh();
    } catch (e) {
      setErr(e instanceof ApiClientError ? e.message : "Failed to connect number for voice");
    } finally {
      setConnectingSid(null);
    }
  }

  // Load the account's WhatsApp / RCS senders when the pick-sender step
  // opens. These are separate Twilio resources from phone numbers.
  useEffect(() => {
    if (
      step !== "pick-sender" ||
      !selectedConnection ||
      channelMode === "number" ||
      channelMode === "voice"
    )
      return;
    setChannelSenders(null);
    setChannelSendersErr(null);
    api.agentflow
      .listTwilioChannelSenders({ connectionId: selectedConnection.id, channel: channelMode })
      .then(setChannelSenders)
      .catch((e) =>
        setChannelSendersErr(e instanceof ApiClientError ? e.message : "Failed to load senders"),
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, selectedConnection?.id, channelMode]);

  async function connectSender(senderId: string) {
    if (!selectedConnection || channelMode === "number" || channelMode === "voice" || connectingSid)
      return;
    setConnectingSid(senderId);
    setErr(null);
    try {
      const res = await api.agentflow.connectChannelSender(flowId, {
        twilioConnectionId: selectedConnection.id,
        channel: channelMode,
        senderId,
      });
      setStep(null);
      // We auto-add the sender to the Messaging Service sender pool. Only
      // nudge if that auto-attach failed (transient Twilio error).
      setAttachNote(
        res.attachedToMessagingService
          ? null
          : `Connected, but we couldn't add this ${channelMode.toUpperCase()} sender to your Messaging Service automatically. Add it to the sender pool in the Twilio Console so messages route.`,
      );
      await refresh();
    } catch (e) {
      setErr(e instanceof ApiClientError ? e.message : "Failed to connect sender");
    } finally {
      setConnectingSid(null);
    }
  }

  async function connectWebhook() {
    setBusy(true);
    setErr(null);
    try {
      const created = await api.agentflow.createChannelBinding(flowId, { channel: "webhook" });
      if (created.signingSecretOnce) setCreatedSecret(created);
      setStep(null);
      await refresh();
    } catch (e) {
      setErr(e instanceof ApiClientError ? e.message : "Failed to create webhook");
    } finally {
      setBusy(false);
    }
  }

  async function remove(bindingId: string) {
    if (removingId) return;
    setRemovingId(bindingId);
    setErr(null);
    try {
      await api.agentflow.deleteChannelBinding(bindingId);
      await refresh();
    } catch (e) {
      setErr(e instanceof ApiClientError ? e.message : "Failed to remove channel");
    } finally {
      setRemovingId(null);
    }
  }

  return (
    <EditorPage
      eyebrow="Channels"
      title="Where this flow can be reached"
      subtitle="Connect a Twilio number for SMS/RCS/WhatsApp, or generate a webhook for programmatic transports. Each binding routes traffic to this flow."
    >
      <div className="flex items-center justify-between">
        <p className="text-[12px] text-zinc-500">
          {bindings === null
            ? "Loading…"
            : bindings.length === 0
              ? "No channels connected yet."
              : `${bindings.length} channel${bindings.length === 1 ? "" : "s"} connected.`}
        </p>
        <Button onClick={openWizard}>+ Connect channel</Button>
      </div>

      {err && <p className="mb-2 text-[11.5px] text-red-600">{err}</p>}
      {attachNote && (
        <div className="mb-3 flex items-start justify-between gap-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2.5 text-[12px] text-amber-900 dark:border-amber-700/50 dark:bg-amber-900/20 dark:text-amber-200">
          <p>{attachNote}</p>
          <button
            type="button"
            onClick={() => setAttachNote(null)}
            className="shrink-0 text-[11.5px] font-medium text-amber-900 hover:underline dark:text-amber-200"
          >
            Dismiss
          </button>
        </div>
      )}

      {bindings === null ? (
        <div className="h-12 animate-pulse rounded bg-zinc-100 dark:bg-zinc-800/40" />
      ) : bindings.length === 0 ? (
        <p className="rounded-md border border-dashed border-zinc-200 px-3 py-2 text-[12px] text-zinc-500 dark:border-zinc-800">
          No channels yet — hit “Connect channel” to wire this flow up.
        </p>
      ) : (
        <ul className="space-y-1.5">
          {bindings.map((b) => (
            <li
              key={b.id}
              className="flex items-center justify-between rounded-md border border-zinc-200 px-3 py-2 text-[12.5px] dark:border-zinc-800/80"
            >
              <div className="flex items-center gap-3">
                <span className="rounded bg-zinc-100 px-1.5 py-0.5 text-[10.5px] font-semibold uppercase text-zinc-700 dark:bg-zinc-800 dark:text-zinc-200">
                  {b.channel}
                </span>
                <code className="font-mono text-[12px] text-zinc-700 dark:text-zinc-200">
                  {b.address}
                </code>
                {b.channel === "webhook" && b.signingSecretOnFile && (
                  <span className="text-[10.5px] text-emerald-600">secret on file</span>
                )}
              </div>
              <button
                type="button"
                onClick={() => void remove(b.id)}
                disabled={removingId !== null}
                className="text-[11.5px] text-zinc-500 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {removingId === b.id ? "Removing…" : "Remove"}
              </button>
            </li>
          ))}
        </ul>
      )}

      {bindings && bindings.some((b) => b.channel === "whatsapp") && (
        <WhatsAppTemplatePanel flowId={flowId} />
      )}

      {/* Wizard step 1 — pick path */}
      {step === "type" && (
        <ChannelDialog title="Connect a channel" onClose={() => setStep(null)} primary={null}>
          <p className="mb-4 text-[12.5px] text-zinc-600 dark:text-zinc-300">
            Pick how customers will reach this flow.
          </p>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <button
              type="button"
              onClick={() => void startChannelPath("number")}
              className="rounded-lg border border-zinc-200 px-4 py-3 text-left transition hover:border-brand-400 hover:bg-brand-50 dark:border-zinc-800 dark:hover:border-brand-500 dark:hover:bg-brand-950/30"
            >
              <p className="text-[13px] font-semibold text-zinc-900 dark:text-zinc-100">
                SMS (Twilio number)
              </p>
              <p className="mt-1 text-[11.5px] text-zinc-500">
                A phone number on your connected Twilio account. We provision the Messaging Service
                automatically.
              </p>
            </button>
            <button
              type="button"
              onClick={() => void startChannelPath("voice")}
              className="rounded-lg border border-zinc-200 px-4 py-3 text-left transition hover:border-brand-400 hover:bg-brand-50 dark:border-zinc-800 dark:hover:border-brand-500 dark:hover:bg-brand-950/30"
            >
              <p className="text-[13px] font-semibold text-zinc-900 dark:text-zinc-100">
                Voice (Twilio number)
              </p>
              <p className="mt-1 text-[11.5px] text-zinc-500">
                A voice-capable phone number on your connected Twilio account. We point its Voice
                webhook at this flow and answer calls with a real-time voice agent.
              </p>
            </button>
            <button
              type="button"
              onClick={() => void startChannelPath("whatsapp")}
              className="rounded-lg border border-zinc-200 px-4 py-3 text-left transition hover:border-brand-400 hover:bg-brand-50 dark:border-zinc-800 dark:hover:border-brand-500 dark:hover:bg-brand-950/30"
            >
              <p className="text-[13px] font-semibold text-zinc-900 dark:text-zinc-100">WhatsApp</p>
              <p className="mt-1 text-[11.5px] text-zinc-500">
                A WhatsApp sender registered on your Twilio account. We list the ones already
                onboarded — pick one to bind.
              </p>
            </button>
            <button
              type="button"
              onClick={() => void startChannelPath("rcs")}
              className="rounded-lg border border-zinc-200 px-4 py-3 text-left transition hover:border-brand-400 hover:bg-brand-50 dark:border-zinc-800 dark:hover:border-brand-500 dark:hover:bg-brand-950/30"
            >
              <p className="text-[13px] font-semibold text-zinc-900 dark:text-zinc-100">RCS</p>
              <p className="mt-1 text-[11.5px] text-zinc-500">
                A registered RCS Business Messaging agent on your Twilio account. Pick an approved
                agent to bind.
              </p>
            </button>
            <button
              type="button"
              onClick={() => setStep("webhook-confirm")}
              className="rounded-lg border border-zinc-200 px-4 py-3 text-left transition hover:border-brand-400 hover:bg-brand-50 dark:border-zinc-800 dark:hover:border-brand-500 dark:hover:bg-brand-950/30"
            >
              <p className="text-[13px] font-semibold text-zinc-900 dark:text-zinc-100">
                Webhook (programmatic)
              </p>
              <p className="mt-1 text-[11.5px] text-zinc-500">
                Your server POSTs messages to a signed URL we generate. Use for chat widgets,
                internal tools, custom transports.
              </p>
            </button>
          </div>
        </ChannelDialog>
      )}

      {/* Wizard step 2 — pick Twilio account (auto-skipped if exactly one) */}
      {step === "pick-account" && (
        <ChannelDialog title="Choose a Twilio account" onClose={() => setStep(null)} primary={null}>
          {accountLoadErr && <p className="mb-3 text-[12px] text-red-600">{accountLoadErr}</p>}
          {twilioAccounts === null ? (
            <div className="h-20 animate-pulse rounded bg-zinc-100 dark:bg-zinc-800/40" />
          ) : twilioAccounts.length === 0 ? (
            <p className="rounded-md border border-dashed border-zinc-200 px-3 py-3 text-[12.5px] text-zinc-500 dark:border-zinc-800">
              No active Twilio accounts connected. Connect one from the Connections page first.
            </p>
          ) : (
            <ul className="space-y-1.5">
              {twilioAccounts.map((c) => (
                <li
                  key={c.id}
                  className="flex items-center justify-between gap-3 rounded-md border border-zinc-200 px-3 py-2 dark:border-zinc-800/80"
                >
                  <div>
                    <p className="text-[13px] font-medium text-zinc-900 dark:text-zinc-100">
                      {c.friendlyName || "Twilio account"}
                    </p>
                    <p className="font-mono text-[11.5px] text-zinc-500">
                      {c.accountSid.slice(0, 6)}…{c.accountSid.slice(-4)}
                    </p>
                  </div>
                  <Button onClick={() => chooseAccount(c)}>Use this account</Button>
                </li>
              ))}
            </ul>
          )}
        </ChannelDialog>
      )}

      {/* Wizard step 3 — pick Twilio number from the selected account */}
      {step === "pick-number" && selectedConnection && (
        <ChannelDialog
          title={`Numbers in ${selectedConnection.friendlyName || "this Twilio account"}`}
          onClose={() => setStep(null)}
          primary={null}
        >
          {twilioAccounts && twilioAccounts.length > 1 && (
            <div className="mb-3 flex items-center justify-between rounded-md bg-zinc-50 px-3 py-2 dark:bg-zinc-900/40">
              <span className="text-[11.5px] text-zinc-500">
                Account:{" "}
                <span className="text-zinc-700 dark:text-zinc-200">
                  {selectedConnection.friendlyName || "Twilio account"}
                </span>{" "}
                <span className="font-mono text-[11px] text-zinc-400">
                  ({selectedConnection.accountSid.slice(0, 6)}…
                  {selectedConnection.accountSid.slice(-4)})
                </span>
              </span>
              <button
                type="button"
                onClick={() => setStep("pick-account")}
                className="text-[11.5px] text-brand-600 hover:underline"
              >
                Change
              </button>
            </div>
          )}
          {/* Search — debounced 300ms. Empty input shows the 10 most recent. */}
          <div className="mb-3 flex items-center gap-2">
            <input
              type="search"
              value={twilioQuery}
              onChange={(e) => setTwilioQuery(e.target.value)}
              placeholder="Search by number (e.g. +1415) or friendly name"
              className="h-9 flex-1 rounded-md border border-zinc-200 bg-white px-3 text-[13px] dark:border-zinc-800 dark:bg-zinc-950/40 dark:text-zinc-100"
              autoFocus
            />
            {twilioSearching && <span className="text-[11.5px] text-zinc-500">Searching…</span>}
          </div>

          <p className="mb-2 text-[11px] uppercase tracking-wider text-zinc-500">
            {twilioQuery.trim() ? `Matches` : `10 most recent`}
          </p>

          {twilioLoadErr && <p className="mb-3 text-[12px] text-red-600">{twilioLoadErr}</p>}
          {twilioNumbers === null ? (
            <div className="h-20 animate-pulse rounded bg-zinc-100 dark:bg-zinc-800/40" />
          ) : twilioNumbers.length === 0 ? (
            <p className="rounded-md border border-dashed border-zinc-200 px-3 py-3 text-[12.5px] text-zinc-500 dark:border-zinc-800">
              {twilioQuery.trim()
                ? `No numbers match "${twilioQuery.trim()}". Try a different prefix or name.`
                : "No numbers found in your Twilio account. Buy one in the Twilio Console, then come back."}
            </p>
          ) : (
            <ul className="space-y-1.5">
              {twilioNumbers.map((n) => {
                const taken = n.attachedBinding;
                return (
                  <li
                    key={n.sid}
                    className="flex items-center justify-between gap-3 rounded-md border border-zinc-200 px-3 py-2 dark:border-zinc-800/80"
                  >
                    <div>
                      <p className="font-mono text-[13px] text-zinc-900 dark:text-zinc-100">
                        {n.phoneNumber}
                      </p>
                      <p className="text-[11.5px] text-zinc-500">
                        {n.friendlyName || "—"}
                        {" · "}
                        {[
                          n.capabilities.sms && "SMS",
                          n.capabilities.mms && "MMS",
                          n.capabilities.voice && "Voice",
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </p>
                      {taken && (
                        <p className="mt-0.5 text-[11px] text-amber-600 dark:text-amber-500">
                          Already connected to{" "}
                          <span className="font-medium">{taken.flowName || "another flow"}</span>
                          {" · "}
                          {taken.channel.toUpperCase()}
                        </p>
                      )}
                    </div>
                    {taken ? (
                      <span className="shrink-0 rounded-md bg-zinc-100 px-2.5 py-1 text-[11.5px] font-medium text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400">
                        Connected
                      </span>
                    ) : channelMode === "voice" && !n.capabilities.voice ? (
                      <span className="shrink-0 rounded-md bg-zinc-100 px-2.5 py-1 text-[11.5px] font-medium text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400">
                        No voice
                      </span>
                    ) : (
                      <Button
                        onClick={() =>
                          void (channelMode === "voice"
                            ? connectVoiceNumber(n.sid)
                            : connectTwilio(n.sid))
                        }
                        disabled={connectingSid !== null}
                      >
                        {connectingSid === n.sid ? "Connecting…" : "Connect"}
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}

          {!twilioQuery.trim() && twilioNumbers && twilioNumbers.length >= 10 && (
            <p className="mt-3 text-[11px] text-zinc-500">
              Showing 10 of your most recent numbers. Type above to search the rest.
            </p>
          )}
          <p className="mt-3 rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2 text-[11px] leading-snug text-zinc-500 dark:border-zinc-800 dark:bg-zinc-900/40">
            <strong className="text-zinc-700 dark:text-zinc-300">Heads up:</strong>{" "}
            {channelMode === "voice" ? (
              <>
                Once connected, Twilio routes inbound <strong>calls</strong> to this flow via the
                number&rsquo;s Voice webhook. Remove here to restore the number&rsquo;s previous
                Voice config.
              </>
            ) : (
              <>
                Once attached, Twilio routes inbound messages through our Messaging Service — the
                number&rsquo;s own Messaging webhook config in the Twilio Console becomes
                informational. Detach here to restore the original per-number config.
              </>
            )}
          </p>
        </ChannelDialog>
      )}

      {/* Wizard step 3 (channel-sender path) — pick a WhatsApp / RCS sender */}
      {step === "pick-sender" && selectedConnection && channelMode !== "number" && (
        <ChannelDialog
          title={`${channelMode === "whatsapp" ? "WhatsApp" : "RCS"} senders in ${
            selectedConnection.friendlyName || "this Twilio account"
          }`}
          onClose={() => setStep(null)}
          primary={null}
        >
          {twilioAccounts && twilioAccounts.length > 1 && (
            <div className="mb-3 flex items-center justify-between rounded-md bg-zinc-50 px-3 py-2 dark:bg-zinc-900/40">
              <span className="text-[11.5px] text-zinc-500">
                Account:{" "}
                <span className="text-zinc-700 dark:text-zinc-200">
                  {selectedConnection.friendlyName || "Twilio account"}
                </span>
              </span>
              <button
                type="button"
                onClick={() => setStep("pick-account")}
                className="text-[11.5px] text-brand-600 hover:underline"
              >
                Change
              </button>
            </div>
          )}

          {channelSendersErr && (
            <p className="mb-3 text-[12px] text-red-600">{channelSendersErr}</p>
          )}
          {channelSenders === null ? (
            <div className="h-20 animate-pulse rounded bg-zinc-100 dark:bg-zinc-800/40" />
          ) : channelSenders.length === 0 ? (
            <p className="rounded-md border border-dashed border-zinc-200 px-3 py-3 text-[12.5px] text-zinc-500 dark:border-zinc-800">
              No {channelMode === "whatsapp" ? "WhatsApp" : "RCS"} senders found on this Twilio
              account. Onboard one in the Twilio Console first, then come back.
            </p>
          ) : (
            <ul className="space-y-1.5">
              {channelSenders.map((s) => {
                const taken = s.attachedBinding;
                const selectable = s.online && !taken;
                return (
                  <li
                    key={s.sid}
                    className="flex items-center justify-between gap-3 rounded-md border border-zinc-200 px-3 py-2 dark:border-zinc-800/80"
                  >
                    <div>
                      <p className="font-mono text-[13px] text-zinc-900 dark:text-zinc-100">
                        {s.senderId}
                      </p>
                      <p className="text-[11.5px] text-zinc-500">
                        {s.displayName || "—"}
                        {" · "}
                        <span className={s.online ? "text-emerald-600" : "text-amber-600"}>
                          {s.status}
                        </span>
                      </p>
                      {taken && (
                        <p className="mt-0.5 text-[11px] text-amber-600 dark:text-amber-500">
                          Already connected to{" "}
                          <span className="font-medium">{taken.flowName || "another flow"}</span>
                        </p>
                      )}
                    </div>
                    {taken ? (
                      <span className="shrink-0 rounded-md bg-zinc-100 px-2.5 py-1 text-[11.5px] font-medium text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400">
                        Connected
                      </span>
                    ) : (
                      <Button
                        onClick={() => void connectSender(s.senderId)}
                        disabled={!selectable || connectingSid !== null}
                      >
                        {connectingSid === s.senderId
                          ? "Connecting…"
                          : s.online
                            ? "Connect"
                            : "Not ready"}
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          <p className="mt-3 rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2 text-[11px] leading-snug text-zinc-500 dark:border-zinc-800 dark:bg-zinc-900/40">
            <strong className="text-zinc-700 dark:text-zinc-300">Note:</strong> Senders must be
            onboarded in Twilio first. On connect we add the sender to your Messaging Service sender
            pool automatically so messages route.
          </p>
        </ChannelDialog>
      )}

      {/* Wizard step 3 (webhook path) — confirm + create */}
      {step === "webhook-confirm" && (
        <ChannelDialog
          title="Create webhook channel"
          onClose={() => setStep(null)}
          primary={
            <Button onClick={() => void connectWebhook()} disabled={busy}>
              {busy ? "Creating…" : "Create webhook"}
            </Button>
          }
        >
          <p className="text-[12.5px] text-zinc-600 dark:text-zinc-300">
            We&rsquo;ll generate a signed URL + signing secret. The secret appears once on the next
            screen — copy it before closing.
          </p>
        </ChannelDialog>
      )}

      {/* One-shot secret reveal after a webhook binding creates */}
      {createdSecret && (
        <ChannelDialog
          title="Webhook channel connected"
          onClose={() => setCreatedSecret(null)}
          primary={<Button onClick={() => setCreatedSecret(null)}>I&rsquo;ve saved it</Button>}
        >
          <p className="mb-3 text-[12.5px] text-zinc-700 dark:text-zinc-200">
            Copy your signing secret now — it will not be shown again. Verify the{" "}
            <code className="rounded bg-zinc-100 px-1 dark:bg-zinc-800">
              X-TwilioPulse-Signature
            </code>{" "}
            header on incoming POSTs using HMAC-SHA256 with this secret.
          </p>
          <label className="mb-3 flex flex-col gap-1">
            <span className="text-[11px] font-medium uppercase tracking-wider text-zinc-500">
              Endpoint
            </span>
            <input
              readOnly
              value={createdSecret.webhookUrl ?? ""}
              className="h-9 rounded-md border border-zinc-200 bg-zinc-50 px-3 font-mono text-[11.5px] dark:border-zinc-800 dark:bg-zinc-950/40"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[11px] font-medium uppercase tracking-wider text-zinc-500">
              Signing secret (shown once)
            </span>
            <input
              readOnly
              value={createdSecret.signingSecretOnce ?? ""}
              className="h-9 rounded-md border border-amber-300 bg-amber-50 px-3 font-mono text-[11.5px] text-amber-900 dark:border-amber-700/60 dark:bg-amber-900/20 dark:text-amber-200"
            />
          </label>
        </ChannelDialog>
      )}
    </EditorPage>
  );
}

// ===========================================================================
// CanvasDock — floating glass-style dock for add-node controls.
//
// Positioned absolutely over the top-left of the canvas so the canvas
// itself owns the full vertical viewport. Color-coded buttons mirror
// the canvas node tones (indigo intent / emerald action / rose guardrail)
// so the dock and the nodes read as one visual system.
// ===========================================================================

interface SandboxMessage {
  role: "user" | "assistant";
  content: string;
  toolCalls?: SandboxToolCallSummary[];
  /** v2 (stage) flows: the stage that produced this assistant reply. */
  stage?: string;
  /** v2: the stage the next turn will run in (after this turn's transition). */
  nextStage?: string;
  /** v2: why the turn transitioned/stayed — for debugging. */
  transitionReason?: string;
  /** v2: the matched condition (e.g. "square_feet > 2000") when slot_condition. */
  transitionDetail?: string;
}

/** Human-friendly label for a transition reason (debug display). */
function transitionReasonLabel(reason: string | undefined | null): string {
  switch (reason) {
    case "action_success":
      return "action succeeded";
    case "slot_condition":
      return "condition matched";
    case "slots_complete":
      return "info complete";
    case "no_progress":
      return "no progress → escape";
    case "stay":
      return "stayed";
    default:
      return reason ?? "";
  }
}

/** Chip text for a transition: the actual condition for slot_condition, else the reason. */
function transitionChipText(reason?: string | null, detail?: string | null): string {
  if (reason === "slot_condition" && detail) return `if ${detail}`;
  return transitionReasonLabel(reason);
}
const SANDBOX_KEY_PREFIX = "tw-pulse.agentflow.sandbox.";
const SANDBOX_CTX_KEY_PREFIX = "tw-pulse.agentflow.sandbox.ctx.";

/** Author-editable test context for a sandbox session (channel + slot prefills). */
interface SandboxTestContext {
  channel?: SandboxSimulatedChannel;
  /** slot id → prefill value. */
  slots: Record<string, string>;
}

const SANDBOX_CHANNELS: { value: SandboxSimulatedChannel; label: string }[] = [
  { value: "sms", label: "SMS" },
  { value: "whatsapp", label: "WhatsApp" },
  { value: "rcs", label: "RCS" },
];

function loadSandboxContext(flowId: string): SandboxTestContext {
  try {
    const raw = localStorage.getItem(SANDBOX_CTX_KEY_PREFIX + flowId);
    if (!raw) return { slots: {} };
    const parsed = JSON.parse(raw) as Partial<SandboxTestContext>;
    return {
      channel: parsed.channel,
      slots: parsed.slots && typeof parsed.slots === "object" ? parsed.slots : {},
    };
  } catch {
    return { slots: {} };
  }
}

interface SandboxErrorInfo {
  message: string;
  code?: string;
  /** Free-form server detail blob. Only rendered when present. */
  details?: unknown;
}

/**
 * Render the sandbox error with a structured detail panel when the
 * server returned compiler-shaped detail (action id + Zod field errors).
 * Falls back to a plain message for everything else.
 */
function SandboxErrorBanner({ err }: { err: SandboxErrorInfo }) {
  const isCompilerError =
    err.code === "invalid_action_config" ||
    err.code === "missing_tool" ||
    err.code === "unsupported_schema_version" ||
    err.code === "unresolved_reference";
  const details = err.details as
    | {
        formErrors?: string[];
        fieldErrors?: Record<string, string[]>;
      }
    | undefined;
  return (
    <div
      role="alert"
      className="mx-4 mb-2 space-y-1 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300"
    >
      <p className="font-medium">{err.message}</p>
      {isCompilerError && details && (details.formErrors?.length || details.fieldErrors) && (
        <div className="space-y-0.5 pt-1 text-[11.5px]">
          {details.formErrors?.map((e, i) => (
            <p key={`f-${i}`}>· {e}</p>
          ))}
          {details.fieldErrors &&
            Object.entries(details.fieldErrors).map(([field, msgs]) => (
              <p key={`fe-${field}`}>
                · <code className="font-mono">{field}</code>: {msgs.join(", ")}
              </p>
            ))}
          <p className="pt-1 italic">
            Open the action in the canvas and hit “Reset config” to drop stale keys.
          </p>
        </div>
      )}
    </div>
  );
}

/** Summary string for the context (channel + filled count), or null if empty. */
function sandboxContextSummary(ctx: SandboxTestContext): string | null {
  const bits: string[] = [];
  if (ctx.channel)
    bits.push(SANDBOX_CHANNELS.find((c) => c.value === ctx.channel)?.label ?? ctx.channel);
  const filled = Object.values(ctx.slots).filter((v) => v.trim() !== "").length;
  if (filled > 0) bits.push(`${filled}`);
  return bits.length > 0 ? bits.join(" · ") : null;
}

/** Sliders glyph for the sandbox "Context" button. */
function SlidersIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      className="h-3.5 w-3.5"
      aria-hidden="true"
    >
      <path d="M4 6h10M18 6h2M4 12h2M10 12h10M4 18h10M18 18h2" />
      <circle cx="16" cy="6" r="2" />
      <circle cx="8" cy="12" r="2" />
      <circle cx="16" cy="18" r="2" />
    </svg>
  );
}

/**
 * Test-context modal for the sandbox (v2 flows). A roomy, focused editor for
 * simulating a channel and pre-filling slots before a test conversation —
 * scales to many slots via a scrollable, searchable list (search appears past 8
 * slots, per Hick's Law). Drives channel-specific behavior + the "already known
 * → confirm, don't ask" path. Locked while a conversation is active (Reset the
 * sandbox to change). Matches the ConfirmDialog modal pattern: backdrop,
 * Esc-to-close, focus into the panel on open + restore to the trigger on close.
 */
function SandboxContextModal({
  open,
  slots,
  ctx,
  onChange,
  onClose,
  locked,
}: {
  open: boolean;
  slots: Slot[];
  ctx: SandboxTestContext;
  onChange: (next: SandboxTestContext) => void;
  onClose: () => void;
  locked: boolean;
}) {
  const [query, setQuery] = useState("");
  const triggerRef = useRef<Element | null>(null);
  const firstFieldRef = useRef<HTMLSelectElement>(null);
  const ctxFieldCls = `${inputCls} disabled:cursor-not-allowed disabled:opacity-60`;

  // Hold the latest onClose in a ref so the open/focus effect can depend ONLY on
  // `open`. Depending on `onClose` (a fresh closure each parent render) would
  // re-run this effect on every keystroke and steal focus back to the channel
  // select.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    triggerRef.current = document.activeElement;
    firstFieldRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onCloseRef.current();
    }
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      if (triggerRef.current instanceof HTMLElement) triggerRef.current.focus();
    };
  }, [open]);

  if (!open) return null;

  const q = query.trim().toLowerCase();
  const visibleSlots = q
    ? slots.filter((s) => s.id.toLowerCase().includes(q) || s.prompt.toLowerCase().includes(q))
    : slots;
  const filledCount = Object.values(ctx.slots).filter((v) => v.trim() !== "").length;
  const showSearch = slots.length > 8;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="sbx-ctx-title"
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
    >
      <div
        className="absolute inset-0 bg-zinc-900/40 backdrop-blur-sm dark:bg-zinc-950/70"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="relative flex max-h-[85vh] w-full max-w-xl flex-col rounded-xl border border-zinc-200 bg-white shadow-card-light dark:border-zinc-800 dark:bg-zinc-900/95 dark:shadow-card dark:ring-1 dark:ring-inset dark:ring-white/[0.03]">
        <div className="flex items-start justify-between gap-3 border-b border-zinc-200 px-5 py-3.5 dark:border-zinc-800">
          <div>
            <h2
              id="sbx-ctx-title"
              className="text-[15px] font-semibold tracking-tight text-zinc-900 dark:text-zinc-50"
            >
              Test context
            </h2>
            <p className="mt-0.5 text-[12px] text-zinc-500">
              Simulate a channel and pre-fill known facts. Applied when you start a new
              conversation.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="-mr-1 -mt-1 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:hover:bg-zinc-800 dark:hover:text-zinc-200"
          >
            ✕
          </button>
        </div>

        <div className="space-y-3 px-5 pt-4">
          {locked && (
            <p className="rounded-md bg-amber-50 px-2.5 py-2 text-[12px] leading-snug text-amber-700 dark:bg-amber-500/10 dark:text-amber-300">
              This conversation started with this context. Reset the sandbox to apply changes.
            </p>
          )}
          <div>
            <label
              htmlFor="sbx-ctx-channel"
              className="mb-1.5 block text-[12px] font-medium text-zinc-700 dark:text-zinc-300"
            >
              Channel
            </label>
            <select
              id="sbx-ctx-channel"
              ref={firstFieldRef}
              disabled={locked}
              value={ctx.channel ?? ""}
              onChange={(e) =>
                onChange({
                  ...ctx,
                  channel: (e.target.value || undefined) as SandboxSimulatedChannel | undefined,
                })
              }
              className={ctxFieldCls}
            >
              <option value="">None (neutral)</option>
              {SANDBOX_CHANNELS.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
          {slots.length > 0 && (
            <div className="flex items-center justify-between gap-2">
              <p className="text-[12px] font-medium text-zinc-700 dark:text-zinc-300">
                Pre-filled slots{" "}
                {filledCount > 0 && (
                  <span className="font-normal text-zinc-400">· {filledCount} set</span>
                )}
              </p>
              {showSearch && (
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Filter slots…"
                  aria-label="Filter slots"
                  className="w-40 rounded-md border border-zinc-200 bg-white px-2 py-1 text-[12px] text-zinc-900 placeholder:text-zinc-400 focus-visible:border-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30 dark:border-zinc-800 dark:bg-zinc-950/40 dark:text-zinc-100"
                />
              )}
            </div>
          )}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-4 pt-3">
          {slots.length === 0 ? (
            <p className="rounded-md border border-dashed border-zinc-200 px-3 py-6 text-center text-[12px] text-zinc-400 dark:border-zinc-800">
              This flow has no slots to pre-fill.
            </p>
          ) : visibleSlots.length === 0 ? (
            <p className="px-1 py-4 text-center text-[12px] text-zinc-400">
              No slots match “{query}”.
            </p>
          ) : (
            <div className="space-y-3">
              {visibleSlots.map((s) => {
                const filled = (ctx.slots[s.id] ?? "").trim() !== "";
                const inputId = `sbx-slot-${s.id}`;
                return (
                  <div key={s.id}>
                    <div className="mb-1 flex items-center justify-between gap-2">
                      <label
                        htmlFor={inputId}
                        className="truncate text-[12px] font-medium text-zinc-700 dark:text-zinc-300"
                        title={s.id}
                      >
                        {s.id}
                      </label>
                      {filled && (
                        <span className="shrink-0 rounded-full bg-brand-50 px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-wide text-brand-700 dark:bg-brand-500/10 dark:text-brand-300">
                          Set
                        </span>
                      )}
                    </div>
                    <input
                      id={inputId}
                      disabled={locked}
                      value={ctx.slots[s.id] ?? ""}
                      placeholder={s.prompt}
                      onChange={(e) =>
                        onChange({ ...ctx, slots: { ...ctx.slots, [s.id]: e.target.value } })
                      }
                      className={ctxFieldCls}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-zinc-200 px-5 py-3 dark:border-zinc-800">
          <button
            type="button"
            disabled={locked || filledCount === 0}
            onClick={() => onChange({ ...ctx, slots: {} })}
            className="text-[12.5px] text-zinc-500 underline-offset-2 hover:text-zinc-900 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:text-zinc-300 disabled:no-underline dark:hover:text-zinc-100 dark:disabled:text-zinc-700"
          >
            Clear all
          </button>
          <Button onClick={onClose}>Done</Button>
        </div>
      </div>
    </div>
  );
}

function SandboxPane({
  flowId,
  onActiveStage,
  slots,
  greeting,
  entryStageId,
}: {
  flowId: string;
  /** v2 editor passes this to highlight the producing stage on the canvas. */
  onActiveStage?: (stageId: string | undefined) => void;
  /** v2 flows: resolved slots (locals + catalog), so the author can pre-fill them as test context. */
  slots?: Slot[];
  /** v2 flows: the flow greeting, shown as the agent's opener (agent-initiated). */
  greeting?: string;
  /** v2 flows: entry stage id — stamped on the opener bubble. */
  entryStageId?: string;
}) {
  const { me } = useAuth();
  // Agent-initiated opener: these flows are outbound (agent texts first, customer
  // replies), so the sandbox opens with the flow's greeting as the agent's first
  // message. Interpolate {businessName} with the tenant's real name for parity
  // with what the runtime sends.
  const openingGreeting = greeting?.trim()
    ? interpolateFlowVariables(greeting, {
        businessName: me?.tenant.businessName?.trim() || "your business",
      })
    : "";
  const openingMessages = (): SandboxMessage[] =>
    openingGreeting ? [{ role: "assistant", content: openingGreeting, stage: entryStageId }] : [];

  const [conversationId, setConversationId] = useState<string | null>(() =>
    localStorage.getItem(SANDBOX_KEY_PREFIX + flowId),
  );
  // Transient "copied" feedback when the author copies the conversation id
  // (handy for `dump-agent-conversation.ts` when debugging a stuck flow).
  const [copiedConvId, setCopiedConvId] = useState(false);
  // Test context (v2 only): channel to simulate + slot prefills. Persisted per
  // flow; applied only when a NEW conversation starts.
  const [ctx, setCtx] = useState<SandboxTestContext>(() => loadSandboxContext(flowId));
  const [ctxModalOpen, setCtxModalOpen] = useState(false);
  useEffect(() => {
    localStorage.setItem(SANDBOX_CTX_KEY_PREFIX + flowId, JSON.stringify(ctx));
  }, [ctx, flowId]);
  const [messages, setMessages] = useState<SandboxMessage[]>(() =>
    localStorage.getItem(SANDBOX_KEY_PREFIX + flowId) ? [] : openingMessages(),
  );
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  // True while we're loading a previously-stored conversation on mount.
  const [hydrating, setHydrating] = useState<boolean>(() =>
    Boolean(localStorage.getItem(SANDBOX_KEY_PREFIX + flowId)),
  );
  const [err, setErr] = useState<SandboxErrorInfo | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  // Auto-focus the composer once it's ready (after a reply / after hydration),
  // so you can keep typing without clicking back into the field each turn.
  useEffect(() => {
    if (!busy && !hydrating) inputRef.current?.focus();
  }, [busy, hydrating]);

  useEffect(() => {
    if (conversationId) localStorage.setItem(SANDBOX_KEY_PREFIX + flowId, conversationId);
    else localStorage.removeItem(SANDBOX_KEY_PREFIX + flowId);
  }, [conversationId, flowId]);

  // On mount, resume the stored sandbox session: load its transcript and
  // continue it IF it's still active. If it already completed (e.g. a lead
  // was captured) or no longer exists, drop it and start fresh so the author
  // isn't silently appending to a finished conversation.
  useEffect(() => {
    const stored = localStorage.getItem(SANDBOX_KEY_PREFIX + flowId);
    if (!stored) {
      setHydrating(false);
      return;
    }
    let cancelled = false;
    setHydrating(true);
    api.agentflow
      .getConversation(stored)
      .then((convo) => {
        if (cancelled) return;
        if (convo.completed || convo.status !== "active") {
          setConversationId(null);
          setMessages(openingMessages());
          return;
        }
        // Tool calls come from the audit trace, already folded (with args) onto
        // each assistant message by the replay DTO — read them directly rather
        // than re-folding tool rows (v2 doesn't persist tool message rows, so
        // the old fold restored nothing). Matches the ConversationReplayPage.
        const restored: SandboxMessage[] = [];
        for (const r of convo.messages) {
          if (r.role === "tool") continue; // not persisted today; ignore if ever present
          if (r.role === "assistant") {
            restored.push({
              role: "assistant",
              content: r.content,
              toolCalls: r.toolCalls && r.toolCalls.length > 0 ? r.toolCalls : undefined,
              stage: r.stageId ?? undefined,
            });
          } else {
            restored.push({ role: "user", content: r.content });
          }
        }
        setMessages(restored);
      })
      .catch(() => {
        // Not found / no access — the stored id is stale. Start fresh.
        if (!cancelled) {
          setConversationId(null);
          setMessages(openingMessages());
        }
      })
      .finally(() => {
        if (!cancelled) setHydrating(false);
      });
    return () => {
      cancelled = true;
    };
    // Mount/flow-scoped hydration only — must NOT re-run when the greeting opener
    // (derived from `me`) changes, or it would reload the transcript mid-session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowId]);

  useEffect(() => {
    // Auto-scroll on new message.
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages.length, busy]);

  async function send() {
    const text = input.trim();
    if (!text || busy || hydrating) return;
    setBusy(true);
    setErr(null);
    setMessages((m) => [...m, { role: "user", content: text }]);
    setInput("");
    try {
      // Context applies only when starting a NEW conversation. Drop blank slot
      // values and omit the context entirely if there's nothing to send.
      let context: SandboxTestContext | undefined;
      if (!conversationId && slots) {
        const filledSlots = Object.fromEntries(
          Object.entries(ctx.slots).filter(([, v]) => v.trim() !== ""),
        );
        if (ctx.channel || Object.keys(filledSlots).length > 0) {
          context = { channel: ctx.channel, slots: filledSlots };
        }
      }
      const res = await api.agentflow.sandboxTurn(flowId, {
        conversationId: conversationId ?? undefined,
        inboundMessage: text,
        context,
        // On the first turn, persist the greeting as the agent's opener so the
        // transcript + LLM history match (mirrors outbound). No-op if empty.
        seedGreeting: !conversationId,
      });
      setConversationId(res.conversationId);
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content: res.assistantMessage,
          toolCalls: res.toolCalls,
          stage: res.stage,
          nextStage: res.nextStage,
          transitionReason: res.transitionReason,
          transitionDetail: res.transitionDetail,
        },
      ]);
      if (res.stage) onActiveStage?.(res.stage);
    } catch (e) {
      if (e instanceof ApiClientError) {
        setErr({ message: e.message, code: e.code, details: e.details });
      } else {
        setErr({ message: "Turn failed" });
      }
    } finally {
      setBusy(false);
    }
  }

  function reset() {
    setConversationId(null);
    setMessages(openingMessages());
    setErr(null);
    onActiveStage?.(undefined);
  }

  return (
    <>
      <header className="flex items-center justify-between gap-2 border-b border-zinc-200 px-4 py-2.5 dark:border-zinc-800">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-brand-600 dark:text-brand-400">
            Sandbox
          </p>
          {conversationId ? (
            <button
              type="button"
              onClick={() => {
                void navigator.clipboard?.writeText(conversationId);
                setCopiedConvId(true);
                setTimeout(() => setCopiedConvId(false), 1200);
              }}
              title="Click to copy the conversation id (for dump-agent-conversation.ts)"
              className="mt-0.5 flex max-w-full items-center gap-1 font-mono text-[11px] text-zinc-500 hover:text-zinc-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 dark:hover:text-zinc-100"
            >
              <span className="truncate">id: {conversationId}</span>
              <span className="shrink-0 not-italic">{copiedConvId ? "✓ copied" : "⧉"}</span>
            </button>
          ) : (
            <p className="mt-0.5 text-[11.5px] text-zinc-500">Runs against the current draft</p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {slots && (
            <button
              type="button"
              onClick={() => setCtxModalOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-md border border-zinc-200 px-2 py-1 text-[11.5px] font-medium text-zinc-600 hover:border-zinc-300 hover:bg-zinc-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 dark:border-zinc-800 dark:text-zinc-300 dark:hover:border-zinc-700 dark:hover:bg-zinc-800/40"
            >
              <SlidersIcon />
              Context
              {sandboxContextSummary(ctx) && (
                <span className="rounded-full bg-brand-50 px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-wide text-brand-700 dark:bg-brand-500/10 dark:text-brand-300">
                  {sandboxContextSummary(ctx)}
                </span>
              )}
            </button>
          )}
          <button
            type="button"
            onClick={reset}
            className="text-[11.5px] text-zinc-500 underline-offset-2 hover:text-zinc-900 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 dark:hover:text-zinc-100"
          >
            Reset
          </button>
        </div>
      </header>

      {slots && (
        <SandboxContextModal
          open={ctxModalOpen}
          slots={slots}
          ctx={ctx}
          onChange={setCtx}
          onClose={() => setCtxModalOpen(false)}
          locked={conversationId !== null}
        />
      )}

      <div
        ref={scrollRef}
        className="flex-1 space-y-3 overflow-y-auto px-4 py-3"
        aria-live="polite"
      >
        {hydrating && (
          <div className="flex items-center gap-2 text-[12px] text-zinc-500">
            <span className="inline-flex gap-1" aria-hidden="true">
              <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-zinc-400 [animation-delay:-0.3s]" />
              <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-zinc-400 [animation-delay:-0.15s]" />
              <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-zinc-400" />
            </span>
            Restoring conversation…
          </div>
        )}
        {messages.length === 0 && !busy && !hydrating && <EmptySandbox />}
        {messages.map((m, i) => (
          <Bubble key={i} message={m} />
        ))}
        {busy && (
          <div className="flex items-center gap-2 text-[12px] text-zinc-500">
            <span className="inline-flex gap-1" aria-hidden="true">
              <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-zinc-400 [animation-delay:-0.3s]" />
              <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-zinc-400 [animation-delay:-0.15s]" />
              <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-zinc-400" />
            </span>
            Thinking…
          </div>
        )}
      </div>

      {err && <SandboxErrorBanner err={err} />}

      <div className="border-t border-zinc-200 bg-white p-3 dark:border-zinc-800 dark:bg-zinc-900/40">
        <div className="flex items-end gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                void send();
              }
            }}
            ref={inputRef}
            placeholder={hydrating ? "Restoring conversation…" : "Type a message…"}
            rows={2}
            disabled={busy || hydrating}
            aria-label="Sandbox message input"
            className="block min-h-[44px] flex-1 resize-none rounded-md border border-zinc-200 bg-white px-3 py-2 text-[13px] text-zinc-900 placeholder:text-zinc-400 focus-visible:border-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30 dark:border-zinc-800 dark:bg-zinc-950/40 dark:text-zinc-100 dark:placeholder:text-zinc-600"
          />
          <Button
            onClick={() => void send()}
            disabled={busy || hydrating || !input.trim()}
            size="sm"
          >
            {busy ? "…" : "Send"}
          </Button>
        </div>
        <p className="mt-1 text-[10.5px] text-zinc-500">⏎ to send · Shift + ⏎ for newline</p>
      </div>
    </>
  );
}

// ===========================================================================
// MarkdownLite — a tiny, dependency-free renderer for the assistant bubble.
//
// Handles the common chat-model output cases:
//   - **bold**  __bold__
//   - *italic*  _italic_
//   - `inline code`
//   - lines starting with `- ` or `* ` → bullet list
//   - lines starting with `\d+. ` → numbered list
//   - blank lines → paragraph break
//
// Not a full markdown spec. Not HTML — every leaf is a React node, so XSS
// from a model echoing user input is not possible. If we ever need real
// markdown (links, images, headings), pull in `react-markdown` then.
// ===========================================================================

const MARKDOWN_HINT_RE = /(\*\*[^*\n]+\*\*|__[^_\n]+__|`[^`\n]+`|^\s*([-*]|\d+\.)\s+)/m;

function hasMarkdown(text: string): boolean {
  return MARKDOWN_HINT_RE.test(text);
}

function MarkdownLite({ source }: { source: string }) {
  const lines = source.split("\n");
  const out: React.ReactNode[] = [];
  let listBuf: { type: "ul" | "ol"; items: React.ReactNode[] } | null = null;

  function flushList() {
    if (!listBuf) return;
    if (listBuf.type === "ul") {
      out.push(
        <ul key={`l-${out.length}`} className="my-1 list-disc space-y-0.5 pl-5">
          {listBuf.items}
        </ul>,
      );
    } else {
      out.push(
        <ol key={`l-${out.length}`} className="my-1 list-decimal space-y-0.5 pl-5">
          {listBuf.items}
        </ol>,
      );
    }
    listBuf = null;
  }

  lines.forEach((rawLine, i) => {
    const line = rawLine;
    const ulMatch = /^\s*[-*]\s+(.*)$/.exec(line);
    const olMatch = /^\s*\d+\.\s+(.*)$/.exec(line);

    if (ulMatch) {
      if (!listBuf || listBuf.type !== "ul") {
        flushList();
        listBuf = { type: "ul", items: [] };
      }
      listBuf.items.push(<li key={`li-${i}`}>{renderInline(ulMatch[1] ?? "", `${i}`)}</li>);
    } else if (olMatch) {
      if (!listBuf || listBuf.type !== "ol") {
        flushList();
        listBuf = { type: "ol", items: [] };
      }
      listBuf.items.push(<li key={`li-${i}`}>{renderInline(olMatch[1] ?? "", `${i}`)}</li>);
    } else {
      flushList();
      if (line.trim() === "") {
        // Preserve blank lines as a small visual gap.
        out.push(<div key={`gap-${i}`} className="h-1.5" aria-hidden="true" />);
      } else {
        out.push(
          <p key={`p-${i}`} className="my-0 whitespace-pre-wrap">
            {renderInline(line, `${i}`)}
          </p>,
        );
      }
    }
  });
  flushList();

  return <div className="space-y-0">{out}</div>;
}

/** Tokenize a single line for bold / italic / inline code. */
const INLINE_RE = /(\*\*[^*\n]+?\*\*|__[^_\n]+?__|\*[^*\n]+?\*|_[^_\n]+?_|`[^`\n]+?`)/g;

function renderInline(line: string, keyPrefix: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  let lastIdx = 0;
  let key = 0;
  let match: RegExpExecArray | null;

  // Reset regex state per call (it's a /g instance).
  INLINE_RE.lastIndex = 0;
  while ((match = INLINE_RE.exec(line)) !== null) {
    const token = match[0];
    if (match.index > lastIdx) {
      parts.push(line.slice(lastIdx, match.index));
    }
    if (token.startsWith("**") && token.endsWith("**")) {
      parts.push(
        <strong key={`${keyPrefix}-${key++}`} className="font-semibold">
          {token.slice(2, -2)}
        </strong>,
      );
    } else if (token.startsWith("__") && token.endsWith("__")) {
      parts.push(
        <strong key={`${keyPrefix}-${key++}`} className="font-semibold">
          {token.slice(2, -2)}
        </strong>,
      );
    } else if (token.startsWith("`") && token.endsWith("`")) {
      parts.push(
        <code
          key={`${keyPrefix}-${key++}`}
          className="rounded bg-zinc-200/70 px-1 font-mono text-[11.5px] dark:bg-zinc-800"
        >
          {token.slice(1, -1)}
        </code>,
      );
    } else if (token.startsWith("*") && token.endsWith("*")) {
      parts.push(<em key={`${keyPrefix}-${key++}`}>{token.slice(1, -1)}</em>);
    } else if (token.startsWith("_") && token.endsWith("_")) {
      parts.push(<em key={`${keyPrefix}-${key++}`}>{token.slice(1, -1)}</em>);
    }
    lastIdx = match.index + token.length;
  }
  if (lastIdx < line.length) parts.push(line.slice(lastIdx));
  return parts;
}

function Bubble({ message }: { message: SandboxMessage }) {
  const isUser = message.role === "user";
  // User messages are plain text by definition. Assistant messages may
  // contain markdown — the compiler instructs against it, but chat-
  // tuned models slip up. Render a safe subset (bold/italic/code/lists)
  // so the sandbox stays readable while the agent learns.
  const containsMarkdown = !isUser && hasMarkdown(message.content);
  return (
    <div className={"flex " + (isUser ? "justify-end" : "justify-start")}>
      <div className={"max-w-[88%] " + (isUser ? "text-right" : "text-left")}>
        <div
          className={
            "inline-block rounded-2xl px-3 py-2 text-left text-[13px] leading-relaxed " +
            (isUser
              ? "bg-brand-500 text-white"
              : "border border-zinc-200 bg-zinc-50 text-zinc-900 dark:border-zinc-800 dark:bg-zinc-900/60 dark:text-zinc-100")
          }
        >
          {isUser ? (
            <span className="whitespace-pre-wrap">{message.content}</span>
          ) : (
            <MarkdownLite source={message.content} />
          )}
        </div>
        {!isUser && message.stage && (
          <p className="mt-1 flex flex-wrap items-center gap-1 text-[10.5px] text-indigo-600 dark:text-indigo-400">
            <Icon name="agent" className="h-3 w-3" />
            <span className="font-mono">{message.stage}</span>
            {message.nextStage && message.nextStage !== message.stage && (
              <>
                <span aria-hidden>→</span>
                <span className="font-mono">{message.nextStage}</span>
              </>
            )}
            {message.transitionReason && (
              <span className="rounded bg-indigo-50 px-1 py-0.5 text-[9.5px] font-medium text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-300">
                {transitionChipText(message.transitionReason, message.transitionDetail)}
              </span>
            )}
          </p>
        )}
        {containsMarkdown && (
          <p
            className="mt-1 text-[10.5px] text-amber-700 dark:text-amber-300"
            title="SMS doesn't render markdown — customers would see the literal asterisks. The compiler instructs the LLM to avoid this, but it slipped through."
          >
            ⚠ contains markdown — SMS will show literal characters
          </p>
        )}
        {message.toolCalls && message.toolCalls.length > 0 && (
          <div className="mt-1 space-y-1 text-left">
            {message.toolCalls.map((tc) => (
              <details
                key={tc.id}
                className="rounded border border-zinc-200 bg-white text-[11px] dark:border-zinc-800 dark:bg-zinc-900/40"
              >
                <summary className="cursor-pointer px-2 py-1 font-mono text-zinc-600 dark:text-zinc-400">
                  {tc.name}({Object.keys(tc.args).join(", ")})
                </summary>
                <div className="space-y-1 border-t border-zinc-100 px-2 py-1.5 dark:border-zinc-800">
                  <p className="font-mono text-[10.5px] text-zinc-500">
                    args: {JSON.stringify(tc.args)}
                  </p>
                  <p className="font-mono text-[10.5px] text-zinc-700 dark:text-zinc-300">
                    → {tc.result}
                  </p>
                </div>
              </details>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function EmptySandbox() {
  return (
    <div className="flex h-full flex-col items-center justify-center text-center">
      <div className="rounded-full border border-zinc-200 bg-zinc-50 p-3 dark:border-zinc-800 dark:bg-zinc-900/60">
        <SandboxIcon />
      </div>
      <p className="mt-3 text-[13px] font-medium text-zinc-700 dark:text-zinc-300">
        Test your flow
      </p>
      <p className="mt-1 max-w-[260px] text-[12px] text-zinc-500">
        Type a message to start a sandbox conversation. Edits to the draft take effect on the next
        turn.
      </p>
    </div>
  );
}

// ===========================================================================
// Publish modal
// ===========================================================================

function defaultsForTool(tool: ToolDefinitionDTO | undefined): Record<string, unknown> {
  if (!tool) return {};
  switch (tool.id) {
    case "book_appointment":
      return { serviceTypes: ["service"], defaultDurationMin: 60 };
    case "qualify_lead":
      return { questions: [{ id: "q1", prompt: "What's going on?" }] };
    case "faq_lookup":
      // ADR 0021 — entries live in the tenant's central Knowledge Base.
      // An empty `tags` array means "search the whole KB."
      return { tags: [] };
    case "escalate_to_human":
      return { message: "Let me get someone to help." };
    default:
      return {};
  }
}

function PlusIcon() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" aria-hidden="true">
      <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}
function ChevronDownIcon() {
  return (
    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" aria-hidden="true">
      <path
        d="m6 9 6 6 6-6"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
function SandboxIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" aria-hidden="true">
      <path
        d="M21 12a8 8 0 0 1-11.6 7.1L4 21l1.9-4.7A8 8 0 1 1 21 12Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
    </svg>
  );
}
function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" aria-hidden="true">
      <path
        d="M6 6l12 12M18 6L6 18"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}
function BraceIcon() {
  return (
    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" aria-hidden="true">
      <path
        d="M9 4c-2 0-3 1-3 3v3c0 1-1 2-2 2 1 0 2 1 2 2v3c0 2 1 3 3 3M15 4c2 0 3 1 3 3v3c0 1 1 2 2 2-1 0-2 1-2 2v3c0 2-1 3-3 3"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ===========================================================================
// StagesFlowEditor — v2 (stage-based) VISUAL canvas (ADR 0026).
//
// Node/edge canvas mirroring the v1 editor's ReactFlow paradigm: stages are
// nodes (terminal = goal, distinct; entry badged), transitions are labeled
// directed edges (no_progress dashed = the escape hatch). Drag between stages
// to wire a transition; click a node/edge to edit it in a side panel. Slots /
// persona / greeting live on sibling tabs. Reuses SandboxPane on the right.
// Spec: docs/agentflow-v2-canvas-spec.md
// ===========================================================================

const inputCls =
  "block w-full rounded-md border border-zinc-200 bg-white px-2.5 py-1.5 text-[13px] text-zinc-900 focus-visible:border-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30 dark:border-zinc-800 dark:bg-zinc-950/40 dark:text-zinc-100";

/** Comfortable, readable textarea — bigger line-height than the dense input. */
const textareaCls =
  "block w-full resize-none rounded-md border border-zinc-200 bg-white px-3 py-2 text-[13px] leading-[1.55] text-zinc-900 focus-visible:border-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30 dark:border-zinc-800 dark:bg-zinc-950/40 dark:text-zinc-100";

/**
 * Label + auto-growing textarea + an Expand button that opens a large focused
 * editor modal. Used for long prose fields (objective, boundaries) where the
 * narrow side panel is too cramped to read/edit comfortably.
 */
function ExpandableTextarea({
  label,
  hint,
  value,
  onChange,
  placeholder,
  minRows = 3,
  maxAutoGrowPx = 320,
}: {
  label: React.ReactNode;
  hint?: React.ReactNode;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  minRows?: number;
  maxAutoGrowPx?: number;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLTextAreaElement>(null);

  // Auto-grow to fit content, capped so the panel stays scrollable.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, maxAutoGrowPx) + "px";
  }, [value, maxAutoGrowPx]);

  // Esc closes the modal.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const labelText = typeof label === "string" ? label : undefined;

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between gap-2">
        <label className="text-[11px] font-medium text-zinc-500">{label}</label>
        <button
          type="button"
          onClick={() => setOpen(true)}
          title="Open large editor"
          className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10.5px] font-medium text-zinc-500 hover:bg-zinc-100 hover:text-zinc-800 dark:hover:bg-zinc-800/60 dark:hover:text-zinc-200"
        >
          <Icon name="expand" className="h-3 w-3" />
          Expand
        </button>
      </div>
      <textarea
        ref={ref}
        className={textareaCls}
        rows={minRows}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
      {hint && <p className="text-[10.5px] leading-snug text-zinc-400">{hint}</p>}

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setOpen(false)}
        >
          <div
            className="flex max-h-[85vh] w-full max-w-3xl flex-col rounded-xl border border-zinc-200 bg-white p-4 shadow-xl dark:border-zinc-800 dark:bg-zinc-900"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-[14px] font-semibold text-zinc-900 dark:text-zinc-100">
                {labelText ?? "Edit"}
              </h3>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close editor"
                className="text-[13px] text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200"
              >
                ✕
              </button>
            </div>
            {hint && <p className="mb-2 text-[12px] leading-snug text-zinc-500">{hint}</p>}
            <textarea
              autoFocus
              className="min-h-[55vh] flex-1 resize-none rounded-lg border border-zinc-200 bg-white p-4 text-[14px] leading-[1.7] text-zinc-900 focus-visible:border-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30 dark:border-zinc-800 dark:bg-zinc-950/40 dark:text-zinc-100"
              value={value}
              placeholder={placeholder}
              onChange={(e) => onChange(e.target.value)}
            />
            <div className="mt-3 flex items-center justify-between">
              <span className="text-[11px] text-zinc-400">
                {value.length} characters · Esc to close
              </span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-md bg-brand-600 px-3 py-1.5 text-[12.5px] font-medium text-white hover:bg-brand-700"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

type V2Tab = "canvas" | "tools" | "slots" | "persona" | "greeting" | "guardrails" | "channels";
type V2Selection =
  | { kind: "stage"; id: string }
  | { kind: "transition"; stageId: string; index: number }
  | null;

function triggerLabel(t: StageTransition): string {
  switch (t.on) {
    case "action_success":
      return `on ${t.actionId ?? "action"}`;
    case "slots_complete":
      return "slots ✓";
    case "no_progress":
      return "no progress";
    case "slot_condition": {
      const c = t.condition;
      if (!c) return "if …";
      if (c.op === "exists") return `if ${c.slotId} set`;
      const v = Array.isArray(c.value) ? c.value.join(" / ") : (c.value ?? "?");
      return `if ${c.slotId} ${c.op === "in" ? "∈" : "="} ${v}`;
    }
  }
}

const agentNodeStyle: React.CSSProperties = {
  background: "rgba(99, 102, 241, 0.10)",
  border: "1px solid rgba(99, 102, 241, 0.55)",
  borderRadius: 8,
  padding: 8,
  width: 172,
  color: "rgb(49, 46, 129)",
};
const entryNodeStyle: React.CSSProperties = {
  ...agentNodeStyle,
  border: "2px solid rgb(99, 102, 241)",
};
const terminalNodeStyle: React.CSSProperties = {
  background: "rgba(16, 185, 129, 0.12)",
  border: "2px solid rgba(16, 185, 129, 0.65)",
  borderRadius: 8,
  padding: 8,
  width: 172,
  color: "rgb(6, 78, 59)",
};
/** Sub-flow connection node (ADR 0030) — distinct violet card. */
const subflowNodeStyle: React.CSSProperties = {
  background: "rgba(139, 92, 246, 0.12)",
  border: "1px dashed rgba(139, 92, 246, 0.7)",
  borderRadius: 8,
  padding: 8,
  width: 172,
  color: "rgb(76, 29, 149)",
};

/** Node face — kept compact; full editing is in the side panel. The native
 *  `title` shows the full objective on hover (canvas transforms clip a custom
 *  tooltip, so we use the browser one here). */
function StageNodeBody({ stage, isEntry }: { stage: Stage; isEntry: boolean }) {
  return (
    <div
      className="text-left"
      title={stage.objective || (stage.kind === "terminal" ? "Terminal goal stage" : stage.id)}
    >
      <div className="flex items-center gap-1.5">
        <Icon
          name={stage.kind === "terminal" ? "goal" : stage.kind === "subflow" ? "subflow" : "agent"}
          className="h-3.5 w-3.5 shrink-0"
        />
        <span className="truncate font-mono text-[12px] font-semibold">
          {stage.id || "(unnamed)"}
        </span>
        {isEntry && (
          <Icon
            name="entry"
            className="h-3.5 w-3.5 shrink-0 text-indigo-600 dark:text-indigo-400"
          />
        )}
      </div>
      {stage.objective && (
        <p className="mt-1 line-clamp-2 max-w-[150px] text-[10px] leading-snug opacity-70">
          {stage.objective}
        </p>
      )}
      {stage.kind === "agent" && (
        <div className="mt-1 flex items-center gap-1 text-[10px] opacity-70">
          <Icon name="tools" className="h-3 w-3" />
          {stage.allowedActionIds.length}
        </div>
      )}
    </div>
  );
}

/** A single outgoing transition, described for the node's source-dot rendering. */
type OutgoingDot = { index: number; label: string; color: string };

/** Dot/edge color by trigger kind — the branch/no-progress/plain palette used
 *  for edges, reused so each source dot visually matches the edge leaving it. */
function transitionColor(t: StageTransition): string {
  if (t.on === "slot_condition") return "#6366f1"; // branch — indigo
  if (t.on === "no_progress") return "#d97706"; // escape hatch — amber
  return "#71717a"; // plain progression — zinc
}

/** Data carried on each ReactFlow node for the custom stage renderer. */
type StageNodeData = { stage: Stage; isEntry: boolean; outgoing: OutgoingDot[] };

/**
 * Custom stage node. Renders the compact stage face plus ONE source dot per
 * outgoing transition — each at a distinct position along the bottom edge and
 * colored to match its edge — so a stage that branches to A vs B shows two
 * separate dots instead of every edge sharing one. A trailing dashed "+" dot
 * is the drag-to-add-transition affordance; the per-transition dots are pure
 * anchors (not draggable) so dragging always means "create a new transition".
 */
function StageFlowNode({ data }: NodeProps) {
  const { stage, isEntry, outgoing } = data as StageNodeData;
  const isTerminal = stage.kind === "terminal";
  const isSubflow = stage.kind === "subflow";
  const n = outgoing.length;
  // Even spacing across the bottom: dot k sits at (k+1)/(n+1) of the width.
  const dotLeft = (k: number): string => `${(((k + 1) / (n + 1)) * 100).toFixed(2)}%`;
  return (
    <>
      {/* Incoming edges land on this single top handle (default target). */}
      <Handle
        type="target"
        position={Position.Top}
        style={{ width: 8, height: 8, background: "#a1a1aa", border: "1.5px solid #fff" }}
      />
      <StageNodeBody stage={stage} isEntry={isEntry} />
      {/* One anchor dot per outgoing transition, color-matched to its edge. */}
      {outgoing.map((o, k) => (
        <Handle
          key={o.index}
          id={`src:${o.index}`}
          type="source"
          position={Position.Bottom}
          isConnectable={false}
          title={o.label}
          style={{
            left: dotLeft(k),
            width: 10,
            height: 10,
            background: o.color,
            border: "2px solid #fff",
            boxShadow: "0 0 0 1px rgba(0,0,0,0.06)",
          }}
        />
      ))}
      {/* Drag-to-add affordance (terminals + sub-flows have no free transitions).
          Sits just right of the last anchor; hollow + dashed so it reads as "add". */}
      {!isTerminal && !isSubflow && (
        <Handle
          id="src:add"
          type="source"
          position={Position.Bottom}
          title="Drag to add a transition"
          style={{
            left: n === 0 ? "50%" : "calc(100% - 6px)",
            width: 10,
            height: 10,
            background: "#fff",
            border: "1.5px dashed #6366f1",
          }}
        />
      )}
      {/* Sub-flow: a single default source handle for the (exit) edges, which
          carry no per-transition sourceHandle. */}
      {isSubflow && (
        <Handle
          type="source"
          position={Position.Bottom}
          isConnectable={false}
          style={{ width: 9, height: 9, background: "#8b5cf6", border: "2px solid #fff" }}
        />
      )}
    </>
  );
}

/** Stable node-type registry (must be referentially stable across renders). */
const stageNodeTypes: NodeTypes = { stage: StageFlowNode };

/** Per-edge data the custom edge needs to place + style its own label. */
type StageEdgeData = {
  stageId: string;
  index: number;
  isBranch: boolean;
  dashed: boolean;
};

/**
 * Custom edge. Draws the orthogonal path, but renders its label near its OWN
 * source dot and staggers it vertically by transition index — so two branches
 * leaving the same stage never stack their labels on top of each other (the
 * midpoint-label overlap problem). The label is a readable pill matching the
 * edge's kind, and clicking it selects the transition just like clicking the line.
 */
function StageFlowEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  markerEnd,
  style,
  label,
  data,
}: EdgeProps) {
  const [path] = getSmoothStepPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    borderRadius: 12,
    offset: 16,
  });
  const d = data as StageEdgeData | undefined;
  const idx = d?.index ?? 0;
  const isBranch = d?.isBranch ?? false;
  // Sit just below the source dot, one row per transition index — guarantees
  // vertical separation regardless of how the lines route.
  const labelX = sourceX;
  const labelY = sourceY + 20 + idx * 20;
  return (
    <>
      <BaseEdge id={id} path={path} markerEnd={markerEnd} style={style} />
      <EdgeLabelRenderer>
        <div
          className={
            "pointer-events-auto absolute cursor-pointer select-none whitespace-nowrap rounded px-1.5 py-0.5 text-[10px] font-medium shadow-sm ring-1 " +
            (isBranch
              ? "bg-indigo-50 text-indigo-700 ring-indigo-200 dark:bg-indigo-500/15 dark:text-indigo-300 dark:ring-indigo-500/30"
              : d?.dashed
                ? "bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-500/15 dark:text-amber-300 dark:ring-amber-500/30"
                : "bg-white text-zinc-600 ring-zinc-200 dark:bg-zinc-900 dark:text-zinc-300 dark:ring-zinc-700")
          }
          style={{ transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)` }}
          data-stage-edge-label={id}
          onClick={(e) => {
            e.stopPropagation();
            if (d)
              window.dispatchEvent(
                new CustomEvent("stage-edge-select", {
                  detail: { stageId: d.stageId, index: d.index },
                }),
              );
          }}
        >
          {label}
        </div>
      </EdgeLabelRenderer>
    </>
  );
}

/** Stable edge-type registry. */
const stageEdgeTypes: EdgeTypes = { stageEdge: StageFlowEdge };

/** Layered layout: BFS depth from the entry stage → x by depth, y by row. */
function buildStageGraphView(def: FlowDefinition): { nodes: Node[]; edges: Edge[] } {
  const byId = new Map(def.stages.map((s) => [s.id, s]));
  const depth = new Map<string, number>();
  const queue: Array<[string, number]> = [];
  if (byId.has(def.entryStageId)) queue.push([def.entryStageId, 0]);
  while (queue.length > 0) {
    const head = queue.shift();
    if (!head) break;
    const [id, d] = head;
    const seen = depth.get(id);
    if (seen !== undefined && seen <= d) continue;
    depth.set(id, d);
    const s = byId.get(id);
    if (s) {
      for (const t of s.transitions) {
        if (byId.has(t.toStage)) queue.push([t.toStage, d + 1]);
      }
    }
  }

  const rowByLayer = new Map<number, number>();
  let orphanLayer = 0;
  const nodes: Node[] = def.stages.map((s) => {
    let layer = depth.get(s.id);
    if (layer === undefined) {
      layer = (orphanLayer += 1) + 8; // park unreachable stages off to the right
    }
    const row = rowByLayer.get(layer) ?? 0;
    rowByLayer.set(layer, row + 1);
    const isEntry = s.id === def.entryStageId;
    // Use the saved position if the user has placed this stage; else auto-lay-out.
    const saved = def.layout?.[s.id];
    // One source dot per (resolvable) outgoing transition — index preserved so
    // the dot's handle id (`src:{index}`) matches the edge's sourceHandle.
    const outgoing: OutgoingDot[] = s.transitions
      .map((t, i) => ({ t, i }))
      .filter(({ t }) => byId.has(t.toStage))
      .map(({ t, i }) => ({ index: i, label: triggerLabel(t), color: transitionColor(t) }));
    return {
      id: s.id,
      type: "stage",
      position: saved ? { x: saved.x, y: saved.y } : { x: 30 + layer * 215, y: 30 + row * 118 },
      data: { stage: s, isEntry, outgoing } satisfies StageNodeData,
      style:
        s.kind === "terminal"
          ? terminalNodeStyle
          : s.kind === "subflow"
            ? subflowNodeStyle
            : isEntry
              ? entryNodeStyle
              : agentNodeStyle,
    };
  });

  const edges: Edge[] = [];
  for (const s of def.stages) {
    s.transitions.forEach((t, i) => {
      if (!byId.has(t.toStage)) return;
      const isBranch = t.on === "slot_condition";
      const dashed = t.on === "no_progress";
      edges.push({
        id: `e:${s.id}:${i}`,
        source: s.id,
        sourceHandle: `src:${i}`,
        target: t.toStage,
        // Custom edge: orthogonal path + a label rendered near its own source
        // dot and staggered by index, so parallel branches never stack labels.
        type: "stageEdge",
        label: triggerLabel(t),
        animated: !dashed,
        // Conditional branches are drawn in indigo so a split reads at a glance;
        // no-progress stays dashed amber; the rest are plain progression edges.
        markerEnd: { type: MarkerType.ArrowClosed, color: isBranch ? "#6366f1" : undefined },
        style: isBranch
          ? { stroke: "#6366f1", strokeWidth: 2 }
          : dashed
            ? { strokeDasharray: "5 4", stroke: "#d97706" }
            : undefined,
        data: { stageId: s.id, index: i, isBranch, dashed } satisfies StageEdgeData,
      });
    });

    // Sub-flow connection nodes route via `exits` (sub terminal → parent stage),
    // not transitions — draw those as dashed violet edges so the graph reads.
    if (s.kind === "subflow" && s.exits) {
      Object.entries(s.exits).forEach(([subStageId, target], i) => {
        if (!byId.has(target)) return;
        edges.push({
          id: `x:${s.id}:${i}`,
          source: s.id,
          target,
          type: "stageEdge",
          label: `exit: ${subStageId}`,
          animated: false,
          markerEnd: { type: MarkerType.ArrowClosed, color: "#8b5cf6" },
          style: { strokeDasharray: "5 4", stroke: "#8b5cf6" },
          data: { stageId: s.id, index: i, isBranch: false, dashed: true } satisfies StageEdgeData,
        });
      });
    }
  }
  return { nodes, edges };
}

/**
 * Normalize a stored definition into a clean v2 shape. Rebuilds from known v2
 * keys only, which drops legacy fields left over from flows converted before
 * intents were removed — top-level `intents`, `on: "intent"` transitions, and
 * `intentId` — so they save under the strict v2 schema instead of erroring on
 * unrecognized keys.
 */
function sanitizeV2Definition(raw: unknown): FlowDefinition {
  const d = (raw ?? {}) as Partial<FlowDefinition> & Record<string, unknown>;
  const rawStages: Stage[] = Array.isArray(d.stages) ? (d.stages as Stage[]) : [];
  const stages: Stage[] = rawStages.map((s) => ({
    id: s.id,
    kind: s.kind,
    // Typed-stage fields (ADR 0029) — carried through on reload, or selecting a
    // stage type / editing examples silently reverts to `custom` every refresh.
    ...(s.type ? { type: s.type } : {}),
    ...(s.goalType ? { goalType: s.goalType } : {}),
    objective: s.objective,
    ...(typeof s.boundaries === "string" && s.boundaries.trim() !== ""
      ? { boundaries: s.boundaries }
      : {}),
    ...(s.goal &&
    typeof s.goal === "object" &&
    typeof s.goal.id === "string" &&
    s.goal.id.trim() !== ""
      ? {
          goal: {
            id: s.goal.id,
            ...(typeof s.goal.label === "string" && s.goal.label.trim() !== ""
              ? { label: s.goal.label }
              : {}),
            ...(s.goal.capture === "lead" || s.goal.capture === "none"
              ? { capture: s.goal.capture }
              : {}),
            ...(s.goal.emitEvent === true ? { emitEvent: true } : {}),
          },
        }
      : {}),
    // Sub-flow connection fields (ADR 0030) — carried through on reload, or the
    // node loses its connected flow + exit map every refresh.
    ...(s.kind === "subflow" && typeof s.flowRef === "string" && s.flowRef.trim() !== ""
      ? { flowRef: s.flowRef }
      : {}),
    ...(s.kind === "subflow" && s.exits && typeof s.exits === "object"
      ? { exits: s.exits as Record<string, string> }
      : {}),
    allowedActionIds: Array.isArray(s.allowedActionIds) ? s.allowedActionIds : [],
    requiredActionIds: Array.isArray(s.requiredActionIds) ? s.requiredActionIds : [],
    requiredSlotIds: Array.isArray(s.requiredSlotIds) ? s.requiredSlotIds : [],
    ...(typeof s.maxTurns === "number" ? { maxTurns: s.maxTurns } : {}),
    // Also ADR 0029 — few-shot examples, stage-scoped never-say, and the faq
    // toggle must survive a reload just like `type` above.
    ...(Array.isArray(s.examples) ? { examples: s.examples } : {}),
    ...(Array.isArray(s.neverSay) ? { neverSay: s.neverSay } : {}),
    ...(typeof s.faqEnabled === "boolean" ? { faqEnabled: s.faqEnabled } : {}),
    transitions: (Array.isArray(s.transitions) ? s.transitions : [])
      .filter((t) => (t as { on: string }).on !== "intent")
      .map((t) => {
        const tt = t as StageTransition & { intentId?: unknown };
        return {
          on: tt.on,
          ...(tt.actionId ? { actionId: tt.actionId } : {}),
          ...(tt.on === "slot_condition" && tt.condition ? { condition: tt.condition } : {}),
          toStage: tt.toStage,
        } as StageTransition;
      }),
  }));
  return {
    schemaVersion: 2,
    persona: typeof d.persona === "string" ? d.persona : "",
    greeting: typeof d.greeting === "string" ? d.greeting : "",
    slots: Array.isArray(d.slots) ? d.slots : [],
    actions: Array.isArray(d.actions) ? d.actions : [],
    guardrails: Array.isArray(d.guardrails) ? d.guardrails : [],
    stages,
    entryStageId: typeof d.entryStageId === "string" ? d.entryStageId : (stages[0]?.id ?? ""),
    defaultStageMaxTurns: typeof d.defaultStageMaxTurns === "number" ? d.defaultStageMaxTurns : 8,
    layout: d.layout && typeof d.layout === "object" ? d.layout : {},
    ...(d.businessHours ? { businessHours: d.businessHours } : {}),
    ...(d.escalation ? { escalation: d.escalation } : {}),
  };
}

function StagesFlowEditor({
  flow,
  tools,
  onFlowUpdated,
}: {
  flow: AgentFlowDTO;
  tools: ToolDefinitionDTO[] | null;
  onFlowUpdated: (flow: AgentFlowDTO) => void;
}) {
  const [def, setDef] = useState<FlowDefinition>(() => sanitizeV2Definition(flow.definition));
  // Account slot catalog (ADR 0030) — for referencing shared slots + resolving
  // refs to concrete slots in the condition / required-slot pickers.
  const [catalog, setCatalog] = useState<AgentFlowSlotDTO[]>([]);
  // Gate the slot-ref auto-sync on a successful catalog load — otherwise an
  // empty catalog (pre-fetch or on error) would strip valid catalog refs and
  // fail validation / lose data.
  const [catalogLoaded, setCatalogLoaded] = useState(false);
  useEffect(() => {
    let cancelled = false;
    api.agentflow
      .listSlots()
      .then((s) => {
        if (cancelled) return;
        setCatalog(s);
        setCatalogLoaded(true);
      })
      .catch(() => {
        /* catalog unavailable — keep the flow's existing slots untouched */
      });
    return () => {
      cancelled = true;
    };
  }, []);
  const catalogByKey = useMemo(() => new Map(catalog.map((s) => [s.key, s])), [catalog]);
  const effectiveSlots = useMemo(
    () => computeEffectiveSlots(def.slots, catalog),
    [def.slots, catalog],
  );
  // The catalog is the source of truth (ADR 0030): a flow no longer maintains
  // its own slot list. Keep `def.slots` derived from what the stages reference —
  // used catalog slots as `{ ref }`, legacy inline locals preserved. Guarded by
  // equality so it settles in one pass and doesn't fight autosave.
  useEffect(() => {
    if (!catalogLoaded) return;
    const derived = deriveSlotEntries(def, catalogByKey);
    if (JSON.stringify(derived) !== JSON.stringify(def.slots)) {
      setDef((d) => ({ ...d, slots: derived }));
    }
  }, [def, catalogByKey, catalogLoaded]);
  const [tab, setTab] = useState<V2Tab>("canvas");
  const [selected, setSelected] = useState<V2Selection>(null);
  const [sandboxOpen, setSandboxOpen] = useState(true);
  const [outboundOpen, setOutboundOpen] = useState(false);
  // The stage that produced the latest sandbox reply — highlighted on the canvas.
  const [activeStageId, setActiveStageId] = useState<string | null>(null);
  // Shared right-rail: resizable width + which occupant is shown when both the
  // stage/transition editor and the sandbox are open.
  const [panelWidth, setPanelWidth] = useState(440);
  const [railTab, setRailTab] = useState<"editor" | "sandbox">("editor");
  const resizingRef = useRef(false);
  const startResize = useCallback(() => {
    resizingRef.current = true;
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";
  }, []);
  useEffect(() => {
    const move = (e: MouseEvent) => {
      if (!resizingRef.current) return;
      // Rail is flush-right, so width = distance from the viewport's right edge.
      setPanelWidth(Math.min(760, Math.max(360, window.innerWidth - e.clientX)));
    };
    const up = () => {
      resizingRef.current = false;
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
  }, []);
  const [saveState, setSaveState] = useState<"idle" | "dirty" | "saving" | "saved" | "error">(
    "idle",
  );
  const [errors, setErrors] = useState<string[] | null>(null);
  const lastSaved = useRef<string | null>(null);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const patch = useCallback((p: Partial<FlowDefinition>) => {
    setDef((d) => ({ ...d, ...p }));
  }, []);

  const setStageById = useCallback(
    (id: string, p: Partial<Stage>) => {
      patch({ stages: def.stages.map((s) => (s.id === id ? { ...s, ...p } : s)) });
    },
    [def.stages, patch],
  );

  // Renaming a stage must cascade — otherwise the panel (keyed by id) unmounts
  // mid-type and references (entryStageId, transition targets) go dangling.
  const renameStage = useCallback((oldId: string, newId: string) => {
    setDef((d) => {
      // Migrate the saved canvas position to the new id, or the renamed stage
      // loses its placement and snaps back to auto-layout.
      const layout = { ...(d.layout ?? {}) };
      if (layout[oldId]) {
        layout[newId] = layout[oldId];
        delete layout[oldId];
      }
      return {
        ...d,
        entryStageId: d.entryStageId === oldId ? newId : d.entryStageId,
        layout,
        stages: d.stages.map((s) => {
          const self = s.id === oldId ? { ...s, id: newId } : s;
          return {
            ...self,
            transitions: self.transitions.map((t) =>
              t.toStage === oldId ? { ...t, toStage: newId } : t,
            ),
          };
        }),
      };
    });
    setSelected({ kind: "stage", id: newId });
  }, []);

  // ReactFlow view derived from the definition. The auto-layout only supplies
  // a position for stages the user hasn't placed yet — once a node has been
  // dragged we KEEP its position across edits/saves (we never re-lay-out a
  // node the user has arranged). Labels + edges still refresh from the def.
  const view = useMemo(() => buildStageGraphView(def), [def]);
  const [rfNodes, setRfNodes] = useState<Node[]>(view.nodes);
  const [rfEdges, setRfEdges] = useState<Edge[]>(view.edges);
  useEffect(() => {
    setRfNodes((prev) => {
      const priorPos = new Map(prev.map((n) => [n.id, n.position]));
      return view.nodes.map((n) => {
        const kept = priorPos.get(n.id);
        return kept ? { ...n, position: kept } : n; // preserve user placement
      });
    });
    setRfEdges(view.edges);
  }, [view]);

  // Overlay a glowing highlight on the stage that produced the latest sandbox
  // reply, so testers can see which block is responding. Layered on top of
  // rfNodes (positions) at render time — doesn't mutate the stored layout.
  const displayNodes = useMemo(
    () =>
      rfNodes.map((n) =>
        n.id === activeStageId
          ? {
              ...n,
              className: `${n.className ?? ""} animate-pulse`.trim(),
              style: {
                ...(n.style ?? {}),
                boxShadow: "0 0 0 3px #6366f1, 0 0 16px 4px rgba(99,102,241,0.45)",
              },
            }
          : n,
      ),
    [rfNodes, activeStageId],
  );

  // ReactFlow instance — used only by the explicit "Fit view" button. We do
  // NOT auto-fit on edits/selection/sandbox toggles, so the user's manual
  // arrangement and zoom are left exactly as they set them.
  const rfRef = useRef<ReactFlowInstance | null>(null);
  // The canvas pane element — lets us compute the center of what the user is
  // currently looking at, so a newly added stage lands in view (not off-screen).
  const canvasRef = useRef<HTMLDivElement | null>(null);
  const fitView = useCallback(() => {
    rfRef.current?.fitView({ padding: 0.18, maxZoom: 1, duration: 200 });
  }, []);

  const onNodesChange = useCallback(
    (changes: NodeChange[]) => setRfNodes((nds) => applyNodeChanges(changes, nds)),
    [],
  );
  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) => setRfEdges((eds) => applyEdgeChanges(changes, eds)),
    [],
  );

  // Custom edge labels live in a portal (EdgeLabelRenderer), so their clicks
  // don't flow through ReactFlow's onEdgeClick. Bridge them back to selection.
  useEffect(() => {
    const onSelect = (e: Event) => {
      const detail = (e as CustomEvent<{ stageId?: string; index?: number }>).detail;
      if (detail?.stageId !== undefined && detail.index !== undefined) {
        setSelected({ kind: "transition", stageId: detail.stageId, index: detail.index });
      }
    };
    window.addEventListener("stage-edge-select", onSelect);
    return () => window.removeEventListener("stage-edge-select", onSelect);
  }, []);

  // Deleting a node on the canvas (Backspace/Delete) must sync to the flow
  // definition — otherwise the stage lingers in `def` and blocks save. Remove
  // the stage(s) plus any transitions/exits/layout that referenced them.
  const onNodesDelete = useCallback(
    (deleted: Node[]) => {
      const ids = new Set(deleted.map((n) => n.id));
      if (ids.size === 0) return;
      patch({
        stages: def.stages
          .filter((s) => !ids.has(s.id))
          .map((s) => ({
            ...s,
            transitions: s.transitions.filter((t) => !ids.has(t.toStage)),
            ...(s.exits
              ? {
                  exits: Object.fromEntries(
                    Object.entries(s.exits).filter(([, target]) => !ids.has(target)),
                  ),
                }
              : {}),
          })),
        layout: Object.fromEntries(Object.entries(def.layout ?? {}).filter(([k]) => !ids.has(k))),
      });
      setSelected((cur) =>
        cur?.kind === "stage" && ids.has(cur.id)
          ? null
          : cur?.kind === "transition" && ids.has(cur.stageId)
            ? null
            : cur,
      );
    },
    [def.stages, def.layout, patch],
  );

  const onConnect = useCallback(
    (conn: Connection) => {
      if (!conn.source || !conn.target || conn.source === conn.target) return;
      const src = def.stages.find((s) => s.id === conn.source);
      if (!src || src.kind === "terminal") return; // terminals have no outgoing edges
      const target = conn.target;
      const next: StageTransition = src.requiredSlotIds.length
        ? { on: "slots_complete", toStage: target }
        : src.allowedActionIds.length
          ? { on: "action_success", actionId: src.allowedActionIds[0], toStage: target }
          : { on: "no_progress", toStage: target };
      const newIndex = src.transitions.length;
      setStageById(src.id, { transitions: [...src.transitions, next] });
      setSelected({ kind: "transition", stageId: src.id, index: newIndex });
    },
    [def.stages, setStageById],
  );

  const addStage = useCallback(
    (kind: Stage["kind"]) => {
      const isSub = kind === "subflow";
      // One id used consistently for the stage, its saved position, and selection
      // — keying the layout/selection off a different id parks the node off-screen.
      const id = `${isSub ? "subflow" : kind === "terminal" ? "done" : "stage"}_${def.stages.length + 1}`;
      // Give a new stage a real archetype up front instead of leaving it blank —
      // an untyped stage renders as "Custom" in the editor, which reads as "the
      // stage has no type." A terminal is always `custom` (its objective is the
      // closing line); a new agent stage defaults to `collect_info` (the most
      // common working stage) and the author can switch the Type dropdown. Typed
      // stages generate their own objective, so seed a short placeholder just to
      // keep the draft valid until the author configures it.
      const type: Stage["type"] = kind === "agent" ? "collect_info" : "custom";
      const stage: Stage = {
        id,
        kind,
        type,
        objective: isSub
          ? "Runs a connected sub-flow."
          : kind === "terminal"
            ? "Confirm what happens next and close warmly."
            : "Collect the details this step needs.",
        allowedActionIds: [],
        requiredActionIds: [],
        requiredSlotIds: [],
        transitions: [],
        // Sub-flow connection (ADR 0030): author picks the flow + maps exits.
        ...(isSub ? { flowRef: "", exits: {} } : {}),
      };

      // A brand-new stage has no incoming transition, so the auto-layout treats
      // it as an "orphan" and parks it far off to the right — out of view. Pin it
      // to the center of the current viewport instead so it appears right where
      // the user is looking. A small per-stage jitter keeps successive adds from
      // stacking exactly on top of each other. We honor the existing "don't
      // change the user's zoom" rule — no fitView, just a placement.
      const inst = rfRef.current;
      const rect = canvasRef.current?.getBoundingClientRect();
      let layoutPatch: FlowDefinition["layout"] | undefined;
      if (inst && rect) {
        const jitter = (def.stages.length % 5) * 28;
        // Nudge up-left by ~half a node so the node body lands centered.
        const center = inst.screenToFlowPosition({
          x: rect.left + rect.width / 2 - 90 + jitter,
          y: rect.top + rect.height / 2 - 36 + jitter,
        });
        layoutPatch = { ...(def.layout ?? {}), [id]: { x: center.x, y: center.y } };
      }

      patch({
        stages: [...def.stages, stage],
        ...(layoutPatch ? { layout: layoutPatch } : {}),
      });
      setSelected({ kind: "stage", id });
    },
    [def.stages, def.layout, patch],
  );

  // Validate + persist the current definition. Used by both autosave and the
  // explicit Save button. Won't hit the backend on an invalid def — it surfaces
  // the validation errors instead.
  const persist = useCallback(async (): Promise<boolean> => {
    const parsed = FlowDefinitionSchema.safeParse(def);
    if (!parsed.success) {
      setErrors(
        parsed.error.issues.map(
          (i) => `${i.path.length ? i.path.join(".") : "(root)"}: ${i.message}`,
        ),
      );
      setSaveState("error");
      return false;
    }
    setErrors(null);
    setSaveState("saving");
    try {
      const updated = await api.agentflow.updateFlow(flow.id, {
        definition: def as unknown as FlowDefinition,
      });
      lastSaved.current = JSON.stringify(def);
      onFlowUpdated(updated);
      setSaveState("saved");
      return true;
    } catch (e) {
      setErrors([e instanceof ApiClientError ? `${e.code}: ${e.message}` : "Save failed."]);
      setSaveState("error");
      return false;
    }
  }, [def, flow.id, onFlowUpdated]);

  // Publish — save the draft first (so the snapshot matches the editor), then
  // create an immutable FlowVersion. Live webhook + outbound traffic resolve
  // the latest published version, never the draft.
  const [publishState, setPublishState] = useState<
    "idle" | "publishing" | "published" | "warned" | "error"
  >("idle");
  const [publishMsg, setPublishMsg] = useState<string | null>(null);
  const publish = useCallback(async () => {
    setPublishMsg(null);
    setPublishState("publishing");
    const saved = await persist();
    if (!saved) {
      setPublishState("error");
      setPublishMsg("Fix the validation errors above before publishing.");
      return;
    }
    try {
      const res = await api.agentflow.publishFlow(flow.id);
      onFlowUpdated(res.flow);
      // Publish succeeded either way. Warnings flag legal-but-trapping shapes
      // (e.g. a required choice slot with no "none" option) — advisory, because
      // some enums are genuinely exhaustive. Shown as a caution, not an error.
      setPublishState(res.warnings.length > 0 ? "warned" : "published");
      setPublishMsg(
        res.warnings.length > 0
          ? `Published v${res.version.version} — live now, but check this: ${res.warnings.join(" ")}`
          : `Published v${res.version.version}. It's now live for inbound & outbound.`,
      );
    } catch (e) {
      setPublishState("error");
      setPublishMsg(e instanceof ApiClientError ? `${e.code}: ${e.message}` : "Publish failed.");
    }
  }, [persist, flow.id, onFlowUpdated]);

  // Auto-save (1s debounce), mirroring the v1 editor. The first run records the
  // loaded baseline so we don't save on mount.
  useEffect(() => {
    const serialized = JSON.stringify(def);
    if (lastSaved.current === null) {
      lastSaved.current = serialized;
      return;
    }
    if (serialized === lastSaved.current) return;
    setSaveState("dirty");
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => void persist(), 1000);
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [def, persist]);

  const selectedStage =
    selected?.kind === "stage" ? def.stages.find((s) => s.id === selected.id) : undefined;
  const selectedTransition =
    selected?.kind === "transition"
      ? def.stages.find((s) => s.id === selected.stageId)?.transitions[selected.index]
      : undefined;

  // Selecting a stage/transition brings the editor forward in the shared rail.
  useEffect(() => {
    if (selected) setRailTab("editor");
  }, [selected]);

  return (
    <div className="flex h-screen flex-col">
      <header className="flex items-center justify-between border-b border-zinc-200 px-4 py-2.5 dark:border-zinc-800">
        <div>
          <Link
            to="/agentflow"
            className="text-[11px] text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-200"
          >
            ← Flows
          </Link>
          <h1 className="text-sm font-semibold text-zinc-900 dark:text-zinc-50">
            {flow.name}{" "}
            <span className="ml-1 rounded bg-brand-50 px-1.5 py-0.5 text-[10px] font-medium text-brand-700 dark:bg-brand-500/10 dark:text-brand-300">
              stages · v2
            </span>
            <span
              className={
                "ml-1 rounded px-1.5 py-0.5 text-[10px] font-medium " +
                (flow.status === "published"
                  ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300"
                  : "bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300")
              }
            >
              {flow.status === "published" ? `Published · v${flow.version}` : "Draft"}
            </span>
            {/* Once published at least once, always surface the live version —
                even while editing a newer draft (status flips back to draft, but
                v{version} stays live for inbound/outbound until the next publish). */}
            {flow.status !== "published" && flow.version >= 1 && (
              <span
                className="ml-1 rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300"
                title="The latest published version currently serving live traffic. Publish to make your draft edits live."
              >
                Live · v{flow.version}
              </span>
            )}
          </h1>
          <CopyableId id={flow.id} label="Flow ID" />
        </div>
        <div className="flex items-center gap-2">
          <span
            className={
              "text-[11px] " +
              (saveState === "saved"
                ? "text-emerald-600 dark:text-emerald-400"
                : saveState === "error"
                  ? "text-red-600 dark:text-red-400"
                  : "text-zinc-500")
            }
          >
            {saveState === "saving"
              ? "Saving…"
              : saveState === "saved"
                ? "All changes saved"
                : saveState === "dirty"
                  ? "Unsaved changes…"
                  : saveState === "error"
                    ? "Couldn't save"
                    : ""}
          </span>
          <Button size="sm" variant="secondary" onClick={() => setOutboundOpen(true)}>
            Outbound
          </Button>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              if (!sandboxOpen) setRailTab("sandbox");
              setSandboxOpen((v) => !v);
            }}
          >
            {sandboxOpen ? "Hide sandbox" : "Show sandbox"}
          </Button>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => void persist()}
            disabled={saveState === "saving"}
          >
            {saveState === "saving" ? "Saving…" : "Save"}
          </Button>
          <Button
            size="sm"
            onClick={() => void publish()}
            disabled={publishState === "publishing" || saveState === "saving"}
          >
            {publishState === "publishing" ? "Publishing…" : "Publish"}
          </Button>
        </div>
      </header>

      {publishMsg && (
        <div
          role="status"
          className={
            "border-b px-4 py-2 text-[12px] " +
            (publishState === "published"
              ? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300"
              : publishState === "warned"
                ? "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200"
                : "border-red-200 bg-red-50 text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300")
          }
        >
          {publishMsg}
        </div>
      )}

      {outboundOpen && (
        <OutboundModal
          flow={flow}
          onClose={() => setOutboundOpen(false)}
          onGoToChannels={() => setTab("channels")}
        />
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-zinc-200 px-3 dark:border-zinc-800">
        {(
          ["canvas", "tools", "slots", "persona", "greeting", "guardrails", "channels"] as const
        ).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={
              "flex items-center gap-1.5 border-b-2 px-3 py-2.5 text-[13px] font-medium capitalize transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500 " +
              (tab === t
                ? "border-brand-500 text-brand-700 dark:text-brand-300"
                : "border-transparent text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-200")
            }
          >
            <Icon name={t} className="h-4 w-4" />
            {t}
          </button>
        ))}
      </div>

      {errors && (
        <div className="border-b border-red-200 bg-red-50 px-4 py-2 text-[12px] text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
          <p className="font-medium">Can't save — fix these:</p>
          <ul className="mt-1 list-disc pl-5">
            {errors.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex min-h-0 flex-1">
        {/* WORKSPACE */}
        <section className="relative min-h-0 flex-1" aria-label="Stage flow workspace">
          {tab === "canvas" && (
            <div ref={canvasRef} className="relative h-full w-full bg-zinc-50 dark:bg-zinc-950/40">
              <ReactFlow
                nodes={displayNodes}
                edges={rfEdges}
                nodeTypes={stageNodeTypes}
                edgeTypes={stageEdgeTypes}
                onNodesChange={onNodesChange}
                onNodesDelete={onNodesDelete}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                onNodeDragStop={(_e, node) =>
                  patch({
                    layout: {
                      ...(def.layout ?? {}),
                      [node.id]: { x: node.position.x, y: node.position.y },
                    },
                  })
                }
                onInit={(inst) => {
                  rfRef.current = inst;
                }}
                onNodeClick={(_e, node) => setSelected({ kind: "stage", id: node.id })}
                onEdgeClick={(_e, edge) => {
                  const d = edge.data as { stageId?: string; index?: number } | undefined;
                  if (d?.stageId !== undefined && d.index !== undefined) {
                    setSelected({ kind: "transition", stageId: d.stageId, index: d.index });
                  }
                }}
                onPaneClick={() => setSelected(null)}
                fitView
                fitViewOptions={{ padding: 0.18, maxZoom: 1 }}
                minZoom={0.3}
                maxZoom={1.5}
                connectionMode={ConnectionMode.Loose}
                proOptions={{ hideAttribution: true }}
              >
                <Background />
                <Controls />
              </ReactFlow>
              <V2CanvasDock onAdd={addStage} onFit={fitView} />
            </div>
          )}

          {tab === "tools" && (
            <div className="h-full overflow-y-auto p-5">
              <ActionsManager def={def} tools={tools} onPatch={patch} />
            </div>
          )}

          {tab === "slots" && (
            <div className="h-full overflow-y-auto p-5">
              <SectionCard
                title="Slots"
                hint="Slots are defined once in the account Slot catalog and reused across flows. This flow automatically uses the catalog slots its stages require or branch on — add them from a stage's Tools & Data / Routing tabs."
              >
                <div className="flex items-center justify-between gap-2 rounded-md border border-indigo-200 bg-indigo-50/50 px-3 py-2 text-[12px] text-indigo-700 dark:border-indigo-500/30 dark:bg-indigo-500/10 dark:text-indigo-300">
                  <span>Slot definitions live in the account catalog now.</span>
                  <Link to="/agentflow/slots" className="shrink-0 font-medium underline">
                    Open Slot catalog
                  </Link>
                </div>

                {def.slots.length === 0 ? (
                  <Empty>
                    This flow doesn&rsquo;t use any slots yet. On a stage, require a slot or add a
                    branch condition — pick from the catalog.
                  </Empty>
                ) : (
                  def.slots.map((slot, i) =>
                    isSlotRef(slot) ? (
                      <SlotRefRow key={i} slotKey={slot.ref} dto={catalogByKey.get(slot.ref)} />
                    ) : (
                      <LegacyLocalSlotRow key={i} slot={slot} />
                    ),
                  )
                )}
              </SectionCard>
            </div>
          )}

          {tab === "persona" && (
            <div className="flex h-full flex-col overflow-hidden">
              <PersonaEditor
                persona={def.persona}
                onChange={(persona) => patch({ persona })}
                fullWidth
              />
            </div>
          )}

          {tab === "greeting" && (
            <div className="flex h-full flex-col overflow-hidden">
              <GreetingEditor
                greeting={def.greeting}
                onChange={(greeting) => patch({ greeting })}
                fullWidth
              />
            </div>
          )}

          {tab === "guardrails" && (
            <div className="h-full overflow-y-auto p-5">
              <GuardrailsEditor def={def} onPatch={patch} />
            </div>
          )}

          {tab === "channels" && (
            <div className="h-full overflow-y-auto p-5">
              <ChannelsEditor flowId={flow.id} />
            </div>
          )}
        </section>

        {/* RIGHT RAIL — the stage/transition editor and the sandbox share one
            resizable panel. When both are open a switcher toggles between them,
            so the canvas is never squeezed by a third column. */}
        {(() => {
          const editorOpen =
            tab === "canvas" && (selectedStage != null || selectedTransition != null);
          const railOpen = editorOpen || sandboxOpen;
          if (!railOpen) return null;
          const showSwitcher = editorOpen && sandboxOpen;
          const shows: "editor" | "sandbox" = showSwitcher
            ? railTab
            : editorOpen
              ? "editor"
              : "sandbox";
          return (
            <aside
              className="relative flex shrink-0 flex-col border-l border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900/40"
              style={{ width: panelWidth }}
              aria-label="Editor panel"
            >
              <div
                role="separator"
                aria-orientation="vertical"
                aria-label="Resize panel"
                title="Drag to resize"
                onMouseDown={startResize}
                className="absolute left-0 top-0 z-10 h-full w-1.5 -translate-x-1/2 cursor-col-resize hover:bg-brand-500/40"
              />
              {showSwitcher && (
                <div className="flex shrink-0 gap-1 border-b border-zinc-200 bg-zinc-50 p-1.5 dark:border-zinc-800 dark:bg-zinc-950/40">
                  {(["editor", "sandbox"] as const).map((k) => (
                    <button
                      key={k}
                      type="button"
                      onClick={() => setRailTab(k)}
                      className={
                        "flex-1 rounded-md px-2 py-1 text-[12px] font-medium capitalize transition-colors " +
                        (railTab === k
                          ? "bg-white text-zinc-900 shadow-sm dark:bg-zinc-800 dark:text-zinc-100"
                          : "text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-200")
                      }
                    >
                      {k}
                    </button>
                  ))}
                </div>
              )}
              <div className="min-h-0 flex-1">
                {shows === "editor" && selectedStage && (
                  <StagePanel
                    stage={selectedStage}
                    def={def}
                    effectiveSlots={effectiveSlots}
                    isEntry={selectedStage.id === def.entryStageId}
                    onClose={() => setSelected(null)}
                    onChange={(p) => {
                      if (p.id !== undefined && p.id !== selectedStage.id) {
                        renameStage(selectedStage.id, p.id);
                      } else {
                        setStageById(selectedStage.id, p);
                      }
                    }}
                    onSetEntry={() => patch({ entryStageId: selectedStage.id })}
                    onDelete={() => {
                      patch({ stages: def.stages.filter((s) => s.id !== selectedStage.id) });
                      setSelected(null);
                    }}
                  />
                )}
                {shows === "editor" && selected?.kind === "transition" && selectedTransition && (
                  <TransitionPanel
                    transition={selectedTransition}
                    sourceStage={def.stages.find((s) => s.id === selected.stageId)}
                    stages={def.stages}
                    effectiveSlots={effectiveSlots}
                    onClose={() => setSelected(null)}
                    onChange={(p) => {
                      const sid = selected.stageId;
                      const idx = selected.index;
                      const s = def.stages.find((st) => st.id === sid);
                      if (!s) return;
                      setStageById(sid, {
                        transitions: s.transitions.map((t, j) => (j === idx ? { ...t, ...p } : t)),
                      });
                    }}
                    onDelete={() => {
                      const sid = selected.stageId;
                      const idx = selected.index;
                      const s = def.stages.find((st) => st.id === sid);
                      if (s)
                        setStageById(sid, {
                          transitions: s.transitions.filter((_, j) => j !== idx),
                        });
                      setSelected(null);
                    }}
                  />
                )}
                {shows === "sandbox" && (
                  <div className="flex h-full flex-col">
                    <SandboxPane
                      flowId={flow.id}
                      onActiveStage={(s) => setActiveStageId(s ?? null)}
                      slots={effectiveSlots}
                      greeting={def.greeting}
                      entryStageId={def.entryStageId}
                    />
                  </div>
                )}
              </div>
            </aside>
          );
        })()}
      </div>
    </div>
  );
}

/** Floating add-stage dock over the canvas (mirrors the v1 CanvasDock). */
function V2CanvasDock({
  onAdd,
  onFit,
}: {
  onAdd: (kind: Stage["kind"]) => void;
  onFit: () => void;
}) {
  return (
    <div className="absolute left-3 top-3 flex gap-1.5 rounded-lg border border-zinc-200 bg-white/90 p-1.5 shadow-sm backdrop-blur dark:border-zinc-800 dark:bg-zinc-900/80">
      <Tooltip label="Fit / reset view">
        <button
          type="button"
          aria-label="Fit view"
          onClick={onFit}
          className="flex h-9 items-center gap-1.5 rounded-md border border-zinc-200 px-2.5 text-[12px] font-medium text-zinc-700 transition-colors hover:bg-zinc-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:border-zinc-700 dark:text-zinc-200 dark:hover:bg-zinc-800"
        >
          <Icon name="goal" className="h-4 w-4" />
          Fit
        </button>
      </Tooltip>
      <span className="mx-0.5 w-px self-stretch bg-zinc-200 dark:bg-zinc-700" aria-hidden="true" />
      <Tooltip label="Add agent stage">
        <button
          type="button"
          aria-label="Add agent stage"
          onClick={() => onAdd("agent")}
          className="flex h-9 items-center gap-1.5 rounded-md border border-zinc-200 px-2.5 text-[12px] font-medium text-zinc-700 transition-colors hover:bg-zinc-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:border-zinc-700 dark:text-zinc-200 dark:hover:bg-zinc-800"
        >
          <Icon name="plus" className="h-3.5 w-3.5" />
          <Icon name="agent" className="h-4 w-4" />
        </button>
      </Tooltip>
      <Tooltip label="Add terminal (goal) stage">
        <button
          type="button"
          aria-label="Add terminal goal stage"
          onClick={() => onAdd("terminal")}
          className="flex h-9 items-center gap-1.5 rounded-md border border-emerald-300 px-2.5 text-[12px] font-medium text-emerald-700 transition-colors hover:bg-emerald-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 dark:border-emerald-700 dark:text-emerald-300 dark:hover:bg-emerald-900/20"
        >
          <Icon name="plus" className="h-3.5 w-3.5" />
          <Icon name="goal" className="h-4 w-4" />
        </button>
      </Tooltip>
      <Tooltip label="Add sub-flow connection">
        <button
          type="button"
          aria-label="Add sub-flow connection"
          onClick={() => onAdd("subflow")}
          className="flex h-9 items-center gap-1.5 rounded-md border border-violet-300 px-2.5 text-[12px] font-medium text-violet-700 transition-colors hover:bg-violet-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 dark:border-violet-700 dark:text-violet-300 dark:hover:bg-violet-900/20"
        >
          <Icon name="plus" className="h-3.5 w-3.5" />
          <Icon name="subflow" className="h-4 w-4" />
        </button>
      </Tooltip>
    </div>
  );
}

/**
 * Editor for a `kind: "subflow"` connection node (ADR 0030): pick the flow to
 * inline, and map its terminal (goal) stage ids to this flow's continuation
 * stages. The compiler splices the referenced flow's latest published version
 * at run time.
 */
function SubflowStagePanel({
  stage,
  def,
  onChange,
  onClose,
  onDelete,
}: {
  stage: Stage;
  def: FlowDefinition;
  onChange: (p: Partial<Stage>) => void;
  onClose: () => void;
  onDelete: () => void;
}) {
  const [flows, setFlows] = useState<AgentFlowDTO[]>([]);
  useEffect(() => {
    let cancelled = false;
    api.agentflow
      .listFlows()
      .then((res) => {
        if (!cancelled) setFlows(res.flows);
      })
      .catch(() => {
        /* picker just shows the raw id until flows load */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const exits = stage.exits ?? {};
  const exitRows = Object.entries(exits);
  // Valid exit targets: any of THIS flow's non-subflow stages (a continuation).
  const targets = def.stages.filter((s) => s.id !== stage.id && s.kind !== "subflow");
  // Source side: the connected child flow's terminal stages (its goals). Read
  // from the loaded flow list so authors PICK a real goal id — no typos, no
  // drift from the child's actual stage ids.
  const childFlow = flows.find((f) => f.id === stage.flowRef);
  const childTerminals = (childFlow?.definition.stages ?? []).filter((s) => s.kind === "terminal");
  const goalLabel = (s: Stage): string => (s.goal?.label ? `${s.id} — ${s.goal.label}` : s.id);
  const usedSubIds = new Set(exitRows.map(([k]) => k));
  const unmappedTerminal = childTerminals.find((t) => !usedSubIds.has(t.id));

  const setExits = (next: Record<string, string>) =>
    onChange({ exits: Object.keys(next).length > 0 ? next : {} });

  return (
    <PanelShell title="Sub-flow connection" onClose={onClose}>
      <div className="space-y-1">
        <label className="text-[11px] font-medium text-zinc-500">Stage id</label>
        <input
          className={inputCls + " font-mono"}
          value={stage.id}
          onChange={(e) => onChange({ id: e.target.value })}
        />
      </div>

      <div className="space-y-1">
        <label className="text-[11px] font-medium text-zinc-500">Kind</label>
        <select
          className={inputCls}
          value="subflow"
          onChange={(e) => {
            const kind = e.target.value as Stage["kind"];
            if (kind === "subflow") return;
            // Leaving subflow: drop sub-flow fields, give it valid agent/terminal shape.
            onChange({
              kind,
              flowRef: undefined,
              exits: undefined,
              ...(kind === "terminal"
                ? {
                    allowedActionIds: [],
                    requiredActionIds: [],
                    requiredSlotIds: [],
                    transitions: [],
                  }
                : {}),
            });
          }}
        >
          <option value="agent">agent</option>
          <option value="terminal">terminal (goal)</option>
          <option value="subflow">sub-flow</option>
        </select>
      </div>

      <div className="space-y-1">
        <label className="text-[11px] font-medium text-zinc-500">Connected flow</label>
        <select
          className={inputCls}
          value={stage.flowRef ?? ""}
          onChange={(e) => onChange({ flowRef: e.target.value || undefined })}
        >
          <option value="">— pick a flow —</option>
          {flows.map((f) => (
            <option key={f.id} value={f.id}>
              {f.name}
            </option>
          ))}
        </select>
        <p className="text-[10.5px] text-zinc-400">
          The flow&rsquo;s latest published version is inlined here on each run.
        </p>
      </div>

      <fieldset className="space-y-2">
        <legend className="text-[11px] font-medium text-zinc-500">
          Exits (sub-flow goal → continue here)
        </legend>
        {!stage.flowRef ? (
          <p className="text-[11px] text-zinc-400">Pick a connected flow above to map its goals.</p>
        ) : childTerminals.length === 0 ? (
          <p className="text-[11px] text-amber-600 dark:text-amber-400">
            {childFlow
              ? "The connected flow has no terminal (goal) stages to map."
              : "Loading the connected flow…"}
          </p>
        ) : (
          <>
            {exitRows.length === 0 && (
              <p className="text-[11px] text-zinc-400">
                No exits mapped — the sub-flow&rsquo;s goals will end the conversation. Map a goal
                to a stage to continue this flow afterward.
              </p>
            )}
            {exitRows.map(([subId, target], i) => {
              // Offer every child goal not already mapped, plus this row's own
              // current value (so it stays selectable). If the stored subId no
              // longer exists in the child, keep it visible + flagged.
              const options = childTerminals.filter((t) => t.id === subId || !usedSubIds.has(t.id));
              const stale = subId !== "" && !childTerminals.some((t) => t.id === subId);
              return (
                <div key={i} className="flex items-center gap-2">
                  <select
                    className={inputCls + " flex-1" + (stale ? " border-amber-400" : "")}
                    value={subId}
                    title={stale ? "This goal no longer exists in the connected flow." : undefined}
                    onChange={(e) => {
                      const next: Record<string, string> = {};
                      exitRows.forEach(([k, v], j) => {
                        next[j === i ? e.target.value : k] = v;
                      });
                      setExits(next);
                    }}
                  >
                    <option value="">— goal —</option>
                    {stale && <option value={subId}>{subId} (missing)</option>}
                    {options.map((t) => (
                      <option key={t.id} value={t.id}>
                        {goalLabel(t)}
                      </option>
                    ))}
                  </select>
                  <span className="text-zinc-400" aria-hidden>
                    →
                  </span>
                  <select
                    className={inputCls + " flex-1"}
                    value={target}
                    onChange={(e) => setExits({ ...exits, [subId]: e.target.value })}
                  >
                    <option value="">— stage —</option>
                    {targets.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.id}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="shrink-0 text-[11px] text-zinc-400 hover:text-red-600"
                    onClick={() => {
                      const next = { ...exits };
                      delete next[subId];
                      setExits(next);
                    }}
                  >
                    remove
                  </button>
                </div>
              );
            })}
            {unmappedTerminal && (
              <button
                type="button"
                className="text-[11px] text-brand-600 hover:text-brand-700 dark:text-brand-400"
                onClick={() => setExits({ ...exits, [unmappedTerminal.id]: targets[0]?.id ?? "" })}
              >
                + Add exit
              </button>
            )}
          </>
        )}
      </fieldset>

      <button
        type="button"
        className="text-[11px] text-zinc-400 hover:text-red-600"
        onClick={onDelete}
      >
        Delete stage
      </button>
    </PanelShell>
  );
}

function PanelShell({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  // Container-agnostic: the shared right rail owns width/border/resize.
  return (
    <div className="flex h-full flex-col" aria-label={title}>
      <div className="flex items-center justify-between border-b border-zinc-200 px-3.5 py-2.5 dark:border-zinc-800">
        <h2 className="text-[13px] font-semibold text-zinc-900 dark:text-zinc-100">{title}</h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close panel"
          className="text-[12px] text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200"
        >
          ✕
        </button>
      </div>
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">{children}</div>
    </div>
  );
}

type StageSection = "basics" | "behavior" | "tools" | "routing" | "goal";

/** Which section tabs a stage exposes (terminal stages hide agent-only groups). */
function stageSections(stage: Stage): { key: StageSection; label: string }[] {
  return stage.kind === "terminal"
    ? [
        { key: "basics", label: "Basics" },
        { key: "goal", label: "Goal" },
      ]
    : [
        { key: "basics", label: "Basics" },
        { key: "behavior", label: "Behavior" },
        { key: "tools", label: "Tools & Data" },
        { key: "routing", label: "Routing" },
      ];
}

/** Per-tab count badge / error dot — so authors see what's configured without opening every tab. */
function stageSectionMeta(stage: Stage): Record<StageSection, { count?: number; error?: boolean }> {
  const requiredMissing = (stage.requiredActionIds ?? []).some(
    (id) => !stage.allowedActionIds.includes(id),
  );
  return {
    basics: {},
    behavior: { count: (stage.examples?.length ?? 0) + (stage.neverSay?.length ?? 0) },
    tools: {
      count: stage.allowedActionIds.length + stage.requiredSlotIds.length,
      error: requiredMissing,
    },
    routing: { count: stage.transitions.length },
    goal: { count: stage.goal?.id ? 1 : 0 },
  };
}

/** A catalog slot this flow uses, shown read-only in the Slots tab (ADR 0030).
 *  Its definition is managed in the Slot catalog; here it's just a reference. */
function SlotRefRow({ slotKey, dto }: { slotKey: string; dto?: AgentFlowSlotDTO }) {
  return (
    <div className="flex items-center gap-2 rounded-md border border-indigo-200 bg-indigo-50/40 p-2 dark:border-indigo-500/30 dark:bg-indigo-500/10">
      <span className="shrink-0 rounded bg-indigo-100 px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-wide text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300">
        catalog
      </span>
      <span className="shrink-0 font-mono text-[12px] text-zinc-900 dark:text-zinc-100">
        {slotKey}
      </span>
      {dto ? (
        <span className="min-w-0 flex-1 truncate text-[11.5px] text-zinc-500">
          {dto.type} · {dto.prompt}
        </span>
      ) : (
        <span className="min-w-0 flex-1 truncate text-[11.5px] text-amber-600 dark:text-amber-400">
          not found in catalog — create it, or the referencing stage won&rsquo;t validate
        </span>
      )}
      <Link
        to="/agentflow/slots"
        className="shrink-0 text-[11px] text-brand-600 hover:text-brand-700 dark:text-brand-400"
      >
        manage
      </Link>
    </div>
  );
}

/** A legacy inline slot from a pre-catalog flow (ADR 0030). Read-only — the
 *  author should recreate it in the catalog to reuse it across flows. */
function LegacyLocalSlotRow({ slot }: { slot: Slot }) {
  return (
    <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50/50 p-2 dark:border-amber-500/30 dark:bg-amber-500/10">
      <span className="shrink-0 rounded bg-amber-100 px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-wide text-amber-700 dark:bg-amber-500/20 dark:text-amber-300">
        inline · legacy
      </span>
      <span className="shrink-0 font-mono text-[12px] text-zinc-900 dark:text-zinc-100">
        {slot.id}
      </span>
      <span className="min-w-0 flex-1 truncate text-[11.5px] text-zinc-500">
        {slot.type} · {slot.prompt}
      </span>
      <Link
        to="/agentflow/slots"
        className="shrink-0 text-[11px] text-brand-600 hover:text-brand-700 dark:text-brand-400"
      >
        move to catalog
      </Link>
    </div>
  );
}

/**
 * Redesigned stage editor: same right-rail, but the ~12-section scroll is split
 * into tabs (Basics / Behavior / Tools & Data / Routing / Goal). Renders a
 * height-filling column — the parent rail owns width/border/resize (ADR: see
 * docs/agentflow-stage-editor-redesign-spec.md). Number keys 1–N jump tabs.
 */
function StagePanel({
  stage,
  def,
  effectiveSlots,
  isEntry,
  onClose,
  onChange,
  onSetEntry,
  onDelete,
}: {
  stage: Stage;
  def: FlowDefinition;
  /** Slots resolved for the pickers (locals + catalog refs), ADR 0030. */
  effectiveSlots: Slot[];
  isEntry: boolean;
  onClose: () => void;
  onChange: (p: Partial<Stage>) => void;
  onSetEntry: () => void;
  onDelete: () => void;
}) {
  const [section, setSection] = useState<StageSection>("basics");
  const sections = stageSections(stage);
  const meta = stageSectionMeta(stage);
  const isTerminal = stage.kind === "terminal";
  const typeVal = stage.type ?? "custom";

  // Auto-generate the CUSTOMER side of an example from the author's agent line.
  // The author writes only the agent's line; we synthesize a realistic customer
  // message (from the stage's objective + slots) and store it on the example.
  const [genExampleIdx, setGenExampleIdx] = useState<number | null>(null);
  const generateExampleCustomer = useCallback(
    async (index: number, agentLine: string) => {
      const agent = agentLine.trim();
      if (!agent) return;
      setGenExampleIdx(index);
      try {
        const slotPrompts = stage.requiredSlotIds
          .map((id) => effectiveSlots.find((s) => s.id === id)?.prompt)
          .filter((p): p is string => Boolean(p));
        const { customer } = await api.agentflow.generateExampleCustomer({
          agent,
          stageObjective: stage.objective || undefined,
          slotPrompts,
        });
        onChange({
          examples: (stage.examples ?? []).map((x, j) => (j === index ? { ...x, customer } : x)),
        });
      } catch {
        /* best-effort — leave the customer line empty; the author can retry */
      } finally {
        setGenExampleIdx(null);
      }
    },
    [stage.requiredSlotIds, stage.objective, stage.examples, effectiveSlots, onChange],
  );

  // Snap back to a valid tab when the stage (or its kind) changes.
  useEffect(() => {
    if (!sections.some((s) => s.key === section)) setSection("basics");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage.id, stage.kind]);

  // Number keys jump between tabs — but not while typing in a field.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      if (el && /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName)) return;
      const n = Number(e.key);
      if (n >= 1 && n <= sections.length) setSection(sections[n - 1]!.key);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections.length]);

  function toggle(list: string[], id: string): string[] {
    return list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
  }

  // Sub-flow connection nodes get a dedicated, minimal editor (ADR 0030).
  if (stage.kind === "subflow") {
    return (
      <SubflowStagePanel
        stage={stage}
        def={def}
        onChange={onChange}
        onClose={onClose}
        onDelete={onDelete}
      />
    );
  }

  return (
    <div className="flex h-full flex-col">
      {/* header */}
      <div className="flex items-center justify-between border-b border-zinc-200 px-3.5 py-2.5 dark:border-zinc-800">
        <div className="flex min-w-0 items-center gap-2">
          <span
            className={
              "inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide " +
              (isTerminal
                ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300"
                : "bg-brand-50 text-brand-700 dark:bg-brand-500/15 dark:text-brand-300")
            }
          >
            <Icon name={isTerminal ? "goal" : "agent"} className="h-3 w-3" />
            {stage.kind}
          </span>
          <span className="truncate font-mono text-[13px] font-semibold text-zinc-900 dark:text-zinc-100">
            {stage.id || "(unnamed)"}
          </span>
          {isEntry && (
            <span className="shrink-0 rounded bg-brand-50 px-1.5 py-0.5 text-[9.5px] font-semibold uppercase text-brand-700 dark:bg-brand-500/10 dark:text-brand-300">
              entry
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close panel"
          className="shrink-0 text-[12px] text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200"
        >
          ✕
        </button>
      </div>

      {/* tabs */}
      <div
        role="tablist"
        aria-label="Stage sections"
        className="flex gap-1 border-b border-zinc-200 px-2.5 pt-2 dark:border-zinc-800"
      >
        {sections.map(({ key, label }) => {
          const m = meta[key];
          const active = section === key;
          return (
            <button
              key={key}
              role="tab"
              type="button"
              aria-selected={active}
              onClick={() => setSection(key)}
              className={
                "relative flex items-center gap-1.5 rounded-t-md px-2.5 py-1.5 text-[12px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500 " +
                (active
                  ? "text-zinc-900 dark:text-zinc-100"
                  : "text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-200")
              }
            >
              {label}
              {m.error ? (
                <span className="h-1.5 w-1.5 rounded-full bg-red-500" aria-label="has error" />
              ) : m.count ? (
                <span className="rounded bg-zinc-100 px-1 text-[10px] tabular-nums text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400">
                  {m.count}
                </span>
              ) : null}
              {active && <span className="absolute inset-x-0 -bottom-px h-0.5 bg-brand-500" />}
            </button>
          );
        })}
      </div>

      {/* body */}
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
        {section === "basics" && (
          <>
            <div className="space-y-1">
              <label className="text-[11px] font-medium text-zinc-500">Stage id</label>
              <input
                className={inputCls + " font-mono"}
                value={stage.id}
                onChange={(e) => onChange({ id: e.target.value })}
              />
            </div>
            <div className="space-y-1">
              <label className="text-[11px] font-medium text-zinc-500">Kind</label>
              <select
                className={inputCls}
                value={stage.kind}
                onChange={(e) => {
                  const kind = e.target.value as Stage["kind"];
                  onChange(
                    kind === "terminal"
                      ? {
                          kind,
                          allowedActionIds: [],
                          requiredActionIds: [],
                          requiredSlotIds: [],
                          transitions: [],
                        }
                      : { kind },
                  );
                }}
              >
                <option value="agent">agent</option>
                <option value="terminal">terminal (goal)</option>
              </select>
            </div>
            {stage.kind === "agent" && (
              <div className="space-y-1">
                <label className="text-[11px] font-medium text-zinc-500">Type</label>
                <select
                  className={inputCls}
                  value={typeVal}
                  title="Stage archetype. Typed stages generate a tight objective from config; 'custom' uses the free-text objective."
                  onChange={(e) => {
                    const type = e.target.value as Stage["type"];
                    const seedExamples =
                      type &&
                      type !== "custom" &&
                      (!stage.examples || stage.examples.length === 0) &&
                      STAGE_TYPE_EXAMPLE_DEFAULTS[type].length > 0;
                    onChange(
                      seedExamples
                        ? {
                            type,
                            examples: STAGE_TYPE_EXAMPLE_DEFAULTS[type].map((ex) => ({ ...ex })),
                          }
                        : { type },
                    );
                  }}
                >
                  <option value="identify_intent">Identify intent (which service)</option>
                  <option value="collect_info">Collect info</option>
                  <option value="pitch">Pitch (present value / how it works)</option>
                  <option value="action">Action (book / quote / qualify / handoff)</option>
                  <option value="wrap_up">Wrap-up (confirm + answer + close)</option>
                  <option value="custom">Custom (free-text objective)</option>
                </select>
              </div>
            )}

            {isTerminal ? (
              <ExpandableTextarea
                label="Closing message"
                value={stage.objective}
                minRows={4}
                placeholder="What the agent says to wrap up and close — e.g. “You’re all set! We’ll see you then. Anything else I can help with?”"
                hint="This is the goal stage’s closing reply. Keep it warm and final; the conversation ends here."
                onChange={(v) => onChange({ objective: v })}
              />
            ) : typeVal === "custom" ? (
              <ExpandableTextarea
                label="Objective"
                value={stage.objective}
                minRows={4}
                placeholder="What this stage should accomplish, in plain language."
                onChange={(v) => onChange({ objective: v })}
              />
            ) : (
              <div className="space-y-1">
                <label className="text-[11px] font-medium text-zinc-500">Objective</label>
                <p className="rounded-md border border-zinc-200 bg-zinc-50 px-2.5 py-2 text-[11.5px] text-zinc-500 dark:border-zinc-800 dark:bg-zinc-900/40">
                  Generated from this stage&rsquo;s type and config. Switch Type to
                  &ldquo;Custom&rdquo; to write it yourself.
                </p>
              </div>
            )}

            {stage.kind === "agent" && typeVal === "action" && (
              <div className="space-y-1">
                <label className="text-[11px] font-medium text-zinc-500">Goal type</label>
                <select
                  className={inputCls}
                  value={stage.goalType ?? ""}
                  title="Which platform goal this stage performs. 'Handoff' routes to a person."
                  onChange={(e) =>
                    onChange({
                      goalType: e.target.value ? (e.target.value as Stage["goalType"]) : undefined,
                    })
                  }
                >
                  <option value="">— none —</option>
                  <option value="book">Book</option>
                  <option value="quote">Quote</option>
                  <option value="qualify">Qualify</option>
                  <option value="handoff">Handoff (to a person)</option>
                  <option value="custom">Custom</option>
                </select>
              </div>
            )}

            <div className="pt-1">
              {isEntry ? (
                <span className="text-[11px] font-medium text-brand-600 dark:text-brand-400">
                  Entry stage
                </span>
              ) : (
                <button
                  type="button"
                  className="text-[11px] text-brand-600 hover:text-brand-700 dark:text-brand-400"
                  onClick={onSetEntry}
                >
                  Set as entry stage
                </button>
              )}
            </div>
          </>
        )}

        {section === "behavior" && stage.kind === "agent" && (
          <>
            <ExpandableTextarea
              label="Boundaries (optional hard rules)"
              value={stage.boundaries ?? ""}
              minRows={3}
              placeholder="e.g. Don't discuss pricing here. Don't promise timelines."
              hint="Stage-scoped “do NOT” rules, applied with precedence over the persona. The agent already auto-scopes to this stage’s required slots — use this only for rules slots can’t express."
              onChange={(v) => onChange({ boundaries: v || undefined })}
            />

            {typeVal === "collect_info" ? (
              <p className="rounded-md border border-zinc-200 bg-zinc-50 px-2.5 py-2 text-[11px] text-zinc-500 dark:border-zinc-800 dark:bg-zinc-900/40">
                Examples for the fields this stage collects live on each field in the{" "}
                <span className="font-medium">Slots</span> tab (&ldquo;How to ask&rdquo;) — that way
                the phrasing follows the field wherever it&rsquo;s used.
              </p>
            ) : (
              <fieldset className="space-y-2">
                <legend className="text-[11px] font-medium text-zinc-500">
                  Example turns (optional)
                </legend>
                {(stage.examples ?? []).map((ex, i) => (
                  <div
                    key={i}
                    className="space-y-1.5 rounded-md border border-zinc-200 p-2 dark:border-zinc-800"
                  >
                    <input
                      className={inputCls}
                      placeholder="Agent replies… (e.g. Got it — what's the best callback number?)"
                      value={ex.agent}
                      onChange={(e) =>
                        onChange({
                          examples: (stage.examples ?? []).map((x, j) =>
                            // Editing the agent line invalidates the generated
                            // customer line — clear it so it regenerates on blur.
                            j === i ? { ...x, agent: e.target.value, customer: undefined } : x,
                          ),
                        })
                      }
                      onBlur={(e) => {
                        if (e.target.value.trim() && !ex.customer) {
                          void generateExampleCustomer(i, e.target.value);
                        }
                      }}
                    />
                    {/* Platform-generated customer side (ADR 0029). */}
                    {genExampleIdx === i ? (
                      <p className="text-[11px] text-zinc-400">Generating customer message…</p>
                    ) : ex.customer ? (
                      <div className="flex items-start gap-1.5 text-[11px] text-zinc-500">
                        <span className="shrink-0 rounded bg-zinc-100 px-1 py-0.5 text-[9.5px] font-medium uppercase tracking-wide text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400">
                          customer · auto
                        </span>
                        <span className="min-w-0 flex-1">{ex.customer}</span>
                        <button
                          type="button"
                          className="shrink-0 text-brand-600 hover:text-brand-700 dark:text-brand-400"
                          onClick={() => void generateExampleCustomer(i, ex.agent)}
                        >
                          redo
                        </button>
                      </div>
                    ) : ex.agent.trim() ? (
                      <p className="text-[11px] text-zinc-400">
                        A customer message will be generated automatically.
                      </p>
                    ) : null}
                    {/* Platform-generated counter-example (do/don't), filled in at
                        publish. Display-only — regenerated when the agent line changes. */}
                    {ex.counterExample ? (
                      <div className="flex items-start gap-1.5 text-[11px] text-zinc-500">
                        <span className="shrink-0 rounded bg-red-100 px-1 py-0.5 text-[9.5px] font-medium uppercase tracking-wide text-red-700 dark:bg-red-900/30 dark:text-red-300">
                          avoid · auto
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="line-through decoration-red-400/60">
                            {ex.counterExample.agent}
                          </span>
                          {ex.counterExample.reason ? (
                            <span className="mt-0.5 block text-zinc-400">
                              why: {ex.counterExample.reason}
                            </span>
                          ) : null}
                        </span>
                      </div>
                    ) : null}
                    <button
                      type="button"
                      className="text-[11px] text-zinc-400 hover:text-red-600"
                      onClick={() =>
                        onChange({ examples: (stage.examples ?? []).filter((_, j) => j !== i) })
                      }
                    >
                      remove
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="text-[11px] text-brand-600 hover:text-brand-700 dark:text-brand-400"
                  onClick={() => onChange({ examples: [...(stage.examples ?? []), { agent: "" }] })}
                >
                  + Add example
                </button>
                <p className="text-[11px] text-zinc-500">
                  Write only the agent&rsquo;s line — we generate a matching customer message.
                  Teaches HOW the agent should speak; style only, not real facts. On publish we also
                  add an &ldquo;avoid&rdquo; counter-example showing what not to do.
                </p>
              </fieldset>
            )}

            <div className="space-y-1">
              <label className="text-[11px] font-medium text-zinc-500">
                Never say (this stage)
              </label>
              <ChipsInput
                value={stage.neverSay ?? []}
                placeholder="Phrase, press Enter"
                title="Phrases the agent must never say in this stage — merged with the flow-wide list."
                onChange={(v) => onChange({ neverSay: v.length > 0 ? v : undefined })}
              />
            </div>
          </>
        )}

        {section === "tools" && stage.kind === "agent" && (
          <>
            <fieldset className="space-y-1">
              <legend className="text-[11px] font-medium text-zinc-500">Allowed tools</legend>
              {def.actions.length === 0 && <Empty>No actions defined.</Empty>}
              {def.actions.map((a) => (
                <label
                  key={a.id}
                  className="flex items-center gap-1.5 text-[12px] text-zinc-700 dark:text-zinc-300"
                >
                  <input
                    type="checkbox"
                    checked={stage.allowedActionIds.includes(a.id)}
                    onChange={() => {
                      const allowedActionIds = toggle(stage.allowedActionIds, a.id);
                      const requiredActionIds = (stage.requiredActionIds ?? []).filter((id) =>
                        allowedActionIds.includes(id),
                      );
                      onChange({ allowedActionIds, requiredActionIds });
                    }}
                  />
                  <span className="font-mono">{a.id}</span>
                  <span className="text-zinc-400">({a.toolId})</span>
                </label>
              ))}
            </fieldset>
            <fieldset className="space-y-1">
              <legend className="text-[11px] font-medium text-zinc-500">
                Required tools (must invoke)
              </legend>
              {stage.allowedActionIds.length === 0 ? (
                <Empty>Allow a tool above to require it here.</Empty>
              ) : (
                stage.allowedActionIds.map((aid) => {
                  const a = def.actions.find((x) => x.id === aid);
                  return (
                    <label
                      key={aid}
                      className="flex items-center gap-1.5 text-[12px] text-zinc-700 dark:text-zinc-300"
                    >
                      <input
                        type="checkbox"
                        checked={(stage.requiredActionIds ?? []).includes(aid)}
                        onChange={() =>
                          onChange({
                            requiredActionIds: toggle(stage.requiredActionIds ?? [], aid),
                          })
                        }
                      />
                      <span className="font-mono">{aid}</span>
                      {a && <span className="text-zinc-400">({a.toolId})</span>}
                    </label>
                  );
                })
              )}
              <p className="text-[11px] text-zinc-500">
                The agent must actually call these before this stage can complete — it can’t claim
                success (e.g. “you’re booked”) without invoking the tool.
              </p>
            </fieldset>
            <fieldset className="space-y-1">
              <legend className="text-[11px] font-medium text-zinc-500">Required slots</legend>
              {effectiveSlots.length === 0 && <Empty>No slots defined.</Empty>}
              {effectiveSlots.map((s) => (
                <label
                  key={s.id}
                  className="flex items-center gap-1.5 text-[12px] text-zinc-700 dark:text-zinc-300"
                >
                  <input
                    type="checkbox"
                    checked={stage.requiredSlotIds.includes(s.id)}
                    onChange={() =>
                      onChange({ requiredSlotIds: toggle(stage.requiredSlotIds, s.id) })
                    }
                  />
                  <span className="font-mono">{s.id}</span>
                </label>
              ))}
            </fieldset>
          </>
        )}

        {section === "routing" && stage.kind === "agent" && (
          <>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-medium text-zinc-500">Transitions (go to…)</span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 text-[11px] text-brand-600 hover:text-brand-700 dark:text-brand-400"
                    onClick={() => {
                      const target = def.stages.find((s) => s.id !== stage.id)?.id ?? stage.id;
                      const next: StageTransition = stage.allowedActionIds.length
                        ? {
                            on: "action_success",
                            actionId: stage.allowedActionIds[0],
                            toStage: target,
                          }
                        : stage.requiredSlotIds.length
                          ? { on: "slots_complete", toStage: target }
                          : { on: "no_progress", toStage: target };
                      onChange({ transitions: [...stage.transitions, next] });
                    }}
                  >
                    <Icon name="plus" className="h-3 w-3" />
                    Add
                  </button>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 text-[11px] font-medium text-indigo-600 hover:text-indigo-700 dark:text-indigo-400"
                    title="Split this stage to different stages based on a slot's value (e.g. home vs business)."
                    onClick={() => {
                      const target = def.stages.find((s) => s.id !== stage.id)?.id ?? stage.id;
                      const next: StageTransition = {
                        on: "slot_condition",
                        condition: { slotId: effectiveSlots[0]?.id ?? "", op: "exists" },
                        toStage: target,
                      };
                      onChange({ transitions: [...stage.transitions, next] });
                    }}
                  >
                    <Icon name="plus" className="h-3 w-3" />
                    Branch
                  </button>
                </div>
              </div>
              {stage.transitions.length === 0 && (
                <Empty>No transitions — this stage exits via the no-progress budget.</Empty>
              )}
              {stage.transitions.map((t, i) => {
                const targets = def.stages.filter((s) => s.id !== stage.id);
                const rowLabel = "w-11 shrink-0 text-[10px] uppercase tracking-wide text-zinc-400";
                return (
                  <div
                    key={i}
                    className="space-y-1.5 rounded-md border border-zinc-200 p-2 dark:border-zinc-800"
                  >
                    <div className="flex items-center gap-1.5">
                      <span className={rowLabel}>When</span>
                      <select
                        className={inputCls + " flex-1"}
                        value={t.on}
                        onChange={(e) => {
                          const on = e.target.value as StageTransitionTrigger;
                          onChange({
                            transitions: stage.transitions.map((x, j) =>
                              j === i
                                ? {
                                    ...x,
                                    on,
                                    actionId: on === "action_success" ? x.actionId : undefined,
                                    condition:
                                      on === "slot_condition"
                                        ? (x.condition ?? {
                                            slotId: effectiveSlots[0]?.id ?? "",
                                            op: "exists",
                                          })
                                        : undefined,
                                  }
                                : x,
                            ),
                          });
                        }}
                      >
                        {STAGE_TRANSITION_TRIGGERS.map((tr) => (
                          <option key={tr} value={tr}>
                            {tr === "slot_condition" ? "when slot value…" : tr}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        aria-label="Delete transition"
                        className="shrink-0 text-zinc-400 hover:text-red-600"
                        onClick={() =>
                          onChange({ transitions: stage.transitions.filter((_, j) => j !== i) })
                        }
                      >
                        ✕
                      </button>
                    </div>
                    {t.on === "action_success" && (
                      <div className="flex items-center gap-1.5">
                        <span className={rowLabel}>Tool</span>
                        <select
                          className={inputCls + " flex-1"}
                          value={t.actionId ?? ""}
                          onChange={(e) =>
                            onChange({
                              transitions: stage.transitions.map((x, j) =>
                                j === i ? { ...x, actionId: e.target.value } : x,
                              ),
                            })
                          }
                        >
                          <option value="">(pick a tool)</option>
                          {stage.allowedActionIds.map((aid) => (
                            <option key={aid} value={aid}>
                              {aid}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    {t.on === "slot_condition" && (
                      <ConditionEditor
                        condition={t.condition}
                        slots={effectiveSlots}
                        onChange={(condition) =>
                          onChange({
                            transitions: stage.transitions.map((x, j) =>
                              j === i ? { ...x, condition } : x,
                            ),
                          })
                        }
                      />
                    )}
                    <div className="flex items-center gap-1.5">
                      <span className={rowLabel}>Go to</span>
                      <select
                        className={inputCls + " flex-1"}
                        value={t.toStage}
                        onChange={(e) =>
                          onChange({
                            transitions: stage.transitions.map((x, j) =>
                              j === i ? { ...x, toStage: e.target.value } : x,
                            ),
                          })
                        }
                      >
                        {targets.length === 0 && <option value={t.toStage}>{t.toStage}</option>}
                        {targets.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.id}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="space-y-1 border-t border-zinc-200 pt-3 dark:border-zinc-800">
              <label className="text-[11px] font-medium text-zinc-500">
                No-progress budget (turns)
              </label>
              <input
                type="number"
                min={1}
                className={inputCls + " max-w-[110px]"}
                value={stage.maxTurns ?? ""}
                placeholder={String(def.defaultStageMaxTurns)}
                onChange={(e) =>
                  onChange({ maxTurns: e.target.value ? Number(e.target.value) : undefined })
                }
              />
            </div>
          </>
        )}

        {section === "goal" && stage.kind === "terminal" && (
          <>
            <div className="space-y-1">
              <label className="text-[11px] font-medium text-zinc-500">Goal type</label>
              <select
                className={inputCls}
                value={stage.goalType ?? ""}
                title="Which platform goal this stage performs. 'Handoff' routes to a person."
                onChange={(e) =>
                  onChange({
                    goalType: e.target.value ? (e.target.value as Stage["goalType"]) : undefined,
                  })
                }
              >
                <option value="">— none —</option>
                <option value="book">Book</option>
                <option value="quote">Quote</option>
                <option value="qualify">Qualify</option>
                <option value="handoff">Handoff (to a person)</option>
                <option value="custom">Custom</option>
              </select>
            </div>

            <div className="space-y-2 rounded-md border border-zinc-200 p-2.5 dark:border-zinc-800">
              <div className="text-[11px] font-medium text-zinc-500">Goal (recorded on reach)</div>
              <div className="space-y-1">
                <label className="text-[11px] text-zinc-500">Goal id</label>
                <input
                  className={inputCls}
                  placeholder={stage.id}
                  value={stage.goal?.id ?? ""}
                  onChange={(e) => {
                    const id = e.target.value;
                    onChange({ goal: id.trim() ? { ...stage.goal, id } : undefined });
                  }}
                />
              </div>
              <div className="space-y-1">
                <label className="text-[11px] text-zinc-500">Label (optional)</label>
                <input
                  className={inputCls}
                  disabled={!stage.goal?.id}
                  value={stage.goal?.label ?? ""}
                  onChange={(e) =>
                    stage.goal?.id &&
                    onChange({
                      goal: {
                        ...stage.goal,
                        id: stage.goal.id,
                        label: e.target.value || undefined,
                      },
                    })
                  }
                />
              </div>
              <div className="space-y-1">
                <label className="text-[11px] text-zinc-500">Also write a typed record</label>
                <select
                  className={inputCls}
                  disabled={!stage.goal?.id}
                  value={stage.goal?.capture ?? "none"}
                  onChange={(e) =>
                    stage.goal?.id &&
                    onChange({
                      goal: {
                        ...stage.goal,
                        id: stage.goal.id,
                        capture: e.target.value as "none" | "lead",
                      },
                    })
                  }
                >
                  <option value="none">None (generic outcome only)</option>
                  <option value="lead">Lead</option>
                </select>
              </div>
              <label className="flex items-center gap-1.5 text-[12px] text-zinc-700 dark:text-zinc-300">
                <input
                  type="checkbox"
                  disabled={!stage.goal?.id}
                  checked={stage.goal?.emitEvent === true}
                  onChange={(e) =>
                    stage.goal?.id &&
                    onChange({
                      goal: {
                        ...stage.goal,
                        id: stage.goal.id,
                        emitEvent: e.target.checked || undefined,
                      },
                    })
                  }
                />
                Post webhook event when reached
              </label>
              <p className="text-[10.5px] leading-snug text-zinc-400">
                Reaching this stage records a goal outcome (goal id + a snapshot of all slots).
                “Also write a typed record” additionally writes that entity (e.g. a Lead). “Post
                webhook event” sends an <span className="font-mono">agentflow.goal_reached</span>{" "}
                event to your configured action webhook. Defaults to the stage id if left blank.
              </p>
            </div>
          </>
        )}
      </div>

      {/* footer */}
      <div className="flex items-center justify-between border-t border-zinc-200 px-3.5 py-2 dark:border-zinc-800">
        {isEntry ? (
          <span className="text-[11px] font-medium text-brand-600 dark:text-brand-400">
            Entry stage
          </span>
        ) : (
          <button
            type="button"
            className="text-[11px] text-brand-600 hover:text-brand-700 dark:text-brand-400"
            onClick={onSetEntry}
          >
            Set as entry stage
          </button>
        )}
        <button
          type="button"
          className="inline-flex items-center gap-1.5 text-[11px] text-red-500 hover:text-red-700"
          onClick={onDelete}
        >
          <Icon name="trash" className="h-3.5 w-3.5" />
          Delete stage
        </button>
      </div>
    </div>
  );
}

function TransitionPanel({
  transition,
  sourceStage,
  stages,
  effectiveSlots,
  onClose,
  onChange,
  onDelete,
}: {
  transition: StageTransition;
  sourceStage: Stage | undefined;
  /** All stages in the flow — the "go to stage" target options. */
  stages: Stage[];
  /** Slots resolved for the condition picker (locals + catalog refs), ADR 0030. */
  effectiveSlots: Slot[];
  onClose: () => void;
  onChange: (p: Partial<StageTransition>) => void;
  onDelete: () => void;
}) {
  return (
    <PanelShell title="Transition" onClose={onClose}>
      <p className="text-[11px] text-zinc-500">
        From <span className="font-mono">{sourceStage?.id ?? "?"}</span> →{" "}
        <span className="font-mono">{transition.toStage}</span>
      </p>
      <div className="space-y-1">
        <label className="text-[11px] font-medium text-zinc-500">Trigger</label>
        <select
          className={inputCls}
          value={transition.on}
          onChange={(e) => {
            const on = e.target.value as StageTransitionTrigger;
            if (on === "slot_condition" && !transition.condition) {
              onChange({
                on,
                condition: { slotId: effectiveSlots[0]?.id ?? "", op: "exists" },
              });
            } else {
              onChange({ on });
            }
          }}
        >
          {STAGE_TRANSITION_TRIGGERS.map((tr) => (
            <option key={tr} value={tr}>
              {tr === "slot_condition" ? "when slot value…" : tr}
            </option>
          ))}
        </select>
      </div>
      {transition.on === "action_success" && (
        <div className="space-y-1">
          <label className="text-[11px] font-medium text-zinc-500">When action succeeds</label>
          <select
            className={inputCls}
            value={transition.actionId ?? ""}
            onChange={(e) => onChange({ actionId: e.target.value })}
          >
            <option value="">(pick action)</option>
            {(sourceStage?.allowedActionIds ?? []).map((aid) => (
              <option key={aid} value={aid}>
                {aid}
              </option>
            ))}
          </select>
        </div>
      )}
      {transition.on === "slot_condition" && (
        <ConditionEditor
          condition={transition.condition}
          slots={effectiveSlots}
          onChange={(condition) => onChange({ condition })}
        />
      )}
      <div className="space-y-1">
        <label className="text-[11px] font-medium text-zinc-500">Go to stage</label>
        <select
          className={inputCls}
          value={transition.toStage}
          onChange={(e) => onChange({ toStage: e.target.value })}
        >
          {stages.map((s) => (
            <option key={s.id} value={s.id}>
              {s.id}
            </option>
          ))}
        </select>
      </div>
      <button
        type="button"
        className="text-[11px] text-red-500 hover:text-red-700"
        onClick={onDelete}
      >
        Delete transition
      </button>
    </PanelShell>
  );
}

/** Editor for a slot_condition (ADR 0027): slot + operator + value(s). */
function ConditionEditor({
  condition,
  slots,
  onChange,
}: {
  condition: SlotCondition | undefined;
  slots: Slot[];
  onChange: (c: SlotCondition) => void;
}) {
  const c: SlotCondition = condition ?? { slotId: slots[0]?.id ?? "", op: "exists" };
  const slot = slots.find((s) => s.id === c.slotId);
  const slotType = slot?.type ?? "text";
  const options = slot?.options ?? [];
  const selectedList = Array.isArray(c.value) ? c.value : [];
  const valStr = typeof c.value === "string" ? c.value : "";
  const isNumericOp = c.op === "gt" || c.op === "gte" || c.op === "lt" || c.op === "lte";

  const opLabel: Record<SlotConditionOp, string> = {
    eq: "is",
    in: "is one of",
    exists: "is provided",
    gt: "greater than",
    gte: "at least",
    lt: "less than",
    lte: "at most",
  };
  // Operators offered depend on the slot's type: numeric comparisons for
  // number slots, set membership for text/choice slots.
  const opsFor = (t: Slot["type"] | undefined): SlotConditionOp[] =>
    t === "number" ? ["eq", "gt", "gte", "lt", "lte", "exists"] : ["eq", "in", "exists"];
  const availableOps = opsFor(slotType);

  // Reset value to a shape valid for the given op when slot/op changes.
  const valueForOp = (op: SlotConditionOp): Partial<SlotCondition> =>
    op === "exists" ? {} : { value: op === "in" ? [] : "" };

  return (
    <div className="space-y-2 rounded-md border border-zinc-200 p-2.5 dark:border-zinc-800">
      <div className="space-y-1">
        <label className="text-[11px] font-medium text-zinc-500">When slot</label>
        <select
          className={inputCls}
          value={c.slotId}
          onChange={(e) => {
            const slotId = e.target.value;
            const nextType = slots.find((s) => s.id === slotId)?.type;
            // Keep the op if the new slot supports it, else fall back to exists.
            const op = opsFor(nextType).includes(c.op) ? c.op : "exists";
            onChange({ slotId, op, ...valueForOp(op) });
          }}
        >
          {slots.length === 0 && <option value="">(no slots — add one first)</option>}
          {slots.map((s) => (
            <option key={s.id} value={s.id}>
              {s.id}
            </option>
          ))}
        </select>
      </div>
      <div className="space-y-1">
        <label className="text-[11px] font-medium text-zinc-500">Condition</label>
        <select
          className={inputCls}
          value={c.op}
          onChange={(e) => {
            const op = e.target.value as SlotConditionOp;
            onChange({ slotId: c.slotId, op, ...valueForOp(op) });
          }}
        >
          {availableOps.map((op) => (
            <option key={op} value={op}>
              {opLabel[op]}
            </option>
          ))}
        </select>
      </div>

      {/* Numeric comparison value. */}
      {isNumericOp && (
        <input
          type="number"
          className={inputCls}
          value={valStr}
          placeholder="number"
          onChange={(e) => onChange({ slotId: c.slotId, op: c.op, value: e.target.value })}
        />
      )}

      {c.op === "eq" &&
        (slotType === "choice" && options.length > 0 ? (
          <select
            className={inputCls}
            value={valStr}
            onChange={(e) => onChange({ slotId: c.slotId, op: "eq", value: e.target.value })}
          >
            <option value="">(pick a value)</option>
            {options.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        ) : (
          <input
            type={slotType === "number" ? "number" : "text"}
            className={inputCls}
            value={valStr}
            placeholder="value to match"
            onChange={(e) => onChange({ slotId: c.slotId, op: "eq", value: e.target.value })}
          />
        ))}

      {c.op === "in" &&
        (options.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {options.map((o) => {
              const on = selectedList.includes(o);
              return (
                <button
                  key={o}
                  type="button"
                  onClick={() =>
                    onChange({
                      slotId: c.slotId,
                      op: "in",
                      value: on ? selectedList.filter((x) => x !== o) : [...selectedList, o],
                    })
                  }
                  className={
                    "rounded-full border px-2 py-0.5 text-[11px] " +
                    (on
                      ? "border-brand-500 bg-brand-50 text-brand-700 dark:bg-brand-500/15 dark:text-brand-300"
                      : "border-zinc-300 text-zinc-600 dark:border-zinc-700 dark:text-zinc-300")
                  }
                >
                  {o}
                </button>
              );
            })}
          </div>
        ) : (
          <ChipsInput
            value={selectedList}
            placeholder="Type a value, press Enter"
            onChange={(items) => onChange({ slotId: c.slotId, op: "in", value: items })}
          />
        ))}

      {c.op === "exists" && (
        <p className="text-[11px] text-zinc-500">Branches as soon as this slot has any value.</p>
      )}

      {slotType === "text" && (c.op === "eq" || c.op === "in") && (
        <p className="text-[11px] text-amber-600 dark:text-amber-400">
          Tip: set this slot’s type to “Choices” on the Slots tab so matching stays reliable.
        </p>
      )}
    </div>
  );
}

function SectionCard({
  title,
  hint,
  onAdd,
  children,
}: {
  title: string;
  hint: string;
  onAdd?: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-[13px] font-semibold text-zinc-900 dark:text-zinc-100">{title}</h2>
          <p className="text-[11px] text-zinc-500">{hint}</p>
        </div>
        {onAdd && (
          <Button size="sm" onClick={onAdd}>
            + Add
          </Button>
        )}
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <p className="text-[12px] italic text-zinc-400">{children}</p>;
}

/**
 * Guardrails tab — edits the two runtime-compiled guardrails on the flow:
 * `never_say` (phrases) and `stay_on_topic` (topics). Each is a single entry in
 * `def.guardrails`; clearing all chips removes the entry (matching the runtime,
 * which only compiles non-empty guardrails). `must_confirm_before` is set
 * per-action in the Tools tab, not here.
 */
function GuardrailsEditor({
  def,
  onPatch,
}: {
  def: FlowDefinition;
  onPatch: (patch: Partial<FlowDefinition>) => void;
}) {
  const guardrails = def.guardrails ?? [];
  const neverSay = guardrails.find((g) => g.kind === "never_say");
  const stayOnTopic = guardrails.find((g) => g.kind === "stay_on_topic");
  const phrases = neverSay?.kind === "never_say" ? neverSay.phrases : [];
  const topics = stayOnTopic?.kind === "stay_on_topic" ? stayOnTopic.topics : [];

  function setChips(kind: "never_say" | "stay_on_topic", values: string[]) {
    const others = guardrails.filter((g) => g.kind !== kind);
    if (values.length === 0) {
      onPatch({ guardrails: others });
      return;
    }
    const entry =
      kind === "never_say"
        ? { kind: "never_say" as const, id: "never_say", phrases: values }
        : { kind: "stay_on_topic" as const, id: "stay_on_topic", topics: values };
    onPatch({ guardrails: [...others, entry] });
  }

  return (
    <div className="max-w-2xl space-y-6">
      <div className="space-y-2">
        <div>
          <h2 className="text-[13px] font-semibold text-zinc-900 dark:text-zinc-100">Never say</h2>
          <p className="text-[11px] text-zinc-500">
            Phrases the agent must never say (compliance lines, claims, competitor names). Matching
            replies are scrubbed before they're sent.
          </p>
        </div>
        <ChipsInput
          value={phrases}
          placeholder="Type a phrase, press Enter"
          title="The agent's replies are filtered so these never appear."
          onChange={(v) => setChips("never_say", v)}
        />
      </div>

      <div className="space-y-2">
        <div>
          <h2 className="text-[13px] font-semibold text-zinc-900 dark:text-zinc-100">
            Stay on topic
          </h2>
          <p className="text-[11px] text-zinc-500">
            Topics the agent will engage with. Leave empty to allow anything; otherwise off-topic
            messages get a polite redirect.
          </p>
        </div>
        <ChipsInput
          value={topics}
          placeholder="Type a topic, press Enter"
          title="Messages unrelated to these topics are deflected."
          onChange={(v) => setChips("stay_on_topic", v)}
        />
      </div>

      <p className="text-[11px] text-zinc-500">
        Need approval before an action runs? Set{" "}
        <span className="font-medium">requires confirmation</span> on the action in the{" "}
        <span className="font-medium">Tools</span> tab.
      </p>
    </div>
  );
}

/**
 * Tools tab — manage the flow's `actions` (the tool instances + config) inside
 * the v2 canvas. Reuses the v1 per-tool config editors (`ActionConfigEditor`,
 * `defaultsForTool`). Deleting an action cleans up every stage's
 * `allowedActionIds` and any `action_success` transitions that referenced it,
 * so the graph can't be left referencing a deleted tool.
 */
function ActionsManager({
  def,
  tools,
  onPatch,
}: {
  def: FlowDefinition;
  tools: ToolDefinitionDTO[] | null;
  onPatch: (p: Partial<FlowDefinition>) => void;
}) {
  const [addToolId, setAddToolId] = useState<string>("");
  // Expansion tracked by index (not id) so renaming an action doesn't remount
  // the card and steal focus mid-type.
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);

  function setAction(id: string, p: Partial<Action>) {
    onPatch({ actions: def.actions.map((a) => (a.id === id ? { ...a, ...p } : a)) });
  }

  // Renaming an action must retarget references in stages (allowedActionIds +
  // action_success transitions) or they go dangling and the flow won't save.
  function renameAction(oldId: string, newId: string) {
    onPatch({
      actions: def.actions.map((a) => (a.id === oldId ? { ...a, id: newId } : a)),
      stages: def.stages.map((s) => ({
        ...s,
        allowedActionIds: s.allowedActionIds.map((aid) => (aid === oldId ? newId : aid)),
        transitions: s.transitions.map((t) =>
          t.on === "action_success" && t.actionId === oldId ? { ...t, actionId: newId } : t,
        ),
      })),
    });
  }

  function addAction() {
    if (!addToolId) return;
    const tool = tools?.find((t) => t.id === addToolId);
    const existing = new Set(def.actions.map((a) => a.id));
    let id = addToolId;
    let n = 1;
    while (existing.has(id)) {
      n += 1;
      id = `${addToolId}_${n}`;
    }
    const action: Action = {
      id,
      toolId: addToolId as Action["toolId"],
      config: defaultsForTool(tool),
      requiresConfirmation: false,
    };
    setExpandedIdx(def.actions.length); // auto-expand the newly added tool
    onPatch({ actions: [...def.actions, action] });
    setAddToolId("");
  }

  function removeAction(id: string) {
    setExpandedIdx(null);
    onPatch({
      actions: def.actions.filter((a) => a.id !== id),
      stages: def.stages.map((s) => ({
        ...s,
        allowedActionIds: s.allowedActionIds.filter((aid) => aid !== id),
        transitions: s.transitions.filter((t) => !(t.on === "action_success" && t.actionId === id)),
      })),
    });
  }

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">Tools</h2>
        <p className="mt-0.5 text-[12.5px] text-zinc-500">
          The actions this flow can take. Stages gate which tools they may use.
        </p>
      </div>

      {/* Add-tool control — wraps instead of squeezing on a narrow workspace. */}
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-dashed border-zinc-300 bg-zinc-50 px-3 py-2.5 dark:border-zinc-700 dark:bg-zinc-900/30">
        <label
          htmlFor="add-tool"
          className="text-[12.5px] font-medium text-zinc-600 dark:text-zinc-400"
        >
          Add a tool
        </label>
        <select
          id="add-tool"
          className={inputCls + " w-auto min-w-[200px] flex-1"}
          value={addToolId}
          onChange={(e) => setAddToolId(e.target.value)}
        >
          <option value="">Choose a tool…</option>
          {(tools ?? []).map((t) => (
            <option key={t.id} value={t.id}>
              {t.name ?? t.id}
            </option>
          ))}
        </select>
        <Button size="sm" onClick={addAction} disabled={!addToolId}>
          + Add
        </Button>
      </div>

      {def.actions.length === 0 && (
        <div className="rounded-lg border border-dashed border-zinc-200 px-4 py-8 text-center dark:border-zinc-800">
          <p className="text-[13px] font-medium text-zinc-700 dark:text-zinc-300">No tools yet</p>
          <p className="mt-1 text-[12px] text-zinc-500">
            Add a tool above, then gate it per stage on the canvas.
          </p>
        </div>
      )}

      <div className="space-y-2">
        {def.actions.map((action, i) => {
          const tool = tools?.find((t) => t.id === action.toolId);
          const open = expandedIdx === i;
          return (
            <div
              key={i}
              className="overflow-hidden rounded-lg border border-zinc-200 dark:border-zinc-800"
            >
              <button
                type="button"
                aria-expanded={open}
                onClick={() => setExpandedIdx(open ? null : i)}
                className="flex min-h-11 w-full items-center gap-2 px-3 py-2.5 text-left transition-colors hover:bg-zinc-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500 dark:hover:bg-zinc-800/40"
              >
                <svg
                  viewBox="0 0 16 16"
                  aria-hidden="true"
                  className={
                    "h-3 w-3 shrink-0 text-zinc-400 transition-transform duration-150 " +
                    (open ? "rotate-90" : "")
                  }
                >
                  <path d="M6 4l4 4-4 4" fill="none" stroke="currentColor" strokeWidth="1.5" />
                </svg>
                <Icon name={action.toolId} className="h-4 w-4 shrink-0 text-zinc-500" />
                <span className="font-mono text-[13px] font-medium text-zinc-900 dark:text-zinc-100">
                  {action.id}
                </span>
                <span className="rounded bg-zinc-100 px-1.5 py-0.5 text-[11px] text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400">
                  {tool?.name ?? action.toolId}
                </span>
                <span className="ml-auto truncate pl-2 text-[12px] text-zinc-500">
                  {configSummary(action)}
                </span>
              </button>

              {open && (
                <div className="space-y-3 border-t border-zinc-200 p-3 dark:border-zinc-800">
                  <div className="flex items-center gap-2">
                    <label className="text-[11px] font-medium text-zinc-500">Action id</label>
                    <input
                      className={inputCls + " max-w-[200px] font-mono"}
                      value={action.id}
                      aria-label="Action id"
                      onChange={(e) => renameAction(action.id, e.target.value)}
                    />
                  </div>

                  <label className="flex items-center gap-2 text-[12px] text-zinc-700 dark:text-zinc-300">
                    <input
                      type="checkbox"
                      checked={action.requiresConfirmation}
                      onChange={(e) =>
                        setAction(action.id, { requiresConfirmation: e.target.checked })
                      }
                    />
                    Require confirmation before running
                  </label>

                  <label className="flex items-start gap-2 text-[12px] text-zinc-700 dark:text-zinc-300">
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={action.emitEvent ?? false}
                      onChange={(e) => setAction(action.id, { emitEvent: e.target.checked })}
                    />
                    <span>
                      Emit event for this tool
                      <span className="mt-0.5 block text-[10.5px] leading-snug text-zinc-400">
                        POST a signed <span className="font-mono">agentflow.tool_call</span> event
                        (input + outcome + conversation context) when it runs. Requires the webhook
                        enabled in Workspace → Settings → Webhook events. Sandbox runs send too,
                        marked <span className="font-mono">channel: "sandbox"</span>.
                      </span>
                    </span>
                  </label>

                  <ActionConfigEditor
                    toolId={action.toolId}
                    config={action.config}
                    onPatch={(p) => setAction(action.id, { config: { ...action.config, ...p } })}
                  />

                  <div className="pt-1">
                    <button
                      type="button"
                      className="inline-flex items-center gap-1.5 text-[12px] text-red-500 hover:text-red-700"
                      onClick={() => removeAction(action.id)}
                    >
                      <Icon name="trash" className="h-3.5 w-3.5" />
                      Delete tool
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** One-line summary of an action's config for the collapsed accordion header. */
function configSummary(action: Action): string {
  const c = action.config as Record<string, unknown>;
  switch (action.toolId) {
    case "book_appointment": {
      const n = Array.isArray(c.serviceTypes) ? c.serviceTypes.length : 0;
      const dur = typeof c.defaultDurationMin === "number" ? c.defaultDurationMin : 60;
      return `${n} service type${n === 1 ? "" : "s"} · ${dur} min`;
    }
    case "qualify_lead": {
      const n = Array.isArray(c.questions) ? c.questions.length : 0;
      return `${n} question${n === 1 ? "" : "s"}`;
    }
    case "faq_lookup": {
      const n = Array.isArray(c.tags) ? c.tags.length : 0;
      return n > 0 ? `${n} tag${n === 1 ? "" : "s"}` : "whole knowledge base";
    }
    case "escalate_to_human": {
      const m = typeof c.message === "string" ? c.message : "";
      return m ? (m.length > 44 ? m.slice(0, 44) + "…" : m) : "no message set";
    }
    default:
      return "";
  }
}

// ---------------------------------------------------------------------------
// Icon set + Tooltip — hand-rolled (repo has no icon lib). Stroke-based,
// 24×24, currentColor. Used to make the v2 canvas icon-forward.
// ---------------------------------------------------------------------------

type V2IconName =
  | "canvas"
  | "tools"
  | "slots"
  | "persona"
  | "greeting"
  | "guardrails"
  | "channels"
  | "agent"
  | "goal"
  | "plus"
  | "trash"
  | "entry"
  | "expand"
  | "subflow"
  | "book_appointment"
  | "qualify_lead"
  | "faq_lookup"
  | "escalate_to_human";

const V2_ICON_PATHS: Record<V2IconName, React.ReactNode> = {
  canvas: (
    <>
      <circle cx="6" cy="6" r="2.4" />
      <circle cx="18" cy="6" r="2.4" />
      <circle cx="12" cy="18" r="2.4" />
      <path d="M7.5 7.6 11 16M16.5 7.6 13 16" />
    </>
  ),
  tools: (
    <path d="M14.7 6.3a3.6 3.6 0 0 0-4.9 4.9L4 17l3 3 5.8-5.8a3.6 3.6 0 0 0 4.9-4.9l-2.2 2.2-2-2 2.2-2.2z" />
  ),
  slots: (
    <>
      <path d="M9 6h11M9 12h11M9 18h11" />
      <circle cx="4.5" cy="6" r="1" />
      <circle cx="4.5" cy="12" r="1" />
      <circle cx="4.5" cy="18" r="1" />
    </>
  ),
  persona: (
    <>
      <circle cx="12" cy="8" r="3.4" />
      <path d="M5.5 20a6.5 6.5 0 0 1 13 0" />
    </>
  ),
  greeting: <path d="M22 2 11 13M22 2l-7 20-4-9-9-4 20-7z" />,
  guardrails: <path d="M12 3l7 3v5c0 4.5-3 7.6-7 9-4-1.4-7-4.5-7-9V6l7-3z" />,
  channels: (
    <>
      <path d="M4 9a16 16 0 0 1 16 0M7 12.5a11 11 0 0 1 10 0M10 16a6 6 0 0 1 4 0" />
      <circle cx="12" cy="19.5" r="1" />
    </>
  ),
  agent: (
    <>
      <rect x="4" y="5" width="16" height="14" rx="2" />
      <path d="M8 9.5h8M8 13.5h5" />
    </>
  ),
  goal: (
    <>
      <circle cx="12" cy="12" r="8" />
      <circle cx="12" cy="12" r="3.3" />
    </>
  ),
  plus: <path d="M12 5v14M5 12h14" />,
  trash: <path d="M4 7h16M9 7V5h6v2M6.5 7l1 13h9l1-13" />,
  expand: <path d="M9 21H3v-6M15 3h6v6M21 3l-7 7M3 21l7-7" />,
  entry: <path d="M15 12H4M11 8l4 4-4 4M19 4v16" />,
  subflow: (
    <>
      <rect x="3" y="4" width="7" height="6" rx="1.5" />
      <rect x="14" y="14" width="7" height="6" rx="1.5" />
      <path d="M10 7h4a3 3 0 0 1 3 3v4" />
    </>
  ),
  book_appointment: (
    <>
      <rect x="4" y="5" width="16" height="16" rx="2" />
      <path d="M4 9.5h16M8 3v4M16 3v4" />
    </>
  ),
  qualify_lead: (
    <>
      <rect x="6" y="4" width="12" height="17" rx="2" />
      <path d="M9.5 4h5v2.5h-5zM9 11h6M9 15h4" />
    </>
  ),
  faq_lookup: (
    <>
      <path d="M5 4h11a2 2 0 0 1 2 2v15H7a2 2 0 0 1-2-2z" />
      <path d="M5 18.5h13" />
    </>
  ),
  escalate_to_human: (
    <>
      <circle cx="9" cy="8" r="3" />
      <path d="M3.5 20a5.5 5.5 0 0 1 11 0M16 8h6M19 5l3 3-3 3" />
    </>
  ),
};

function Icon({ name, className = "h-4 w-4" }: { name: V2IconName; className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      {V2_ICON_PATHS[name]}
    </svg>
  );
}

/** Lightweight hover/focus tooltip. Trigger keeps its own accessible name
 *  (aria-label); this is the visual affordance. Avoid inside the ReactFlow
 *  canvas (transforms clip it) — use native `title` on nodes there. */
function Tooltip({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <span className="group/tt relative inline-flex">
      {children}
      <span
        role="tooltip"
        className="pointer-events-none absolute left-1/2 top-full z-50 mt-1.5 -translate-x-1/2 whitespace-nowrap rounded-md bg-zinc-900 px-2 py-1 text-[11px] font-medium text-white opacity-0 shadow-md transition-opacity duration-150 group-hover/tt:opacity-100 group-focus-within/tt:opacity-100 dark:bg-zinc-700"
      >
        {label}
      </span>
    </span>
  );
}
